/**
 * `AgentProvider` — the terminal state machine, ported from the parked
 * `AgentProvider.tsx` and rewired onto `@paradigm/terminal-core` factories
 * plus a host-supplied `TerminalConfig` (read from context).
 *
 * Responsibilities (all preserved from the original):
 *  - Connection lifecycle (connect / disconnect / reconnect-on-account-change)
 *  - Auth + self-service pairing (delegated to `createOpenClawClient`)
 *  - Per-session frame routing (runId → session ownership + background
 *    buffering) so an in-flight run keeps writing to its originating chat
 *  - Processing indicator, `/model` echo suppression
 *  - Sessions / folders CRUD, hydrate-on-account-change
 *
 * Remote session-sync is NATIVE (not host-injected): on hydration we pull the
 * gateway's `/sync/v1` snapshot, merge it into the chat-history store, and push
 * local changes. It degrades to local-only when the gateway has no sync API.
 */
import {
  clearSubtasksFor,
  generateChatSessionId,
  generateMessageId,
  SESSION_TITLE_MAX_LEN,
  createOpenClawClient,
  createSessionSync,
  estimateChatSendBytes,
  hydrateFromRemote,
  isDeviceLocalPreference,
  languageDirective,
  MAX_CHAT_SEND_BYTES,
  NO_RUNNING_SUBTASKS,
  allLanguageDirectives,
  bareSessionId,
  deleteGatewaySession,
  deleteSessionGroup,
  foldersFromGatewayGroups,
  isTerminalChatRow,
  isUntitledRow,
  listSessionGroups,
  listSessions,
  projectGatewaySessions,
  subscribeSessions,
  parseSessionContextUsage,
  patchSessionModel,
  patchSessionOrganisation,
  pageTranscriptId,
  projectGatewayHistory,
  putSessionGroups,
  reconcileSessionContextUsage,
  renameSessionGroup,
  resetSession,
  sendKeyFromRow,
  stripTitleScope,
  toSyncedPreferencesDoc,
} from '@paradigm/terminal-core';
import type {
  AgentConnectionStatus,
  AgentWebSocket,
  ChatAttachment,
  ChatMessage,
  ChatSession,
  CustomFolder,
  GatewayFrame,
  GatewayHistoryPage,
  GatewaySessionGroup,
  GatewaySessionsListResult,
  // Aliased: this file already has a `GatewaySessionRow` for the
  // `sessions.describe` model-ref shape, which is a different row entirely.
  GatewaySessionRow as GatewayRosterRow,
  MessageAttachment,
  SessionOrganisationPatch,
  ModelInfo,
  SessionFolder,
  TelemetryPreference,
  TerminalSync,
} from '@paradigm/terminal-core';
import { clearAttachmentPreviews } from '../components/chat/messages/useAttachmentPreview';
import { deriveHttpBase, fetchGatewayModels } from '../models/gateway-models';
import { createSidecarRequest } from '../sidecar/request';
import {
  defaultModelLabel,
  modelIdsMatch,
  pickDefaultModelId,
  resolveModelInCatalog,
} from '../models/model-labels';
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { useTerminalConfig } from '../context/config-context';
import { buildPreferencesStore, buildTerminalStores } from '../config';
import {
  usePreferencesActions,
  usePreferencesState,
} from './PreferencesProvider';
import { buildSendPayload } from '../send-payload';
import {
  attributionFor,
  buildChatErrorReport,
  categorizeChatError,
  CHAT_ERROR_CATEGORIES,
  pseudonymFor,
  toMinimalRunOutcome,
} from '../telemetry/chat-errors';
import type {
  ChatErrorCategory,
  ChatErrorSignal,
  ChatErrorUser,
  ChatRunContext,
  HarnessInfo,
  RunOutcomeReport,
} from '../telemetry/chat-errors';
import {
  AgentActionsProvider,
  AgentStateProvider,
  INITIAL_AGENT_STATE,
  type AgentActions,
  type AgentState,
} from './contexts';
import { applyProcessingPatch, handleFrame } from './handleFrame';
import type { ProcessingPatch, RunFramePayload } from './handleFrame';

interface Props {
  readonly children: ReactNode;
}

const DEFAULT_SESSION_TITLE = 'New Chat';

/**
 * Model fields of a gateway session row (`sessions.describe` /
 * `sessions.list`). The gateway resolves these from the session's stored
 * override, else its runtime model, else the agent default — so for an
 * existing session they reflect what a send would actually run.
 */
interface GatewaySessionModelRef {
  readonly modelProvider?: string | null;
  readonly model?: string | null;
}

/**
 * `sessions.describe`'s payload. The row is deliberately typed as the model
 * fields PLUS an open record: two readers narrow it — `composeSessionModelId`
 * for the picker and `parseSessionContextUsage` for the meter — and the second
 * reads keys this interface does not name. Typing it closed said those keys
 * did not exist while the parser read them anyway, which compiled only because
 * the parser takes `unknown`, and would have let a gateway rename land with no
 * pushback from either the compiler or a fixture.
 */
interface GatewaySessionRow
  extends GatewaySessionModelRef, Record<string, unknown> {}

interface SessionsDescribeResult {
  readonly session?: GatewaySessionRow | null;
}

interface SessionsListDefaultsResult {
  readonly defaults?: GatewaySessionModelRef | null;
}

/**
 * Compose a session row's split provider/model fields back into the
 * picker's catalog id shape. The catalog ids are provider-qualified
 * (`dt-a/bedrock/anthropic/opus/4.8`) while gateway rows return
 * `modelProvider: 'dt-a'` and `model: 'bedrock/anthropic/opus/4.8'`
 * separately — the same wrapping openclaw uses internally.
 */
function composeSessionModelId(
  ref: GatewaySessionModelRef | null | undefined,
): string | null {
  const model = ref?.model?.trim();
  if (model == null || model.length === 0) return null;
  const provider = ref?.modelProvider?.trim();
  return provider != null && provider.length > 0
    ? `${provider}/${model}`
    : model;
}

/**
 * Map a gateway-reported model id onto the live catalog for display: the
 * catalog's exact id when it carries an equivalent, else the raw id. The
 * picker resolves the label through the same helper, so state and trigger
 * can no longer name different models.
 */
function reconcileModelIdWithCatalog(
  modelId: string,
  models: readonly ModelInfo[],
): string {
  return resolveModelInCatalog(models, modelId)?.id ?? modelId;
}

/**
 * Resolve an incoming frame's `sessionKey` to one of the UI's known session
 * ids. The gateway echoes the key namespaced as `agent:<channel>:<uiSessionId>`
 * (e.g. `agent:main:<uuid>`, `agent:cron:<uuid>`), but the UI tracks bare
 * session ids. So we:
 *   1. Prefer a whole-key exact match — an id that arrives un-namespaced (even
 *      one that itself contains colons) wins outright and is never split.
 *   2. Otherwise strip the `agent:<channel>:` prefix and match what remains.
 *      We keep EVERYTHING after the channel segment (not just the trailing
 *      `:`-segment), so an id containing colons still resolves and a prefix
 *      collision like ids `{'id', 'session:id'}` with key `agent:main:session:id`
 *      resolves to `'session:id'`, never the shorter `'id'`.
 * Returns the matched session id, or null when nothing resolves (the caller
 * then falls back to run-ownership / last-send heuristics).
 */
export function matchKnownSessionKey(
  rawKey: string | null | undefined,
  knownSessionIds: ReadonlySet<string>,
): string | null {
  if (rawKey == null) return null;
  if (knownSessionIds.has(rawKey)) return rawKey;
  const parts = rawKey.split(':');
  if (parts[0] === 'agent' && parts.length >= 3) {
    const sessionId = parts.slice(2).join(':');
    if (sessionId.length > 0 && knownSessionIds.has(sessionId)) {
      return sessionId;
    }
    // 3. Then without openclaw's `dashboard:` title scope, which this client
    //    adds to the keys of chats it minted (`session-keys.ts`). Tried LAST,
    //    after the whole-key and un-scoped forms, so a chat whose id really is
    //    `dashboard:…` still resolves to itself rather than to the id inside
    //    it. Every run, error and usage frame for a scoped chat arrives under
    //    the scoped key, so without this they all read as another device's.
    const unscoped = stripTitleScope(sessionId);
    if (unscoped !== sessionId && knownSessionIds.has(unscoped)) {
      return unscoped;
    }
  }
  return null;
}

/**
 * Whether a frame belongs to a session that NOTHING on this device knows
 * about: its `sessionKey` resolves to no locally known session AND its run
 * isn't one we've already claimed. These frames come from a session created
 * on another device (all devices share one gateway, which fans events out to
 * every connection, but the sync index only pulls on (re)connect — so a
 * session born mid-connection is invisible here until the next snapshot
 * merge). Foreign frames must never fall through to the last-send /
 * active-session heuristics: that's exactly the cross-device leak where a
 * mobile chat lands in an unrelated desktop thread.
 */
export function isForeignSessionFrame(
  payload: { readonly sessionKey?: string; readonly runId?: string },
  knownSessionIds: ReadonlySet<string>,
  ownedRunIds: { has(runId: string): boolean },
): boolean {
  const key = payload.sessionKey;
  if (key == null || key.length === 0) return false;
  if (matchKnownSessionKey(key, knownSessionIds) != null) return false;
  return payload.runId == null || !ownedRunIds.has(payload.runId);
}

function deriveSessionTitle(content: string): string {
  const oneLine = content.replace(/\s+/gu, ' ').trim();
  if (oneLine.length <= SESSION_TITLE_MAX_LEN) return oneLine;
  return `${oneLine.slice(0, SESSION_TITLE_MAX_LEN - 1)}…`;
}

/**
 * Pick which session should be active after hydration. Prefer the
 * persisted `activeSessionId` (still valid only if the session still
 * exists), otherwise the most-recently-updated session, otherwise null.
 */
function resolveActiveSession(
  sessions: readonly ChatSession[],
  persistedActiveId: string | null,
): string | null {
  if (sessions.length === 0) return null;
  if (
    persistedActiveId != null &&
    sessions.some((s) => s.sessionId === persistedActiveId)
  ) {
    return persistedActiveId;
  }
  const mostRecent = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt)[0];
  return mostRecent?.sessionId ?? null;
}

/**
 * Stands in for the stream that carries no `runId`, so ended-run bookkeeping
 * can key on one map. Not a real run id, and deliberately not one any gateway
 * could produce.
 */
const RUNLESS_RUN_KEY = '\u0000runless';
/** How many recently-ended runs to remember (see `endedRunIdsRef`). */
const ENDED_RUN_MEMORY = 64;
/** How many `/model` runs typed by the user to remember (echo folding). */
const SUPPRESSED_MODEL_ECHO_MEMORY = 64;

/**
 * Bounded LRU window. Delete before adding: `Set.add` on a present key does
 * NOT move it, so a long-lived key would age out while still in use.
 */
function rememberBounded(set: Set<string>, key: string, max: number): void {
  set.delete(key);
  set.add(key);
  while (set.size > max) {
    for (const oldest of set) {
      set.delete(oldest);
      break;
    }
  }
}

/**
 * How long to wait for the chat list before calling the read failed.
 *
 * Generous: this is a fault detector, not a latency budget. A gateway paging
 * a large store legitimately takes seconds, and reporting a failure over a
 * slow answer is worse than the wait — the read that follows would show the
 * same chats anyway.
 */
const ROSTER_READ_TIMEOUT_MS = 30_000;

/**
 * The promise, or a rejection once the deadline passes.
 *
 * The gateway client parks a request until the socket answers or closes, with
 * no deadline of its own — correct for a client that cannot know what a
 * reasonable wait is, and the reason a caller whose UI depends on settling
 * has to impose one.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => reject(new Error('timeout')), ms);
    }),
  ]).finally(() => {
    if (timer != null) clearTimeout(timer);
  }) as Promise<T>;
}

/** True while a message is still being produced — streamed text or reasoning. */
function isLiveMessage(m: ChatMessage): boolean {
  return m.isStreaming === true || m.isThinking === true;
}

/**
 * How far back in the agent's answer to look for a message we still hold as
 * unsent.
 *
 * A send is the last thing the user did, so if the agent has it, it is at the
 * end of the transcript. Bounded rather than a full scan so that a chat which
 * genuinely repeats a phrase from months ago does not have this turn
 * swallowed by that old one.
 */
const UNSENT_MATCH_WINDOW = 10;

/**
 * Whether the agent's row is the turn we are holding as unsent.
 *
 * Role and text, because that is all the two representations share: ours is
 * what the user typed, the agent's is the same thing with this app's framing
 * taken back off, and the ids and timestamps are minted independently on each
 * side.
 *
 * An image-only send has no text at all, so for those the comparison is
 * "same role, no text, and both carry a file" — matching on emptiness alone
 * would let any textless row swallow the user's picture.
 */
function isSameTurn(local: ChatMessage, row: ChatMessage): boolean {
  if (local.role !== row.role) return false;
  const ours = local.content.trim();
  const theirs = row.content.trim();
  if (ours.length > 0) return ours === theirs;
  return (
    theirs.length === 0 &&
    (local.attachments?.length ?? 0) > 0 &&
    (row.attachments?.length ?? 0) > 0
  );
}

/**
 * Whether leaving this chat has to keep a copy of its messages in memory.
 *
 * Two reasons, and the second is not about liveness at all:
 *
 *  - a turn still arriving (streamed text, reasoning, or a send whose first
 *    delta has not come back), which the user expects to find finished when
 *    they come back to it; and
 *  - a message that never reached the agent. Nothing else holds that text:
 *    the agent has no record of a message it never received, and the paint
 *    cache on disk is the agent's own answer. Dropping the copy in memory
 *    loses what the user typed, and loses the only mark that keeps such a
 *    chat out of a cross-device delete — so the conversation itself would
 *    then disappear on the next roster read.
 */
function needsBackgroundCopy(
  messages: readonly ChatMessage[],
  processing: unknown,
): boolean {
  return (
    messages.some(isLiveMessage) ||
    processing != null ||
    messages.some((m) => m.unsent === true)
  );
}

function closeStreamingMessages(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  // A tool log counts as live in this sweep but not in `isLiveMessage`, whose
  // other callers ask whether output is still pending — widening it there
  // would make a finished log read as an in-flight run on reconnect.
  const isLive = (m: ChatMessage): boolean =>
    isLiveMessage(m) || m.isToolActive === true;
  if (!messages.some(isLive)) return messages;
  return messages.map((m) =>
    isLive(m)
      ? { ...m, isStreaming: false, isThinking: false, isToolActive: false }
      : m,
  );
}

// Storage bucket sent to the sync server as `X-Sync-User`. A single shared
// value across every surface (standalone / Paradex / Paradigm) so sessions are
// identical once they all embed this package. Token-mode gateways key storage
// on this header; trusted-proxy gateways ignore it (they use the wallet
// address). Auth itself stays on the gateway token for now.
const SYNC_USER = 'default';

/**
 * How many finished runs to remember for the late-frame guard. Generous
 * relative to the window it protects (a trailing frame arrives seconds after
 * its run ended, not hundreds of runs later) and small enough that a tab left
 * open all day cannot grow the set without bound.
 */
/**
 * Stamp the bubbles a forced close cut off, so a partial answer stays
 * identifiable.
 *
 * Needs the array from BEFORE the close: `closeStreamingMessages` has already
 * cleared `isStreaming` by the time it returns, so which bubbles were open is
 * only knowable by comparison.
 *
 * The mark is consumed by `mergeMessages`, which prefers the gateway's whole
 * copy over a truncated local one. Unmarked, the union's local-wins rule made
 * the half-answer permanent.
 */
function markTruncated(
  before: readonly ChatMessage[],
  after: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (after === before) return after;
  const wasStreaming = new Set(
    before.filter((m) => m.isStreaming === true).map((m) => m.id),
  );
  if (wasStreaming.size === 0) return after;
  return after.map((m) =>
    wasStreaming.has(m.id) ? { ...m, truncated: true } : m,
  );
}

const MAX_REMEMBERED_ENDED_RUNS = 200;

/**
 * Add run ids to the cancelled set, bounded, refreshing recency.
 *
 * Both writers go through this — the trim used to be on one of the two, which
 * is worse than on neither because it looks handled.
 *
 * Re-adding an id moves it to the back. A plain `new Set([...prev, ...ids])`
 * keeps an id at its FIRST insertion position, so a hung run re-cancelled by
 * every Stop kept its original slot and was evicted after ~200 intervening
 * cancels while still live — losing the guard that stops its frames.
 */
function rememberCancelled(
  prev: ReadonlySet<string>,
  ids: Iterable<string>,
): Set<string> {
  const next = new Set(prev);
  for (const id of ids) {
    next.delete(id);
    next.add(id);
  }
  while (next.size > MAX_REMEMBERED_ENDED_RUNS) {
    const oldest = next.values().next().value;
    if (oldest == null) break;
    next.delete(oldest);
  }
  return next;
}

// Restore (snapshot pull) retry policy. Restore fires when the connection
// reaches `authenticated`; a null result is either a transient failure or a
// gateway without `/sync/v1`. We retry a bounded number of times so transient
// blips recover while a no-sync gateway settles to local-only quickly.
const RESTORE_MAX_ATTEMPTS = 4;
const RESTORE_RETRY_BASE_MS = 1_000;

// Model-catalog retry policy. `fetchGatewayModels` collapses every failure
// (network blip, 502 `models_unavailable` from a cold sidecar, not-yet-seeded
// gateway token) into an empty list, so an empty result is the retry signal.
// The gateway is the ONLY source of models, so giving up means the user cannot
// switch models for the rest of the connection — retry generously. Backoff is
// linear (base × attempt) like restore, capped so the tail doesn't stretch
// into minutes: 1+2+3+4+5+5+5 ≈ 25s across 8 attempts.
const MODELS_MAX_ATTEMPTS = 8;
const MODELS_RETRY_BASE_MS = 1_000;
const MODELS_RETRY_MAX_MS = 5_000;

// Model-switch verification. A pick flips the picker optimistically and
// writes `sessions.patch { model }`; that call's OWN response carries the
// model the gateway resolved, so it is the confirmation — there is no
// read-back to poll and no `/model` chat message. On a mismatch (the gateway
// silently substituted) the picker bounces back to what it reports. See
// `writeSessionModel`.

/**
 * What we remember about a run between send and terminator. Written at SEND
 * time: the turn index moves as the transcript grows and the model can be
 * switched mid-run, so reading them at the terminator would report the world
 * when the run ended, not the conditions it ran under.
 */
interface RunRecord {
  readonly sessionId: string;
  readonly startedAt: number;
  readonly turnIndex: number;
  readonly hadAttachments: boolean;
  readonly isRetry: boolean;
  readonly connectionGeneration: number;
  /**
   * Resolved when the run is claimed rather than at construction: the model a
   * fresh chat runs is seeded on the same send, one step later.
   */
  modelId: string | null;
  /** Set when the run failed. */
  category?: ChatErrorCategory;
  /**
   * This run's outcome has already been counted.
   *
   * Deleting the record was not enough on its own: the same object is held in
   * the send closure, so a Stop landing in the ack gap on a MINTING gateway
   * had it re-filed under the gateway's id (`endedRunIds` holds the send key,
   * not the gateway's, so `claimRun` let it back in) and counted the run a
   * second time. The flag travels with the object, so re-keying cannot
   * resurrect it.
   */
  emitted?: boolean;
}

/** What a terminator said about a run we had not yet claimed — see `earlyEndsRef`. */
interface EarlyRunEnd {
  /** When the first terminator arrived; absent if only the failure has so far. */
  endedAt?: number;
  category?: ChatErrorCategory;
}

/**
 * Cap on remembered in-flight runs. Evicted silently rather than reported as a
 * synthetic outcome: telling "never finished" apart from "resumed across a
 * reconnect" needs gateway semantics this client cannot verify, and a
 * denominator padded with guesses is worse than one with a known gap.
 */
const MAX_TRACKED_RUNS = 64;

// How long a transient error stays up before it self-dismisses.
const TRANSIENT_ERROR_MS = 5_000;

// Throttle for the foreign-frame snapshot pull. A streaming run on another
// device emits many frames for its (locally unknown) session, and that
// device's index upload is itself debounced — so the first pull can
// legitimately miss and a retry on a later frame is expected. Single-flight +
// rate-limited so the frame stream can't hammer `/sync/v1/snapshot`.
const FOREIGN_HYDRATE_THROTTLE_MS = 5_000;
/**
 * How many times the catch-up read will chase a queue that keeps failing.
 *
 * The queue re-arms itself while keys are pending and puts them back when the
 * read fails, and those two together are an infinite loop: on a gateway whose
 * `sessions.list` errors, one frame for an unknown session produced a roster
 * read AND a `reportError` every 5s for the life of the tab. Bounded and
 * backed off, like `RESTORE_*` and `MODELS_*` above.
 */
const FOREIGN_HYDRATE_MAX_ATTEMPTS = 4;

function buildSessionContextMessage(
  sessionContext: Record<string, string>,
): string {
  const body = Object.entries(sessionContext)
    .map(([k, v]) => `${k}=${v}`)
    .join(';');
  return `<session-context>${body}</session-context>`;
}

/**
 * Whether the terminal has enough to show (vs. its login screen). A gateway URL
 * is always required. Token-only hosts (`supportsPairingRole === false`, e.g.
 * Paradigm) additionally need a token — stored, or supplied via config
 * `fallbackPairingToken`. Pairing-role hosts self-pair from the URL alone, so a
 * token isn't required up front.
 */
function deriveIsPaired(opts: {
  readonly account: string | null;
  readonly url: string | null;
  readonly supportsPairingRole?: boolean;
  readonly hasStoredToken: boolean;
  readonly fallbackPairingToken?: string;
}): boolean {
  if (opts.account == null) return false;
  if (opts.url == null || opts.url.length === 0) return false;
  if (opts.supportsPairingRole !== false) return true;
  return opts.hasStoredToken || (opts.fallbackPairingToken?.length ?? 0) > 0;
}

export function AgentProvider({ children }: Props) {
  const config = useTerminalConfig();

  // Stores are stable per namespace; the component memoizes on that key.
  const { urlStore, historyStore, deviceStore } = useMemo(
    () => buildTerminalStores(config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.namespace],
  );
  // Same store the settings provider above reads — a pure `keyFor` closure, so
  // a second instance addresses the identical keys (see `buildPreferencesStore`).
  const prefsStore = useMemo(
    () => buildPreferencesStore(config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.namespace],
  );
  const {
    preferences,
    doc: prefsDoc,
    loaded: prefsLoaded,
  } = usePreferencesState();
  const { adoptDoc: adoptPreferencesDoc, onLocalEdit } =
    usePreferencesActions();
  // Read inside callbacks/effects that must not re-subscribe on every edit.
  const defaultModelIdRef = useRef<string | null>(preferences.defaultModelId);
  defaultModelIdRef.current = preferences.defaultModelId;
  // The full stamped doc, for hydration: live state can hold an edit a failed
  // `store.save` never persisted (quota, private mode), and a hydrate that
  // read only the disk copy uploaded a merge WITHOUT that edit — coalescing
  // over the edit's own queued PUT and losing it on reload.
  const prefsDocRef = useRef(prefsDoc);
  prefsDocRef.current = prefsDoc;
  const languageRef = useRef(preferences.language);
  languageRef.current = preferences.language;
  /**
   * `minimal` until the stored choice is read: preferences hydrate after
   * mount, and failures cluster in exactly that window, so the worst case is a
   * report with LESS in it than the user allowed, never more.
   */
  const telemetryTierRef = useRef<TelemetryPreference>('minimal');
  telemetryTierRef.current = prefsLoaded ? preferences.telemetry : 'minimal';

  const accountId = config.accountId;
  const defaultFolder = (config.defaultFolder ??
    config.namespace) as SessionFolder;

  const accountIdRef = useRef<string | null>(accountId);
  accountIdRef.current = accountId;

  // Seed URL + pairing synchronously from storage/config, before the async
  // hydrate effect runs, so (1) a returning paired user goes straight to the
  // terminal with no login flash, and (2) the login screen prefills the known
  // gateway URL — matching what the settings screen shows.
  //
  // `agentUrl` is seeded ONLY when unpaired (login screen). When paired, we
  // leave it null and let hydrate set it alongside the initial connect;
  // seeding it here would make ChatInterface's mount-connect fire a duplicate.
  const [state, setState] = useState<AgentState>(() => {
    const initialUrl =
      accountId != null
        ? (urlStore.getAgentUrl(accountId) ?? config.gatewayUrl ?? null)
        : null;
    const initialPaired = deriveIsPaired({
      account: accountId,
      url: initialUrl,
      supportsPairingRole: config.supportsPairingRole,
      hasStoredToken:
        accountId != null && urlStore.getAgentToken(accountId) != null,
      fallbackPairingToken: config.fallbackPairingToken,
    });
    return {
      ...INITIAL_AGENT_STATE,
      agentUrl: initialPaired ? null : initialUrl,
      isPaired: initialPaired,
    };
  });
  const wsRef = useRef<AgentWebSocket | null>(null);

  // True after a deliberate `disconnect()` (user hit Disconnect), cleared by any
  // intentional `connect()`. The idle-tab auto-reconnect effect checks this so
  // it recovers passive drops but never silently reopens a socket the user
  // closed on purpose.
  const manualDisconnectRef = useRef(false);

  // Set synchronously at the top of `connect()` and cleared once the attempt
  // settles (first status callback, early bail-out, or failure). It's the guard
  // the auto-reconnect effect actually needs: `connect()` flips `status` to
  // 'connecting' only asynchronously (after `await resolveUrl`) and `statusRef`
  // lags React commits, so status alone can't prevent a second wake event from
  // entering `connect()` while the first is mid-flight and leaking a socket.
  const isConnectingRef = useRef(false);
  // Timestamp (ms) of the last auto-reconnect attempt, for cross-event backoff
  // so a flapping network / rapid alt-tabbing can't spawn a fresh client (and
  // reset its retry budget) on every wake event.
  const lastReconnectAtRef = useRef(0);

  // Stash the latest config bits behind refs so the memoized `connect`
  // closure always sees current values without being re-created on
  // every render.
  const configRef = useRef(config);
  configRef.current = config;

  const generativeUiEnabledRef = useRef(config.features?.generativeUi ?? false);
  generativeUiEnabledRef.current = config.features?.generativeUi ?? false;

  const reportError = useCallback((err: unknown) => {
    configRef.current.logException?.(err);
  }, []);

  /**
   * Diagnostics for the per-session model paths. These failures are all
   * RECOVERABLE by design — the picker keeps a stale or default value and the
   * next switch/reconnect re-reads the truth — so they must not reach
   * `logException`, which is the host's exception channel (a mid-describe
   * reconnect is not a bug worth paging anyone for). But swallowing them
   * silently left "the picker shows the wrong model" with no way to tell
   * which read failed, so they go to the connection-diagnostics hook.
   */
  const logModelDebug = useCallback(
    (event: string, detail?: Record<string, unknown>) => {
      configRef.current.onConnectionDebug?.(event, detail);
    },
    [],
  );

  /**
   * Surface a transient error and schedule its dismissal. The single owner of
   * that pairing: three hand-rolled copies had drifted, and the newest matched
   * on a message PREFIX, so a stale timer dismissed a newer toast early.
   * Clearing is keyed on exact identity, so a timer only ever retires the
   * message it was armed for. Safe to pass as `handleFrame`'s
   * `onTransientError` — re-setting the same string is a no-op.
   */
  const showTransientError = useCallback((msg: string) => {
    setState((prev) =>
      prev.transientError === msg ? prev : { ...prev, transientError: msg },
    );
    setTimeout(() => {
      setState((prev) =>
        prev.transientError === msg ? { ...prev, transientError: null } : prev,
      );
    }, TRANSIENT_ERROR_MS);
  }, []);

  // Remember the last URL we pushed to the consumer's persistence so
  // repeated reconnects with the same URL don't spam the callback.
  const lastPersistedUrlRef = useRef<string | null>(null);

  // Track the identity we've already sent a `<session-context>` for over
  // the current WS instance, so heartbeat-driven reconnects don't repeatedly
  // re-introduce the operator. Cleared whenever the socket drops below
  // 'authenticated', so the *next* successful auth always re-sends.
  const sessionContextSentForRef = useRef<string | null>(null);

  // Per-run session ownership: runId → sessionId.
  const runOwnershipRef = useRef<Map<string, string>>(new Map());

  /**
   * Runs that have reached a terminal state, so a late frame for one can be
   * rendered inert rather than opening something no terminator can close
   * (see `FrameRouting.isRunEnded`). Bounded: `onRunEnd` fires once per run
   * for the life of the page, and the only question ever asked is about a
   * run whose frames are still arriving, so a small insertion-ordered window
   * is enough for run IDS.
   *
   * Run-less marks are keyed `RUNLESS_RUN_KEY:<sessionId>` and share those
   * slots, which is the known weak point: 64 identified runs evict a
   * session's run-less mark, and a late run-less frame then opens a live
   * block again. `sendMessage` clears the active chat's mark, so the window
   * only matters between sends.
   *
   * KNOWN GAPS, deliberately not patched again — three rounds of review each
   * moved this bug rather than closing it, so the next change here should
   * write the invariant down ("which run-less streams does THIS terminator
   * end?") and test it, rather than adjust the key a fourth time:
   *   - a run-less terminator from another device's session resolves to a
   *     null owner and retires the LOCAL open chat's stream;
   *   - a bare terminator (no `sessionKey`) retires only the active chat's
   *     stream, so an attributed background stream is left live;
   *   - run-less marks competing with run ids for the 64 slots, above.
   * All three self-heal on switch-back or the next send.
   */
  const endedRunIdsRef = useRef<Set<string>>(new Set());

  const rememberEndedRun = useCallback((key: string) => {
    rememberBounded(endedRunIdsRef.current, key, ENDED_RUN_MEMORY);
  }, []);

  // In-memory message buffer for sessions that are receiving frames while
  // not active.
  const backgroundSessionsRef = useRef<Map<string, readonly ChatMessage[]>>(
    new Map(),
  );

  // Parallel buffer for the "Thinking" indicator of inactive sessions. Without
  // it, the user switching back to an in-flight chat sees their question but
  // no loading state — the work IS still in flight, but the UI signal vanishes
  // because `processing` only lives on the live state object.
  const backgroundProcessingRef = useRef<Map<string, AgentState['processing']>>(
    new Map(),
  );

  const activeSessionIdRef = useRef<string | null>(null);
  activeSessionIdRef.current = state.activeSessionId;

  const cancelledRunIdsRef = useRef<ReadonlySet<string>>(new Set<string>());
  cancelledRunIdsRef.current = state.cancelledRunIds;

  const suppressedModelEchoRunIdsRef = useRef<Set<string>>(new Set());

  /**
   * Mute a run by id. The bound is load-bearing, and cannot be replaced by
   * dropping the entry when the run ends: the three branches that consult it
   * (`agent` thinking, `agent` assistant delta, `chat` text) have no
   * `isEndedRun` gate, so a trailing frame on any of them would render after
   * the terminator. `retireRun` deletes only on a terminator that releases
   * ownership, so otherwise eviction is the reclaim path.
   */
  const rememberSuppressedModelEchoRun = useCallback((runId: string): void => {
    rememberBounded(
      suppressedModelEchoRunIdsRef.current,
      runId,
      SUPPRESSED_MODEL_ECHO_MEMORY,
    );
  }, []);

  /**
   * The active chat's model, DERIVED rather than stored. This precedence chain
   * is the whole policy, in one place:
   *
   *   1. the active session's own model — the gateway is the truth for a chat
   *      that exists
   *   2. a pick made before any session existed — an explicit user choice
   *      outranks anything we merely resolved
   *   3. the gateway main session's model — display-only, so a fresh install
   *      shows something real (never what a chat runs; another surface may
   *      have set it)
   *   4. the gateway default, then the catalog's Opus-first default
   *
   * Expressing it as an ordering is what removed the guard choreography: five
   * call sites used to write `selectedModelId` directly, each needing its own
   * "is this still the active session / has a newer pick landed" check, and a
   * missed check meant the picker named a model the session was not running.
   * A late read-back now writes its own session's slot and simply loses.
   */
  /**
   * The ONLY writer of per-session model state. Everything that learns a
   * session's model — a pick, a read-back, a verify result, a seed — goes
   * through here, so the keyed store cannot disagree with what the picker
   * shows (the picker reads it through the derivation above).
   */
  const applySessionModel = useCallback(
    /**
     * `null` CLEARS the slot rather than writing a null into it. That is what
     * a refused write on a chat the gateway had no model for has to do: the
     * derivation ranks the slot above every default, so leaving it holding
     * the refused id would keep the picker naming a model the chat is not
     * running.
     */
    (sessionId: string, modelId: string | null): void => {
      setState((prev) => {
        if (modelId == null) {
          if (!(sessionId in prev.sessionModels)) return prev;
          const { [sessionId]: _cleared, ...rest } = prev.sessionModels;
          return { ...prev, sessionModels: rest };
        }
        return prev.sessionModels[sessionId] === modelId
          ? prev
          : {
              ...prev,
              sessionModels: { ...prev.sessionModels, [sessionId]: modelId },
            };
      });
    },
    [],
  );

  const selectedModelId = useMemo<string | null>(() => {
    const active = state.activeSessionId;
    const raw =
      (active != null ? state.sessionModels[active] : undefined) ??
      state.pendingModelId ??
      state.mainSessionModelId ??
      state.gatewayDefaultModelId ??
      pickDefaultModelId(state.models);
    return raw == null ? null : reconcileModelIdWithCatalog(raw, state.models);
  }, [
    state.activeSessionId,
    state.sessionModels,
    state.pendingModelId,
    state.mainSessionModelId,
    state.gatewayDefaultModelId,
    state.models,
  ]);

  const selectedModelIdRef = useRef<string | null>(null);
  selectedModelIdRef.current = selectedModelId;

  // Whole-state mirror, for the few callbacks that need to READ model state
  // synchronously (the pick's no-op guard, the seed's default comparison)
  // without threading it through a setState updater. Read-only by convention —
  // every write still goes through setState.
  const stateRef = useRef<AgentState>(state);
  stateRef.current = state;

  // Catalog mirror, so async callbacks can reconcile a gateway id without
  // doing it inside a setState updater (React defers those to the render
  // phase, so anything the callback needs afterwards is not yet computed).
  const modelsRef = useRef<readonly ModelInfo[]>([]);
  modelsRef.current = state.models;

  const gatewayDefaultsFetchedConnectionRef = useRef(false);
  // The most recent model the user is known to have run/picked, in memory
  // only — new sessions are seeded from it. After a reload it's rebuilt from
  // the gateway (the most recently updated previous session's model), so the
  // UI still persists nothing; a user with no previous sessions falls back
  // to the gateway default.
  const lastUsedModelIdRef = useRef<string | null>(null);
  /**
   * An explicit pick made BEFORE the session exists (the composer picker on a
   * brand-new chat). Mirrors `state.pendingModelId`, but as a ref so the seed
   * path can read it after the minting `setState` has already cleared the
   * state copy. Distinct from `lastUsedModelIdRef` because the two rank on
   * opposite sides of the user's configured default: "I picked this for THIS
   * chat" outranks the default, "this is what I last used somewhere else"
   * does not.
   */
  const pendingModelIdRef = useRef<string | null>(null);

  /**
   * Which model a BRAND-NEW session should start on, most specific first:
   *  1. An explicit pick for this not-yet-minted chat.
   *  2. The user's configured default (Settings → Model).
   *  3. The last model picked this page load.
   *  4. `null` ⇒ caller falls back to the most recent other session, then the
   *     gateway default.
   *
   * (2) outranks (3) deliberately: a default that last-used could override
   * would be overridden almost every time — switch model once in any chat and
   * every later "new chat" would ignore the setting, which reads as a bug. The
   * pre-session pick still wins, or picking a model for a new chat and then
   * sending would silently run the default instead.
   */
  const resolveSeedModelId = useCallback((): string | null => {
    return (
      pendingModelIdRef.current ??
      defaultModelIdRef.current ??
      lastUsedModelIdRef.current
    );
  }, []);
  // Monotonic tokens so a stale describe (session switched away, or an
  // account change) can never clobber a newer selection.
  const sessionModelFetchSeqRef = useRef(0);
  /**
   * How many times the user has PICKED a model for each chat. Deliberately
   * separate from `sessionModelFetchSeqRef`, which also moves when the
   * seeding effect re-runs — see `writeSessionModel`.
   */
  const modelPickSeqRef = useRef<Map<string, number>>(new Map());
  /**
   * The same idea for the context reading, deliberately NOT the same counter.
   *
   * `sessionModelFetchSeqRef` is also bumped when the user picks a model and
   * when the account changes — both statements about which MODEL answer is
   * current, neither of which says anything about the token counts riding the
   * same response. Sharing it meant clicking the picker while the describe was
   * in flight threw the usage seed away, with nothing to re-request it: the
   * gateway holds `totalTokens` at 0 for the rest of the connection, so no
   * later push could fill the gap either and the footer stayed empty until a
   * reload. This one is bumped only when the seeding effect itself re-runs.
   */
  const sessionUsageSeedSeqRef = useRef(0);
  const statusRef = useRef<AgentConnectionStatus>('disconnected');
  statusRef.current = state.status;
  // The gateway the session is currently targeting. Tracked so auto-reconnect
  // retries what the user is actually looking at (e.g. a URL they just switched
  // to that hasn't authenticated yet), not whatever `urlStore` last persisted.
  const agentUrlRef = useRef<string | null>(null);
  agentUrlRef.current = state.agentUrl;

  const knownSessionIdsRef = useRef<Set<string>>(new Set());
  knownSessionIdsRef.current = new Set(state.sessions.map((s) => s.sessionId));

  // Mirror of `state.sessions` for callbacks/effects that need session
  // metadata (e.g. the fresh-session model seeding walks updatedAt) without
  // re-firing on every transcript update.
  const sessionsRef = useRef<readonly ChatSession[]>([]);
  sessionsRef.current = state.sessions;

  const lastSendSessionRef = useRef<string | null>(null);

  const messagesRef = useRef<readonly ChatMessage[]>([]);
  messagesRef.current = state.messages;

  /**
   * `chat.send` idempotency keys for runs this client started and has not seen
   * finish. openclaw adopts the key as the run's `runId`, so a frame carrying
   * one is certainly ours — which is what lets the indicator refuse to be
   * named by a concurrent run, and what lets Stop cancel the right run before
   * any frame has named one. Bounded like `endedRunIds`, and a gateway that
   * mints its own ids simply never matches (see `pendingRunKeys`).
   */
  const pendingRunKeysRef = useRef<Set<string>>(new Set());
  /**
   * True once the gateway has answered a `chat.send` with the key we sent it,
   * i.e. it adopts our keys as run ids rather than minting its own.
   *
   * Settled by the ack, for that exact send — not inferred from frames. It
   * used to latch on "a frame carried an id we hold", which stopped meaning
   * adoption the moment the provider began holding the gateway's own ids, and
   * then read every minting run as proof of the opposite of the truth.
   *
   * Read only by `namesOurRun`, to decide whether an unfamiliar id may name
   * the row: where the gateway adopts keys it provably may not, and where it
   * mints the two are indistinguishable, so it may.
   */
  const gatewayAdoptsRunKeysRef = useRef(false);
  /**
   * True once this gateway has named a run id — any id, minted or adopted.
   *
   * Only a gateway that names NO runs may have a bare frame resolved to
   * whatever the session has outstanding (see `runlessRunIdForFrame`). Without
   * this latch a bare straggler on an id-bearing gateway settled the NEXT run
   * as `ok` while it was still streaming, and that run's real failure then
   * shipped unpaired.
   *
   * Set ONLY from ids the gateway itself produced: its `chat.send` ack, and
   * frames as they are routed. Never from `retireRun`, which `stopProcessing`
   * also calls — with keys this client minted, which say nothing about the
   * gateway.
   *
   * A fact about the connection, so it is cleared with the socket alongside
   * `gatewayAdoptsRunKeysRef`.
   */
  const gatewayNamesRunIdsRef = useRef(false);
  /**
   * Send keys whose `chat.send` response has not come back yet.
   *
   * The gap between a send and its ack is the only time the client holds
   * nothing but its own key — a name a minting gateway never issued. Knowing
   * which sends are in it is what lets Stop tell "I have an id the gateway
   * gave me" from "I have a hopeful stand-in", and is the fallback if a
   * gateway never answers `chat.send` at all.
   */
  const unackedSendKeysRef = useRef(new Set<string>());
  /**
   * For each message this client sent, the id it is on screen under, keyed by
   * the `chat.send` key that went out with it.
   *
   * A message is painted the instant the user presses Enter, under an id the
   * browser invents; the gateway then broadcasts the same turn back with an id
   * of its own. This map is the only thing that knows the two are one message,
   * and without it every message the user sent appeared twice until they
   * reloaded the page. Read by `handleFrame` through `localSendKeys`.
   *
   * Insertion-ordered and trimmed to the same bound as the run bookkeeping, so
   * a long-lived tab does not accumulate one entry per message ever sent. An
   * entry falling off means only that a very old echo would be appended rather
   * than recognised, and the gateway broadcasts a turn once, at send time.
   */
  const sentMessageIdsRef = useRef(new Map<string, string>());
  /**
   * Runs the gateway named for a chat that had already been deleted.
   *
   * Refusing to CLAIM such a run stops it re-creating the transcript, but not
   * its frames being rendered: with no owner, a frame is treated as the active
   * chat's, so a deleted run's answer painted into whatever the user was
   * looking at — including the fresh chat `deleteSession` mints. Recorded so
   * those frames can be dropped outright.
   */
  const orphanedRunIdsRef = useRef(new Set<string>());
  /**
   * A frame belonging to a run whose chat has been deleted.
   *
   * Read everywhere such a frame could land, because dropping it from only
   * one of them is worse than dropping it from none: blocking the text while
   * letting the row rise leaves "Thinking" up with the composer pinned to
   * Stop, and the terminators that would take it down are dropped too.
   */
  const isOrphanedRun = useCallback(
    (payload: { runId?: string }): boolean =>
      payload.runId != null && orphanedRunIdsRef.current.has(payload.runId),
    [],
  );
  /**
   * Bumped whenever the socket goes. An ack from a dead connection must not
   * claim a run on the fresh one: `pendingRequests` in the client only clears
   * on `close()`, so a late response can outlive the socket it belongs to.
   */
  const connectionGenerationRef = useRef(0);
  /**
   * Cancelled runs, readable synchronously.
   *
   * `state.cancelledRunIds` is the same fact, but the ref mirroring it is
   * assigned during render — so a frame handled in the same batch as a Stop
   * still sees the pre-Stop set, and a late delta painted under "Stopped."
   * Every write goes to both.
   */
  const cancelledNowRef = useRef(new Set<string>());
  const processingRef = useRef<AgentState['processing']>(null);
  processingRef.current = state.processing;

  // ──────── Reliability telemetry ────────
  //
  // Failures (`onChatError`) and the runs to divide them by (`onRunOutcome`),
  // both gated by the user's tier HERE rather than in the sink: consent that
  // every downstream consumer must remember to honour is not consent.

  /**
   * In-flight runs this client started. A run absent from here is not ours (a
   * cron turn, another device, a run from before a reload) and is not counted
   * — its failures could never reach the numerator.
   */
  const runRecordsRef = useRef(new Map<string, RunRecord>());

  /**
   * Terminators that arrived for a run no record was filed under, by run id.
   *
   * A gateway that streams BEFORE it answers `chat.send` ends the run under
   * its own id while the record is still keyed by our send key. Dropping
   * that terminator lost the run from the denominator while its failure
   * still shipped as an exception, so the rate read high. Instead the end —
   * and the failure category, which is keyed on the same id — is kept here
   * until the ack names the run, and `settleEarlyEnd` counts it then, with
   * the category and the duration the run really had.
   *
   * Runs that are not ours (cron turns, other devices) land here too and are
   * never claimed; bounded LRU like `endedRunIds`, so they age out.
   */
  const earlyEndsRef = useRef(new Map<string, EarlyRunEnd>());

  const noteEarlyEnd = useCallback(
    (runId: string, patch: Partial<EarlyRunEnd>): void => {
      const ends = earlyEndsRef.current;
      const prev = ends.get(runId);
      // Delete-then-set keeps insertion order an honest LRU (see
      // `rememberEndedRun`); the first `endedAt` wins because a run's
      // several terminators arrive after the one that actually ended it.
      ends.delete(runId);
      ends.set(runId, {
        ...prev,
        ...patch,
        ...(prev?.endedAt != null ? { endedAt: prev.endedAt } : {}),
      });
      while (ends.size > ENDED_RUN_MEMORY) {
        const oldest = ends.keys().next().value;
        if (oldest == null) break;
        ends.delete(oldest);
      }
    },
    [],
  );

  /**
   * `null` until it resolves, and without Web Crypto — reports in that window
   * carry no user reference, which is the safe direction.
   */
  const pseudonymRef = useRef<string | null>(null);
  useEffect(() => {
    pseudonymRef.current = null;
    // Identity is read at EMIT time, but these outlive the account: a
    // straggler terminator from the previous account would be counted under
    // the new one's pseudonym. The runs are unreachable now anyway.
    runRecordsRef.current.clear();
    earlyEndsRef.current.clear();
    if (accountId == null) return;
    let cancelled = false;
    void pseudonymFor(accountId).then((hash) => {
      if (!cancelled) pseudonymRef.current = hash;
    });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  /** Per tier: the real account id, a salted hash, or nothing. */
  const telemetryUser = useCallback((): ChatErrorUser | null => {
    const tier = telemetryTierRef.current;
    if (tier === 'minimal') return null;
    if (tier === 'identified') {
      const account = accountIdRef.current;
      return account == null ? null : { id: account, pseudonymous: false };
    }
    const hash = pseudonymRef.current;
    return hash == null ? null : { id: hash, pseudonymous: true };
  }, []);

  /**
   * Host classifier first (`null` falls through), built-ins second. The single
   * decision point for both the report and the run record, which otherwise
   * end up in different buckets.
   */
  const classify = useCallback((signal: ChatErrorSignal): ChatErrorCategory => {
    const custom = configRef.current.classifyChatError;
    if (custom != null) {
      try {
        const category = custom(signal);
        // Validated, not trusted: a JS host returning an unknown string would
        // make `attributionFor` yield `undefined` and put an unbounded label
        // into the metric. An unrecognised answer falls through as if the
        // host had declined.
        if (category != null && CHAT_ERROR_CATEGORIES.has(category)) {
          return category;
        }
      } catch {
        // A throwing host classifier falls back to the built-ins rather than
        // taking the frame with it.
      }
    }
    return categorizeChatError(signal);
  }, []);

  /**
   * The gateway's answer wins over `config.harness`: a per-deployment constant
   * goes stale the first time the backend is rolled and nobody edits it.
   * Neither knows ⇒ omitted, since a guessed label is worse than none.
   */
  const harnessInfo = useCallback((): HarnessInfo | null => {
    const advertised = wsRef.current?.getServerInfo?.() ?? null;
    const configured = configRef.current.harness;
    const name = advertised?.name ?? configured?.name;
    const version = advertised?.version ?? configured?.version;
    const protocol = advertised?.protocol;
    if (name == null && version == null && protocol == null) return null;
    return {
      ...(name != null ? { name } : {}),
      ...(version != null ? { version } : {}),
      ...(protocol != null ? { protocol } : {}),
    };
  }, []);

  /**
   * The send-time record plus whatever is live now. Phase, context fill and
   * streamed are active-session state, so for a background chat they are
   * OMITTED rather than filled from the wrong session.
   */
  const runContextFor = useCallback(
    (
      record: RunRecord | null,
      runId?: string,
      now = Date.now(),
    ): ChatRunContext => {
      const live =
        record != null && record.sessionId === activeSessionIdRef.current;
      const usage = live ? stateRef.current.contextUsage : null;
      const streamed =
        live && runId != null
          ? stateRef.current.messages.some(
              (m) =>
                m.runId === runId &&
                m.role === 'assistant' &&
                m.content.length > 0,
            )
          : undefined;
      return {
        ...(live && processingRef.current != null
          ? { phase: processingRef.current.label }
          : {}),
        ...(record != null
          ? {
              elapsedMs: now - record.startedAt,
              turnIndex: record.turnIndex,
              hadAttachments: record.hadAttachments,
              isRetry: record.isRetry,
              connectionGeneration: record.connectionGeneration,
            }
          : {}),
        ...(usage?.usedTokens != null && usage.limitTokens > 0
          ? { contextFill: usage.usedTokens / usage.limitTokens }
          : {}),
        ...(streamed != null ? { streamed } : {}),
      };
    },
    [],
  );

  /**
   * Emit one chat failure (`config.onChatError`). Distinct from `reportError`
   * above, which is `logException` — thrown JavaScript; nothing throws here.
   *
   * Suppressed at `minimal`: failure text and the chat it belongs to are the
   * diagnostic half a user can decline. The outcome ping below is what they
   * cannot, so the failure RATE stays honest.
   */
  const reportChatError = useCallback(
    (input: {
      readonly source: 'run' | 'gateway' | 'client';
      readonly message: string | null | undefined;
      readonly code?: string | null;
      readonly sessionId?: string | null;
      readonly runId?: string | null;
    }): void => {
      const onChatError = configRef.current.onChatError;
      if (onChatError == null) return;
      if (telemetryTierRef.current === 'minimal') return;
      const sessionId = input.sessionId ?? null;
      const record =
        input.runId != null
          ? (runRecordsRef.current.get(input.runId) ?? null)
          : null;
      // A failure we hold no record for is a run this client never sent — a
      // cron turn, another device's run in a session we know, a run in flight
      // across a reload. It still ships (those failures are real) but is
      // MARKED, because no `chat_run` will ever pair with it: counted in a
      // rate it inflates the numerator against a denominator it was never in.
      //
      // Only answerable when there IS a run id. A failure with none is local —
      // the transient gateway error, the oversize send — and `record != null`
      // read those as another client's, so the README's `owned="false"` filter
      // dropped exactly the user-visible failures nobody else could report.
      // Left undefined instead: unknown ownership, not foreign.
      const owned = input.runId != null ? record != null : undefined;
      // The model the run was SENT with, or nothing.
      //
      // Falling back to the chat's CURRENT model reintroduced exactly the
      // mislabel this exists to avoid: on a streams-first minting gateway the
      // failure frame carries the gateway id while the record is still under
      // the send key, so the lookup misses and the substitute blames whatever
      // the chat is set to now.
      const modelId = record?.modelId ?? null;
      try {
        onChatError(
          buildChatErrorReport({
            source: input.source,
            message: input.message,
            code: input.code ?? null,
            category: classify({ message: input.message, code: input.code }),
            harness: harnessInfo(),
            sessionId,
            runId: input.runId ?? null,
            modelId,
            user: telemetryUser(),
            owned,
            run: runContextFor(record, input.runId ?? undefined),
          }),
        );
      } catch {
        // Telemetry must never break the chat it is reporting on.
      }
    },
    [telemetryUser, runContextFor, harnessInfo, classify],
  );

  /**
   * Start counting a run. Keyed, not a single slot: a chat can have several
   * runs in flight, and a minting gateway claims one under two ids.
   */
  const beginRunRecord = useCallback(
    (runId: string, record: RunRecord): void => {
      // A run already counted is over; re-filing it under a new key would put
      // a dead run back among the live ones.
      if (record.emitted === true) return;
      const records = runRecordsRef.current;
      records.set(runId, record);
      // Oldest first (`Map` preserves insertion order): the oldest
      // unterminated run is the one least likely to still be coming back.
      while (records.size > MAX_TRACKED_RUNS) {
        const oldest = records.keys().next().value;
        if (oldest == null) break;
        records.delete(oldest);
      }
    },
    [],
  );

  /**
   * A run has ended. Emits exactly once — the record is consumed here, so the
   * several terminators that can retire a run cannot each report it.
   */
  const reportRunOutcome = useCallback(
    (runId: string | undefined, endedAt = Date.now()): void => {
      if (runId == null) return;
      const record = runRecordsRef.current.get(runId);
      if (record == null) {
        // Not (yet) ours. Remembered in case the ack names it — the run is
        // over either way, so the clock stops now, not when we learn of it.
        noteEarlyEnd(runId, { endedAt });
        return;
      }
      runRecordsRef.current.delete(runId);
      // Counted under another key already — see `RunRecord.emitted`.
      if (record.emitted === true) return;
      record.emitted = true;
      const onRunOutcome = configRef.current.onRunOutcome;
      if (onRunOutcome == null) return;
      const cancelled =
        cancelledNowRef.current.has(runId) ||
        cancelledRunIdsRef.current.has(runId);
      // Cancelled outranks failed: nobody was waiting on a run they stopped,
      // and counting it as a failure would inflate the rate.
      const outcome = cancelled
        ? 'cancelled'
        : record.category != null
          ? 'error'
          : 'ok';
      try {
        // Built inside the try, not just emitted from it. `harnessInfo()` calls
        // the host's `getServerInfo`, and a throw there escaped into the frame
        // handler that terminates the run — so a host bug stopped the spinner
        // from ever coming down.
        const user = telemetryUser();
        const harness = harnessInfo();
        const report: RunOutcomeReport = {
          outcome,
          durationMs: endedAt - record.startedAt,
          sessionId: record.sessionId,
          runId,
          ...(record.modelId != null ? { modelId: record.modelId } : {}),
          ...(record.category != null
            ? {
                category: record.category,
                attribution: attributionFor(record.category),
              }
            : {}),
          ...(harness != null ? { harness } : {}),
          ...(user != null ? { user } : {}),
          run: runContextFor(record, runId, endedAt),
          timestamp: Date.now(),
        };
        onRunOutcome(
          telemetryTierRef.current === 'minimal'
            ? toMinimalRunOutcome(report)
            : report,
        );
      } catch {
        // As above: never let a host's sink break the chat.
      }
    },
    [telemetryUser, runContextFor, harnessInfo, noteEarlyEnd],
  );

  /**
   * The id of the run this session currently has outstanding, if any.
   *
   * For a gateway that names no runs: with one run in flight per session at a
   * time — which is what a run-less protocol implies, since nothing could
   * distinguish two — the oldest unemitted record for the session IS the run
   * the terminator just ended. Insertion order makes "oldest" free.
   */
  /**
   * Chats whose read a run got in the way of.
   *
   * A read is declined while a run is outstanding for the chat, because the
   * live path owns the transcript then. That was a silent DROP, and it cost
   * the transcript: sending a message into a chat that was still loading
   * registered a run synchronously, so the page already in flight was
   * discarded on arrival — cursor and all — and the chat rendered as the one
   * turn just sent. A conversation with months of history looked empty until
   * the next reopen or reconnect.
   *
   * So the chat is remembered instead, and read again when the run retires.
   */
  const deferredReadsRef = useRef<Set<string>>(new Set());
  /**
   * How many turns have been sent in each chat, this tab's life.
   *
   * The guards around a transcript read cover a run that is still
   * outstanding, which is the long case. They do not cover a send that
   * STARTED AND FINISHED while a read was in flight — open an uncached chat,
   * send, get a fast answer, and a slow read arrives with no run outstanding
   * and nothing processing, carrying the tail as it was before the exchange.
   * Applying it wound the conversation back past what the user had just
   * said and what the agent had just answered.
   *
   * Same shape as `sessionModelFetchSeqRef`: read at issue, compared on
   * arrival, and a page whose count has moved is somebody else's answer.
   */
  const sendSeqRef = useRef<Map<string, number>>(new Map());

  const outstandingRunForSession = useCallback(
    (sessionId: string | null): string | undefined => {
      if (sessionId == null) return undefined;
      for (const [runId, record] of runRecordsRef.current) {
        if (record.sessionId === sessionId && record.emitted !== true) {
          return runId;
        }
      }
      return undefined;
    },
    [],
  );

  /**
   * Retire a message's `unsent` mark: the gateway acknowledged the send, so
   * its transcript has the message and the merge may treat it normally.
   *
   * Writes through both the state slot and the store, because a send can be
   * acknowledged after the user has switched away — at which point the chat's
   * messages live only on disk, and the mark left there would protect the
   * bubble from replacement for the rest of its life.
   */
  const clearUnsentMark = useCallback(
    (sessionId: string, messageId: string): void => {
      const account = accountIdRef.current;
      const strip = (messages: readonly ChatMessage[]) => {
        let hit = false;
        const next = messages.map((m) => {
          if (m.id !== messageId || m.unsent !== true) return m;
          hit = true;
          const { unsent: _acked, ...rest } = m;
          return rest;
        });
        return hit ? next : null;
      };
      // The BACKGROUND BUFFER first, and unconditionally.
      //
      // `switchSession` and `createSession` stash a copy of the messages there
      // when a chat has work in flight, and whatever is in that copy is later
      // flushed over the store — so stripping only state and disk left the
      // stale copy to put the mark straight back. The bubble then carried
      // `unsent` for the rest of its life: never replaceable by a gateway
      // read, and `hasUnsent` pinning the whole chat against a cross-device
      // delete. Exactly the shape of stuck state the mark was added to avoid.
      const buffered = backgroundSessionsRef.current.get(sessionId);
      if (buffered != null) {
        const next = strip(buffered);
        if (next != null) backgroundSessionsRef.current.set(sessionId, next);
      }
      if (activeSessionIdRef.current === sessionId) {
        setState((prev) => {
          if (prev.activeSessionId !== sessionId) return prev;
          const next = strip(prev.messages);
          return next == null ? prev : { ...prev, messages: next };
        });
        return;
      }
      // Nothing else to reach: a chat this tab has neither open nor
      // buffered holds no reasoning of ours. The paint cache is the agent's
      // own rows, and what it does or does not keep is the agent's business.
      void account;
    },
    [],
  );

  /**
   * The socket went away with runs in flight. Count them as failures now.
   *
   * Every other run ref is cleared on this transition; `runRecordsRef` was
   * not, so a mid-run drop either vanished from the denominator until the LRU
   * evicted it, or — worse — shipped as `ok` if a terminator landed after the
   * reconnect. The user did not get an answer, so `gateway_unreachable` is
   * what happened; a run they had already stopped keeps `cancelled`, which is
   * why this must run BEFORE `cancelledNow` is cleared.
   */
  const settleOnDisconnect = useCallback(
    (generation: number): void => {
      for (const [runId, record] of [...runRecordsRef.current]) {
        if (record.connectionGeneration !== generation) continue;
        record.category ??= 'gateway_unreachable';
        reportRunOutcome(runId);
      }
    },
    [reportRunOutcome],
  );

  /**
   * The ack has named a run that already ended — a streams-first gateway.
   * Count it now from what its terminator said, under the gateway's id, so
   * it joins the denominator with its failure category and the duration it
   * really had, rather than being dropped (or counted `ok` at ack time).
   */
  const settleEarlyEnd = useCallback(
    (runId: string, record: RunRecord): void => {
      const end = earlyEndsRef.current.get(runId);
      if (end?.endedAt == null) return;
      earlyEndsRef.current.delete(runId);
      // Already counted under another key (Stop in the ack gap): the second
      // key must not resurrect it. `reportRunOutcome` refuses too, but
      // re-filing first would put a dead run back among the live ones.
      if (record.emitted === true) return;
      if (end.category != null) record.category = end.category;
      runRecordsRef.current.set(runId, record);
      reportRunOutcome(runId, end.endedAt);
    },
    [reportRunOutcome],
  );

  /**
   * `handleFrame`'s transient channel: gateway errors matching no in-flight
   * request. Reported without a session — attributing a connection-wide
   * failure to whichever chat was open would pin ten of them on one chat.
   */
  const reportGatewayTransientError = useCallback(
    (message: string, code?: string): void => {
      reportChatError({ source: 'gateway', message, code });
      showTransientError(message);
    },
    [reportChatError, showTransientError],
  );

  /**
   * Send one organisation change to the gateway.
   *
   * Titles, pins, folders, clear and delete are moving off the terminal's own
   * sync service and onto openclaw's native session fields, which it has owned
   * since 2026.8.1. This is the write half, and it runs ALONGSIDE the local
   * state for now: local is still what renders, so a refused write costs
   * nothing yet. That is also why it is reported rather than reverted —
   * reverting would undo a change that is, at this stage, still in effect.
   *
   * Reported and not swallowed on purpose. Every one of these methods exists
   * on the gateways this fleet runs, so a failure here is a real defect, and
   * a silent write queue is precisely what let "my other device shows an
   * older version of this chat" go unnoticed for weeks.
   */
  const gatewayOrganisationWrite = useCallback(
    (what: string, run: (ws: AgentWebSocket) => Promise<unknown>): void => {
      const ws = wsRef.current;
      if (ws == null) return;
      run(ws).catch((err: unknown) => {
        reportError(err);
        showTransientError(`Could not ${what} on the agent.`);
      });
    },
    [reportError, showTransientError],
  );

  /**
   * The chat "+ New Chat" is currently targeting, before anyone has typed in
   * it. At most one exists — `createSession` prunes the last one — and it is
   * cleared by the first send, which is what makes the chat real.
   *
   * A ref rather than a state read because the two live in the same batch;
   * see `isDraftSession`.
   */
  const draftSessionIdRef = useRef<string | null>(null);

  /**
   * The `category` a chat can safely be filed under, and why it might be
   * none.
   *
   * `null` unfiles a chat, which is always safe. A NAME is only safe once the
   * gateway is known to hold that group; otherwise the field is omitted
   * entirely ("leave it alone"), because an omitted field is a no-op and a
   * bad name is a chat nobody can find. The local move still applies either
   * way, and the next connect's catalog read plus the folder's own
   * `groups.put` converge it.
   */
  const safeCategory = useCallback(
    (name: string | null): { category?: string | null } => {
      if (name == null) return { category: null };
      const known = gatewayGroupNamesRef.current;
      return known != null && known.has(name) ? { category: name } : {};
    },
    [],
  );

  /**
   * A chat that exists only on this device: opened with "+ New Chat" and not
   * yet sent in.
   *
   * Every gateway write about a chat's ORGANISATION goes through
   * `sessions.patch`, and `sessions.patch` CREATES the row when it does not
   * exist. So renaming, pinning or filing a draft claims a session on the
   * gateway for a chat the user may never send in — and the next roster read
   * projects that row back carrying no `isDraft`, leaving a permanent "New
   * Chat" in the sidebar. That is what the draft lifecycle exists to prevent,
   * and the model pick was the only caller that checked.
   *
   * The folder button was the worst of the three: `handleNewChatInFolder`
   * calls `createSession` and then `setSessionFolder`, so composing into a
   * folder claimed a row every time — and the row came back with a
   * `category`, which `isUntitledRow` does not hide.
   *
   * Guarded rather than blocked: the local update still applies, so the name,
   * the pin and the folder are all in front of the user, and the first send
   * publishes them (see `flushDraftOrganisation`).
   */
  /**
   * A chat the gateway has never heard of, and must not be told about.
   *
   * Two kinds qualify and the name only says one of them. A DRAFT is the
   * unsent chat in the composer. A TEMPORARY chat is the private one whose
   * whole premise is leaving nothing behind — `createSession` does not even
   * put it in the draft ref — and a `sessions.*` write against it claims a
   * gateway row for a conversation this device never persists, which is the
   * exact leak the draft guard exists to close.
   */
  const isDraftSession = useCallback(
    (sessionId: string): boolean =>
      // The REF first, and it is what makes this correct rather than
      // usually-correct. `createSession` syncs it before its `setState`, the
      // same way it syncs the active-session refs — because the caller that
      // matters does both in one batch: `handleNewChatInFolder` calls
      // `createSession` and then `setSessionFolder` in the same click
      // handler, and React has not rendered in between, so the row is not in
      // `stateRef` yet and a state-only check reads the draft as a real chat.
      draftSessionIdRef.current === sessionId ||
      (() => {
        const row = stateRef.current.sessions.find(
          (s) => s.sessionId === sessionId,
        );
        return row?.isDraft === true || row?.isTemporary === true;
      })(),
    [],
  );

  /**
   * Group names the gateway is KNOWN to hold, or null when this connection
   * has never read the catalog successfully.
   *
   * A chat is filed by group NAME, and filing it under a name the gateway
   * does not have puts it in a folder no device can show — which reads as the
   * chat having vanished. The migration already refuses to do that; these are
   * the live writes, which could still do it after `createCustomFolder`'s own
   * `groups.put` was refused (that path reports and keeps the folder locally,
   * so the folder is on screen and not on the gateway).
   */
  const gatewayGroupNamesRef = useRef<Set<string> | null>(null);

  /** A chat's folder name as the gateway knows it: its group `category`. */
  /**
   * The folder name to file a chat under, or null to leave it unfiled.
   *
   * `undefined` in, null out, and that is the honest answer: the chat is not
   * in a folder.
   *
   * An id that resolves to NOTHING is a different thing, and returning null
   * for it actively unfiled a chat the user had just filed. That used to be
   * reachable: folder ids churned — a locally created folder had a random id
   * until the first chat-list read renamed it to the folder's name — so a
   * draft composed in a brand-new folder resolved to null on its first send.
   * The id is minted as the name now (`createCustomFolder`), so the churn is
   * gone and this case means a folder that genuinely no longer exists —
   * deleted here, or on another device. Unfiling is right for that, and it is
   * now the only thing it can mean.
   */
  const folderNameFor = useCallback(
    (customFolderId: string | undefined): string | null => {
      if (customFolderId == null) return null;
      const folder = stateRef.current.customFolders.find(
        (f) => f.id === customFolderId,
      );
      return folder?.name ?? null;
    },
    [],
  );

  /**
   * Where paging back through each chat's history resumes.
   *
   * `nextOffset` null means the agent has nothing older — either it said so
   * (`hasMore: false`) or it does not page at all. `transcriptId` is the
   * backing transcript the offset belongs to: offsets are meaningless across
   * a reset or a branch switch, so a page answering with a different one is
   * discarded rather than spliced on.
   */
  const olderPagesRef = useRef<
    Map<string, { nextOffset: number | null; transcriptId: string | null }>
  >(new Map());
  /**
   * Which roster read is current. Two can be in flight on one connection —
   * the slow connect-time read and a catch-up read for a session created
   * elsewhere — and the later one is the one that has seen more. See
   * `hydrateRosterFromGateway`.
   */
  const rosterReadSeqRef = useRef(0);
  /**
   * A roster read carrying the one-time organisation migration is in flight.
   *
   * The migration is per account per device and losing it costs every local
   * title, pin and folder — so a catch-up read must not supersede it. See
   * `requestForeignSessionHydration`.
   */
  const rosterMigrateInFlightRef = useRef(0);

  /** Session currently fetching an older page — single-flight per chat. */
  const olderPageInFlightRef = useRef<string | null>(null);
  /**
   * Offsets already requested for each chat, so a gateway that answers every
   * request with the same page ends paging instead of looping. Reset whenever
   * `olderPagesRef` is re-seeded from a fresh tail read.
   */
  const requestedOffsetsRef = useRef<Map<string, Set<number>>>(new Map());

  /**
   * A chat's bubbles from the paint cache — the agent's own rows for it, as
   * its last answer carried them, run through the same projection a live read
   * uses.
   *
   * This is the ONLY thing read off disk for a transcript, and it exists so
   * opening a chat paints immediately instead of showing an empty pane for
   * the second it takes to connect and read. It is not consulted for
   * decisions: nothing asks it what the agent holds, whether a bubble was
   * deleted, or what to keep — the read that follows answers all of that by
   * replacing it.
   *
   * Empty means "not read on this device yet", NOT "no messages".
   */
  const paintFromCache = useCallback(
    (account: string, sessionId: string): readonly ChatMessage[] =>
      projectGatewayHistory(
        historyStore.loadSessionTranscript(account, sessionId),
        {
          systemPreamble: configRef.current.systemPreamble,
          languageDirectives: allLanguageDirectives(),
        },
      ),
    [historyStore],
  );

  /**
   * Read one chat back from the GATEWAY and reconcile it with what we hold.
   *
   * The transcript DIME stores is written by whichever tab watched the run.
   * When no tab was watching — the socket dropped mid-answer, the user
   * switched chats while a six-minute reply ran, a scheduled task committed
   * its result overnight — nothing writes those turns, and every device
   * inherits the gap. openclaw's own store has them, and `chat.history`
   * serves them display-normalized for exactly this use (its Control UI
   * hydrates the same way).
   *
   * Additive: `mergeGatewayTranscript` completes a frozen bubble and inserts
   * turns we never saw, but never deletes a local one, so a short or failed
   * read cannot blank a chat. Nothing is written back to the gateway — this
   * direction is read-only.
   *
   * Skipped while a run is in flight for the session: the live path owns the
   * transcript then, and replacing a streaming bubble mid-delta would fight
   * it. The next open or reconnect picks it up.
   */
  /**
   * Reads a chat from the gateway. Answers whether a read was actually
   * ISSUED.
   *
   * Three conditions decline one, and a caller cannot tell which without
   * being told: no socket, a run already outstanding for this chat, or a run
   * in flight on the open chat. The callers that show "Loading conversation…"
   * were assuming a read always followed, so switching into an uncached chat
   * with the socket down left that message on screen with nothing coming to
   * replace it.
   */
  const hydrateSessionFromGateway = useCallback(
    (sessionId: string): boolean => {
      const ws = wsRef.current;
      const account = accountIdRef.current;
      if (ws == null || account == null) return false;
      if (outstandingRunForSession(sessionId) != null) {
        deferredReadsRef.current.add(sessionId);
        return false;
      }
      if (
        sessionId === activeSessionIdRef.current &&
        stateRef.current.processing != null
      ) {
        return false;
      }
      const generation = connectionGenerationRef.current;
      const sendsAtIssue = sendSeqRef.current.get(sessionId) ?? 0;
      // A read is under way, so any earlier failure for this chat no longer
      // describes anything — clearing it here rather than only on success is
      // what stops a retry from showing the failure notice while it runs.
      setState((prev) =>
        prev.failedTranscriptFor === sessionId
          ? { ...prev, failedTranscriptFor: null }
          : prev,
      );
      ws.request<GatewayHistoryPage>('chat.history', {
        sessionKey: sessionId,
        limit: CHAT_HISTORY_GATEWAY_LIMIT,
        // Explicit, so the answer carries `hasMore` / `nextOffset` /
        // `totalMessages` — the only way to tell a complete transcript from a
        // page the gateway bounded (see `GatewayHistoryPage`).
        offset: 0,
        // The gateway truncates long entries and replaces oversized ones with
        // a placeholder, expecting a client to expand them on demand
        // (`chat.message.get`). That was tolerable while this read only
        // ADDED to the local copy; now that it replaces it, a default cap
        // would visibly shorten real answers. This ceiling is far above any
        // real bubble, so the placeholder path stays theoretical until a
        // "Show more" control exists to handle it.
        maxChars: CHAT_HISTORY_MAX_CHARS,
      })
        .then((res) => {
          // Everything below is only true for the account and connection the
          // read was issued on; an account switch or reconnect in the gap
          // makes the answer somebody else's.
          if (accountIdRef.current !== account) return;
          if (connectionGenerationRef.current !== generation) return;
          if (outstandingRunForSession(sessionId) != null) {
            // A run started while this was in flight — typing into a chat
            // that was still loading does exactly that. The live path owns
            // the transcript now, so this page cannot be applied; it is asked
            // for again when the run retires rather than lost.
            deferredReadsRef.current.add(sessionId);
            return;
          }
          // Where paging for this chat resumes. Kept per session so switching
          // chats mid-scroll cannot page one transcript with another's
          // offset, and re-read on every hydration so a chat that grew since
          // the last look starts from the fresh tail.
          olderPagesRef.current.set(sessionId, {
            nextOffset: res.hasMore === true ? (res.nextOffset ?? null) : null,
            transcriptId: pageTranscriptId(res),
          });
          // Paging restarts from this tail, so what was asked for before it
          // says nothing about what is a repeat now.
          requestedOffsetsRef.current.delete(sessionId);
          if (sessionId === activeSessionIdRef.current) {
            const canLoadOlder = res.hasMore === true && res.nextOffset != null;
            setState((prev) =>
              prev.canLoadOlder === canLoadOlder
                ? prev
                : { ...prev, canLoadOlder },
            );
          }
          // A read that carried no rows array at all is a failure — a gateway
          // without the method, a malformed answer, a socket that went away —
          // and it says nothing about the chat. An EMPTY array is an answer:
          // that is what a chat cleared on another device looks like from
          // here, and it applies.
          // A turn was sent in this chat while this was in flight, and it is
          // no longer outstanding — so the run guard above let this through
          // while the answer it describes predates the exchange the user
          // just had. Dropped rather than deferred: what is in memory IS the
          // newer truth (the send and its answer), and the next open or
          // reconnect reads the agent's version of it.
          if ((sendSeqRef.current.get(sessionId) ?? 0) !== sendsAtIssue) {
            return;
          }
          const rows = res.messages;
          if (!Array.isArray(rows)) return;
          // A nonsense answer — no rows, but the gateway says there are more —
          // is not "this chat is empty". One clause, and it is about reading
          // the response rather than second-guessing it: `chat.history` pages
          // from the TAIL, so the first page of a chat that has anything in it
          // cannot be empty.
          if (rows.length === 0 && res.hasMore === true) return;

          // The agent's answer becomes the paint cache verbatim, replacing
          // whatever was there. No merge, no append, no per-bubble decision
          // about what to keep: the agent holds the conversation, so its
          // answer IS the conversation.
          historyStore.saveSessionTranscript(account, sessionId, rows);
          if (sessionId !== activeSessionIdRef.current) return;

          const projected = projectGatewayHistory(rows, {
            systemPreamble: configRef.current.systemPreamble,
            languageDirectives: allLanguageDirectives(),
          });
          setState((prev) => {
            if (prev.activeSessionId !== sessionId) return prev;
            // The one thing carried across: a message that never reached the
            // agent. It is not in the agent's record because it never got
            // there, so a replace would drop the text the user typed while
            // they are still looking at the retry. Memory only — a reload
            // loses it, which is correct for something that never happened.
            //
            // But "never reached the agent" is what the mark MEANS, not what
            // it proves. It is set optimistically on every send and cleared
            // only by the acknowledgement, so a socket dropping in that gap
            // leaves it set on a message the agent did receive — and then the
            // reconnect read renders the agent's copy AND appends ours, so
            // the user saw what they typed twice, once for real and once as
            // an unsent duplicate that never resolved.
            //
            // So the agent's own answer decides: a marked message the
            // transcript already ends with is one the agent has.
            //
            // Two ways of asking, and the exact one REPLACES the other where
            // it applies rather than sitting in front of it.
            //
            // A row carrying back the key this client put on the send IS that
            // send, and nothing about text or timing can be wrong about it. So
            // when the answer carries keys at all — proof this gateway returns
            // them — a message whose key we still hold is decided by the key
            // alone. Consulting the text window as well would undo the point:
            // sending the same thing twice is ordinary, and the second copy
            // would match the first's row and be dropped off the screen while
            // the user is still looking at its retry.
            //
            // The window is the fallback for everything the key cannot answer:
            // an older gateway that returns no key, a send from before this
            // page load, a row written by something that never had one.
            const failed = prev.messages.filter((m) => m.unsent === true);
            if (failed.length === 0) return { ...prev, messages: projected };
            const answeredKeys = new Set<string>();
            for (const r of rows) {
              const key = sendKeyFromRow(r);
              if (key != null) answeredKeys.add(key);
            }
            const keyByMessageId = new Map<string, string>();
            for (const [key, messageId] of sentMessageIdsRef.current) {
              keyByMessageId.set(messageId, key);
            }
            const tail = projected.slice(-UNSENT_MATCH_WINDOW);
            const orphaned = failed.filter((m) => {
              const ourKey = keyByMessageId.get(m.id);
              if (ourKey != null && answeredKeys.size > 0) {
                return !answeredKeys.has(ourKey);
              }
              return !tail.some((row) => isSameTurn(m, row));
            });
            return {
              ...prev,
              messages:
                orphaned.length === 0 ? projected : [...projected, ...orphaned],
            };
          });
        })
        .catch(() => {
          // A gateway without `chat.history`, an unreadable answer, or a
          // socket that went away. The cached transcript stands, and where
          // there is one the failure needs no announcement — the next open
          // or reconnect tries again and the user is reading their
          // conversation meanwhile.
          //
          // Recorded, though, because there is now a case where it DOES
          // change something the user can act on: a chat this device has
          // nothing cached for has nothing to fall back to, and silence
          // there put the "New Chat" greeting over a conversation with
          // months of history in it — telling the user it was empty because
          // we could not reach the agent. The view reads this to offer a
          // retry instead of making that claim.
          if (accountIdRef.current !== account) return;
          if (connectionGenerationRef.current !== generation) return;
          setState((prev) =>
            prev.activeSessionId === sessionId
              ? { ...prev, failedTranscriptFor: sessionId }
              : prev,
          );
        })
        .finally(() => {
          // The wait is over either way. A failed read leaves the chat empty
          // rather than loading — there is nothing else coming until the next
          // open or reconnect, and a spinner that never resolves is worse
          // than an empty chat.
          setState((prev) =>
            prev.loadingTranscriptFor === sessionId
              ? { ...prev, loadingTranscriptFor: null }
              : prev,
          );
        });
      return true;
    },
    [historyStore, outstandingRunForSession],
  );

  /**
   * Fetch the page of older messages before the ones on screen, and prepend.
   *
   * The counterpart to the bounded first page. `chat.history` caps a response
   * three ways — `limit`, a 6 MB budget that discards oldest-first, and the
   * current transcript branch — so the first read of a long chat shows its
   * tail and nothing announces that. This is how the rest is reachable, and
   * it follows openclaw's own Control UI: request `offset: nextOffset`,
   * prepend, stop when the gateway stops advancing.
   *
   * Three things it refuses to do:
   *
   *  - Splice across transcripts. Offsets belong to one transcript identity,
   *    and a `sessions.reset` reuses the session KEY with a new one, so a
   *    page answering with a different `sessionId` is dropped.
   *  - Run twice at once. A fast scroll fires this repeatedly; the second
   *    call would request the same offset and duplicate the page.
   *  - Fight a live run. A run owns the transcript while it streams.
   */
  /**
   * Read the open chat from the agent again, after a read that failed.
   *
   * Says so when there is nothing to try with. Silently doing nothing on a
   * retry the user pressed, on a screen that is already telling them the
   * conversation could not be loaded, reads as the button being broken.
   */
  const retryTranscriptRead = useCallback((): void => {
    const sessionId = activeSessionIdRef.current;
    if (sessionId == null) return;
    const started = hydrateSessionFromGateway(sessionId);
    if (started) {
      setState((prev) =>
        prev.activeSessionId === sessionId
          ? { ...prev, loadingTranscriptFor: sessionId }
          : prev,
      );
      return;
    }
    showTransientError('Still can’t reach the agent. Check the connection.');
  }, [hydrateSessionFromGateway, showTransientError]);

  const loadOlderMessages = useCallback((): void => {
    const ws = wsRef.current;
    const account = accountIdRef.current;
    const sessionId = activeSessionIdRef.current;
    if (ws == null || account == null || sessionId == null) return;
    if (olderPageInFlightRef.current != null) return;
    if (outstandingRunForSession(sessionId) != null) return;
    const cursor = olderPagesRef.current.get(sessionId);
    const offset = cursor?.nextOffset;
    if (offset == null) return;

    const generation = connectionGenerationRef.current;
    olderPageInFlightRef.current = sessionId;
    // Every offset this chat has asked for. The loop-breaker: a gateway that
    // ignores `offset` answers with the same page forever, and comparing
    // against the ONE offset just requested cannot see that.
    const seenOffsets =
      requestedOffsetsRef.current.get(sessionId) ?? new Set<number>();
    seenOffsets.add(offset);
    requestedOffsetsRef.current.set(sessionId, seenOffsets);
    setState((prev) => ({ ...prev, loadingOlder: true }));
    const settle = (): void => {
      olderPageInFlightRef.current = null;
      setState((prev) =>
        prev.loadingOlder ? { ...prev, loadingOlder: false } : prev,
      );
    };

    void ws
      .request<GatewayHistoryPage>('chat.history', {
        sessionKey: sessionId,
        limit: CHAT_HISTORY_GATEWAY_LIMIT,
        offset,
        maxChars: CHAT_HISTORY_MAX_CHARS,
      })
      .then((res) => {
        if (accountIdRef.current !== account) return;
        if (connectionGenerationRef.current !== generation) return;
        if (activeSessionIdRef.current !== sessionId) return;

        // A reset or branch switch under us. Drop the page and stop paging
        // this transcript; the next hydration re-seeds from the new tail.
        const transcriptId = pageTranscriptId(res);
        if (
          cursor?.transcriptId != null &&
          transcriptId != null &&
          transcriptId !== cursor.transcriptId
        ) {
          olderPagesRef.current.set(sessionId, {
            nextOffset: null,
            transcriptId,
          });
          setState((prev) => ({ ...prev, canLoadOlder: false }));
          return;
        }

        // Advance the cursor first, so a page that repeats an offset ends
        // paging instead of looping on it.
        //
        // "Different from the one just asked for, and not asked for before"
        // — not "smaller". `chat.history` pages from the TAIL: offset 0 is
        // the newest page and the offsets GROW as it walks backwards, so
        // requiring a smaller one made `advanced` null on the second page and
        // capped scroll-back at two pages, silently, in exactly the long
        // conversations paging exists for.
        const seen = requestedOffsetsRef.current.get(sessionId);
        const nextOffset = res.nextOffset;
        const advanced =
          res.hasMore === true &&
          nextOffset != null &&
          nextOffset !== offset &&
          !(seen?.has(nextOffset) ?? false)
            ? nextOffset
            : null;
        olderPagesRef.current.set(sessionId, {
          nextOffset: advanced,
          transcriptId: transcriptId ?? cursor?.transcriptId ?? null,
        });

        const older = projectGatewayHistory(res.messages, {
          systemPreamble: configRef.current.systemPreamble,
          languageDirectives: allLanguageDirectives(),
        });
        if (older.length === 0) {
          setState((prev) => ({ ...prev, canLoadOlder: advanced != null }));
          return;
        }

        // Prepended by ID, with a text compare kept for the BOUNDARY row
        // alone.
        //
        // Keying the whole page on `role:text` dropped genuine repeated
        // turns: a conversation with two "yes" or two "continue" in it lost
        // the older one every time it was paged back into view, silently
        // rewriting history to remove a repetition the user actually typed.
        // Ids are the right key — a projected row carries the gateway's
        // transcript entry id — and the only thing they cannot catch is the
        // single row the two pages may share, whose local copy was minted by
        // this client under an id of its own. So that one row, and only that
        // one, is still matched by text.
        setState((prev) => {
          if (prev.activeSessionId !== sessionId) return prev;
          const knownIds = new Set(prev.messages.map((m) => m.id));
          const boundary = prev.messages[0];
          const boundaryKey =
            boundary == null
              ? null
              : `${boundary.role}:${boundary.content.slice(0, 120)}`;
          // The text compare is for the LAST row of the page — the only one
          // that can be the shared boundary, since the page ends where the
          // transcript on screen begins. Applied to every row it was a
          // whole-page text key again by another name: a page containing an
          // earlier "yes" identical to the boundary "yes" lost it, which is
          // the repetition-eating bug the id keys were meant to end.
          const lastIndex = older.length - 1;
          const fresh = older.filter((m, i) => {
            if (knownIds.has(m.id)) return false;
            if (i !== lastIndex) return true;
            return `${m.role}:${m.content.slice(0, 120)}` !== boundaryKey;
          });
          if (fresh.length === 0) {
            return { ...prev, canLoadOlder: advanced != null };
          }
          // The PERSISTENCE cap is a disk budget, not a view budget, and
          // this is the one place those have to differ.
          //
          // `saveSessionMessages` keeps the newest `PERSISTED_HISTORY_HARD_CAP`
          // and returns what it kept — so the rows just prepended are the
          // FIRST it drops. Taking that return into state (which is what this
          // did) turned scroll-back into a no-op the moment a chat reached the
          // cap: the page was fetched, nothing appeared, and `canLoadOlder`
          // stayed true, so the user could keep clicking forever.
          //
          // Not cached. The paint cache holds one thing — the tail the last
          // read returned — so that reopening the chat paints instantly and
          // then offers to page back again. Writing pages into it would make
          // it a growing local record of the conversation, which is the thing
          // the agent already is.
          const messages = [...fresh, ...prev.messages];
          return {
            ...prev,
            messages,
            canLoadOlder: advanced != null,
          };
        });
      })
      .catch((err: unknown) => {
        // A gateway that does not page, or a socket that went away. The
        // transcript on screen stands and the cursor is left alone so a
        // retry is possible.
        logModelDebug('history-older-page-failed', {
          sessionId,
          offset,
          error: String(err),
        });
      })
      .finally(settle);
  }, [outstandingRunForSession, logModelDebug]);

  /**
   * One-time push of local organisation onto the gateway.
   *
   * Titles, pins and folders lived only in the terminal's own documents, so
   * the gateway rows for existing chats carry none of them. Without this the
   * first roster read after upgrading would replace every user-set name with
   * the gateway's generated title and drop every folder.
   *
   * Runs once per account per device, on the rows of the first roster read,
   * and only for values the gateway does not already have — so a second
   * device with staler local state cannot overwrite what the first one
   * migrated. Failures are logged and skipped: the migration is best-effort
   * by nature, and retrying it forever would fight a chat the gateway has
   * legitimately pruned.
   *
   * Returns the same rows with the accepted patches folded in, so the caller
   * can project the sidebar it already read instead of spending a second
   * round trip to see its own writes.
   */
  const migrateOrganisationToGateway = useCallback(
    async (
      rows: readonly GatewayRosterRow[],
      /** The gateway's catalog, or null when that read FAILED. */
      gatewayGroups: readonly GatewaySessionGroup[] | null,
    ): Promise<readonly GatewayRosterRow[]> => {
      const ws = wsRef.current;
      const account = accountIdRef.current;
      if (ws == null || account == null) return rows;
      const localSessions = historyStore.loadSessions(account);
      const localFolders = historyStore.loadCustomFolders(account);
      if (localSessions.length === 0 && localFolders.length === 0) return rows;

      const rowById = new Map(
        rows
          .filter(isTerminalChatRow)
          .map((row) => [bareSessionId(row.key) ?? '', row] as const),
      );

      // Folders first: a chat cannot be filed into a group the gateway does
      // not have yet.
      //
      // The UNION, not this device's list. `sessions.groups.put` replaces the
      // whole ordered catalog, so putting the local names alone deleted every
      // group the gateway had and this device did not — and the migration runs
      // per device, so a laptop that was offline during the first device's
      // migration came back later and wiped the groups it had never seen.
      // Member-less folders vanished from every device's sidebar (a filed
      // chat's `category` would at least have re-created its group).
      //
      // Gateway order is preserved and local-only names are appended, so the
      // catalog only ever grows here; a run with nothing to add skips the
      // write entirely rather than re-putting an identical list.
      // A failed catalog read means we do not know what is on the gateway,
      // and this PUT replaces the whole list — so with nothing to union
      // against, the only safe move is not to write at all.
      const gatewayNames = gatewayGroups?.map((g) => g.name) ?? null;
      const known = new Set(gatewayNames ?? []);
      const newNames = localFolders
        .map((f) => f.name.trim())
        .filter((n) => n.length > 0 && !known.has(n));
      // Whether the catalog now DEMONSTRABLY holds every local folder name.
      // The per-chat `category` patches below depend on it, and the previous
      // reasoning here — that they are field-scoped, so unaffected — was the
      // wrong test: field-scoped says nothing about whether the group exists.
      // Filing a chat into a group the gateway does not hold puts it under a
      // folder no device can show, which reads as the chat having vanished.
      let groupsHold = gatewayNames != null;
      if (gatewayNames != null && newNames.length > 0) {
        groupsHold = await putSessionGroups(ws, [
          ...gatewayNames,
          ...newNames,
        ]).then(
          () => true,
          (err: unknown) => {
            reportError(err);
            return false;
          },
        );
      }

      const applied = new Map<string, SessionOrganisationPatch>();
      for (const session of localSessions) {
        const row = rowById.get(session.sessionId);
        if (row == null) continue; // pruned, or never reached the gateway
        const localTitle = session.title.trim();
        const hasGatewayLabel =
          row.label != null && row.label.trim().length > 0;
        const localFolderName = localFolders.find(
          (f) => f.id === session.customFolderId,
        )?.name;
        const hasGatewayCategory =
          row.category != null && row.category.trim().length > 0;
        const patch: SessionOrganisationPatch = {
          ...(localTitle.length > 0 &&
          localTitle !== DEFAULT_SESSION_TITLE &&
          !hasGatewayLabel
            ? { label: localTitle.slice(0, SESSION_TITLE_MAX_LEN) }
            : {}),
          ...(session.pinned === true && row.pinned !== true
            ? { pinned: true }
            : {}),
          ...(groupsHold && localFolderName != null && !hasGatewayCategory
            ? { category: localFolderName }
            : {}),
        };
        if (Object.keys(patch).length === 0) continue;
        let ok = true;
        await patchSessionOrganisation(ws, session.sessionId, patch).catch(
          (err) => {
            ok = false;
            reportError(err);
          },
        );
        if (ok) applied.set(session.sessionId, patch);
      }

      if (applied.size === 0) return rows;
      return rows.map((row) => {
        const patch = applied.get(bareSessionId(row.key) ?? '');
        if (patch == null) return row;
        // Field by field rather than a blanket spread: the patch type allows
        // the `null` that means "unfile", which a row never carries.
        return {
          ...row,
          ...(patch.label != null ? { label: patch.label } : {}),
          ...(patch.pinned != null ? { pinned: patch.pinned } : {}),
          ...(patch.category != null ? { category: patch.category } : {}),
        };
      });
    },
    [historyStore, reportError],
  );

  /**
   * Forget everything this device holds ABOUT a chat, short of the row.
   *
   * Shared by the two paths that make a chat stop existing here — the user
   * deleting it, and a complete roster read establishing it was deleted
   * elsewhere — because doing half of it leaves the chat half-alive. The
   * roster path used to delete only the transcript blob, and that is the
   * partial version: leave the run ownership behind and the dropped chat's
   * `chat final` still resolves to it, and the background branch writes the
   * transcript back to the store AFTER the blob was deleted. What made that
   * visible was orphan recovery rebuilding the chat from the re-written blob
   * as "Recovered chat"; recovery is gone now, so the leftovers are quieter
   * — a buffer and a run ownership entry for an id that means nothing — but
   * still wrong, and still this function's job.
   *
   * Deliberately NOT included: the tombstone, the sync queue and the gateway
   * `sessions.delete`. Those say "the user deleted this", and the roster path
   * is reacting to a delete that already happened somewhere else.
   */
  const forgetSessionLocally = useCallback(
    (sessionId: string): void => {
      // The buffered transcript and stashed indicator go with the session.
      // These used to be dropped incidentally by the "nothing is streaming"
      // cleanup in `updateActiveSession`; that no longer owns the indicator's
      // lifetime, so losing a session mid-run would otherwise leave both
      // behind for an id that no longer exists.
      backgroundSessionsRef.current.delete(sessionId);
      backgroundProcessingRef.current.delete(sessionId);
      for (const key of [...unackedSendKeysRef.current]) {
        // Hygiene only — nothing observable depends on it. Every reader is
        // already unreachable for a gone chat's key: `ourKeysHere` filters by
        // ownership, which the loop below removes, and `onRunId` deletes the
        // key unconditionally. The set is bounded too, so this is not holding
        // memory either. Kept so the set means what it says rather than
        // carrying entries for chats that no longer exist.
        if (runOwnershipRef.current.get(key) === sessionId) {
          unackedSendKeysRef.current.delete(key);
        }
      }
      // Recorded HERE, not only at ack time. The ack is a microtask, so a run
      // is named long before a user reaches the delete button — which makes
      // this the common ordering, not the rare one.
      const orphaned: string[] = [];
      for (const [runId, owner] of runOwnershipRef.current) {
        if (owner !== sessionId) continue;
        orphaned.push(runId);
        runOwnershipRef.current.delete(runId);
        pendingRunKeysRef.current.delete(runId);
      }
      if (orphaned.length > 0) {
        orphanedRunIdsRef.current = rememberCancelled(
          orphanedRunIdsRef.current,
          orphaned,
        );
      }
      // Per-chat bookkeeping keyed by an id that is about to mean nothing.
      olderPagesRef.current.delete(sessionId);
      requestedOffsetsRef.current.delete(sessionId);
      modelWrittenRef.current.delete(sessionId);
      modelPickSeqRef.current.delete(sessionId);
      rosterGenerationRef.current.delete(sessionId);
      const account = accountIdRef.current;
      if (account != null) {
        historyStore.deleteSessionMessages(account, sessionId);
      }
    },
    [historyStore],
  );

  /**
   * Record the outcome of a roster read, for the sidebar's empty state.
   *
   * Called by the read's CALLERS rather than the read itself: it has a dozen
   * early returns (superseded, account switched, nothing to apply) and the
   * one thing they share is that the promise settles. `ready` therefore means
   * "the gateway answered", not "rows changed" — a user with genuinely no
   * chats is a resolved read that applied nothing, and must land on the "no
   * chats yet" hint rather than spinning.
   *
   * `ready` is terminal: once a read has landed, a later failure is a blip
   * against a sidebar that already has the right rows in it.
   */
  const settleRosterStatus = useCallback(
    (outcome: 'ready' | 'unavailable'): void => {
      setState((prev) =>
        prev.rosterStatus === 'ready'
          ? prev
          : { ...prev, rosterStatus: outcome },
      );
    },
    [],
  );

  /**
   * Load the sidebar from the GATEWAY: the chat list and the folder catalog.
   *
   * These were local state replicated by the terminal's own sync documents.
   * openclaw holds both — a session row per chat with its label, pinned flag
   * and group membership, and a gateway-owned group catalog — so the roster
   * is read rather than replicated, and a chat created on one device appears
   * on every other with nothing to merge.
   *
   * `sessions.subscribe` with list params does two jobs in one request: it
   * returns the first page AND turns on `sessions.changed` for this
   * connection, so later changes arrive as pushes.
   *
   * Two things are deliberately preserved from local state. Chats the
   * gateway has never heard of but that hold no messages are DRAFTS — a
   * "New Chat" the user has not sent yet — and dropping them would make the
   * new-chat button appear to do nothing. And the local store stays as the
   * paint-fast cache: it is written from the roster, never merged into it.
   *
   * A failed read leaves the cached sidebar alone. Replacing it with nothing
   * on a dropped socket would look like every chat had been deleted.
   */
  const hydrateRosterFromGateway = useCallback(
    async (
      opts: {
        /** Read through `sessions.subscribe`: once per connection. */
        readonly subscribe?: boolean;
        /** Push local titles/pins/folders first: once per account, per device. */
        readonly migrate?: boolean;
      } = {},
    ): Promise<void> => {
      const ws = wsRef.current;
      const account = accountIdRef.current;
      if (ws == null || account == null) return;
      // The sidebar is waiting on this: until a read settles, an empty
      // `sessions` is "we have not asked yet", not "you have no chats".
      // Never back off `ready` — a refresh must not flash a placeholder over
      // rows that are already on screen.
      setState((prev) =>
        prev.rosterStatus === 'ready'
          ? prev
          : { ...prev, rosterStatus: 'loading' },
      );
      const generation = connectionGenerationRef.current;
      // NEWEST READ WINS, within one connection.
      //
      // The connection generation is not enough, because the two reads that
      // collide are on the SAME connection: the connect-time read is slow
      // (it pages, and runs the one-time organisation migration per chat),
      // and a frame for an unknown session kicks a catch-up read while it is
      // still going. The connect read then snapshots the session list AFTER
      // its own awaits — so it sees the chat the catch-up read just
      // materialised, does not find it in its own older `sessions.list`
      // answer, and (with `rosterComplete` true) drops the row and forgets
      // the transcript the catch-up read had just written. Exactly the flap
      // the evidence rule was meant to end, from the other direction.
      //
      // Same shape as `sessionModelFetchSeqRef`: bump on entry, and a read
      // whose token has moved applies nothing.
      rosterReadSeqRef.current += 1;
      const readSeq = rosterReadSeqRef.current;
      // When this read's view of the world was taken. Anything created after
      // it is something the read cannot have seen — see the exemption below.
      const readStartedAt = Date.now();

      const rosterParams = {
        limit: SESSION_ROSTER_LIMIT,
        // Ask for the utility-model titles: without this a chat the user
        // never renamed has no name on the wire at all.
        includeDerivedTitles: true,
        archived: false,
        sortBy: 'updatedAt',
      };
      const [list, groups] = await Promise.all([
        // Exactly ONE `sessions.subscribe` per connection: it is what turns
        // on `sessions.changed`, and a second call would re-register the
        // subscription for no gain.
        // BOUNDED, because the request itself is not. An authenticated socket
        // whose answer never comes leaves the promise pending for the life of
        // the tab — nothing rejects it, since only a close settles what is
        // parked — and every settle for the sidebar's status lives in this
        // promise's `then`/`catch`. So the chat list shimmered forever, with
        // no failure and no chats, on the one fault that looks most like a
        // healthy connection.
        withTimeout(
          opts.subscribe === true
            ? subscribeSessions(ws, rosterParams)
            : listSessions(ws, rosterParams),
          ROSTER_READ_TIMEOUT_MS,
        ),
        // A gateway that does not know the group catalog is not a reason to
        // drop the chat list with it.
        // `null`, NOT `[]`: a failed read must stay distinguishable from a
        // user who genuinely has no folders. Collapsing the two emptied the
        // sidebar's folders on any blip and cached the emptiness.
        listSessionGroups(ws).catch(() => null),
      ]);
      if (accountIdRef.current !== account) return;
      if (connectionGenerationRef.current !== generation) return;
      if (rosterReadSeqRef.current !== readSeq) return;
      if (list == null) return;

      // PAGE, rather than taking the first page as the roster.
      //
      // `limit` applies to the RAW list, which carries every session the agent
      // has — cron runs, the heartbeat wake (every 30m on this fleet),
      // sub-agent children, the agent's own main session — and only some of
      // those are chats. So a store nowhere near `SESSION_ROSTER_LIMIT` chats
      // can still fill the page with rows `isTerminalChatRow` then throws
      // away, and the user's older chats fall off the end of a list they never
      // reach.
      //
      // Bounded three ways, because this runs on every connect: a page cap, a
      // row cap, and a requirement that each page actually advance. The last
      // two also make a gateway that ignores `offset` (returning page one
      // forever) terminate instead of spinning — the dedupe below means such a
      // page adds nothing, which stops the loop.
      let rows = list.sessions ?? [];
      // Whether this read reached the END of the agent's list. It is what
      // licenses dropping a local chat the roster did not mention (see the
      // projection below) — a truncated read is not evidence that a chat is
      // gone, and treating it as such is what deleted real conversations from
      // the sidebar.
      let rosterComplete = list.hasMore !== true;
      if (list.hasMore === true && list.nextOffset != null) {
        const seen = new Set(rows.map((r) => r.key));
        let offset: number | null = list.nextOffset;
        for (let page = 1; page < SESSION_ROSTER_MAX_PAGES; page += 1) {
          if (offset == null || rows.length >= SESSION_ROSTER_MAX_ROWS) break;
          const next: GatewaySessionsListResult | null = await listSessions(
            ws,
            { ...rosterParams, offset },
          ).catch(() => null);
          if (accountIdRef.current !== account) return;
          if (connectionGenerationRef.current !== generation) return;
          // A page that failed, or that the gateway answered with rows we
          // already had (one that ignores `offset`): stop, and say the read
          // is incomplete — we cannot tell what is past it.
          const fresh = (next?.sessions ?? []).filter(
            (r) => r.key != null && !seen.has(r.key),
          );
          if (fresh.length === 0) break;
          for (const r of fresh) seen.add(r.key);
          rows = [...rows, ...fresh];
          if (next?.hasMore !== true) {
            rosterComplete = true;
            break;
          }
          offset = next.nextOffset ?? null;
        }
      }

      // ARCHIVED chats, which the roster above deliberately does not carry.
      //
      // Absence from a complete read is what licenses forgetting a chat, and
      // `archived: false` makes an archived chat absent — so archiving one
      // (which this branch's own cleanup script does, on the promise that
      // NOTHING IS DELETED) destroyed the local transcript with its previews,
      // tool logs and widget bubbles. The two cases have to be
      // distinguishable, so they are read.
      //
      // Keys only: these rows are not projected into the sidebar, they just
      // stop a chat being mistaken for deleted. A failed read means we cannot
      // tell the cases apart, and then nothing is forgotten — `null` rather
      // than an empty set, the same distinction the folder catalog draws.
      const archivedKeys = await (async (): Promise<Set<string> | null> => {
        if (!rosterComplete) return null;
        const out = new Set<string>();
        let offset = 0;
        for (let page = 0; page < SESSION_ROSTER_MAX_PAGES; page += 1) {
          const res: GatewaySessionsListResult | null = await listSessions(ws, {
            limit: SESSION_ROSTER_LIMIT,
            archived: true,
            sortBy: 'updatedAt',
            ...(offset === 0 ? {} : { offset }),
          }).catch(() => null);
          if (res == null) return null;
          const before = out.size;
          for (const row of res.sessions ?? []) {
            const id = bareSessionId(row.key);
            if (id != null) out.add(id);
          }
          // Complete means the gateway SAID there is no more. Everything
          // else is an unknown, and this set is only ever used to keep a
          // transcript — so an unknown has to read as `null`, not as a
          // complete set that happens to be missing rows.
          //
          // These two used to `return out`, which said "complete" for a page
          // claiming `hasMore` with no offset to follow, and for a gateway
          // answering the same page twice. The live roster loop calls both of
          // those incomplete, and it is right: an archived chat past page one
          // would then be in neither list, and its transcript deleted — the
          // one thing this read exists to prevent.
          if (res.hasMore !== true) return out;
          if (res.nextOffset == null) return null;
          // A gateway ignoring `offset`, answering with rows already seen.
          if (out.size === before) return null;
          offset = res.nextOffset;
        }
        // Ran out of pages without reaching the end — same rule as above.
        return null;
      })();
      if (accountIdRef.current !== account) return;
      if (connectionGenerationRef.current !== generation) return;

      // One-way catch-up of local titles, pins and folders, using the rows we
      // just read to see what the gateway already has. It hands back the same
      // rows with its accepted patches folded in, so the projection below is
      // built from the rows as they now stand without a second round trip.
      let catalog = groups;
      // LATCHED HERE, not by the caller. `opts.migrate` only says this read
      // is allowed to migrate; whether it does is decided at the moment it
      // gets here, synchronously, and that closes both holes the caller's own
      // latch had. Latching before the call spent the one attempt on a read
      // that died in any of the awaits above — including the silent
      // `list == null` return — and the migration is per account per device,
      // so every local title, pin and folder then stayed local for the life
      // of the tab while the next roster read replaced the names with the
      // gateway's. And with no latch at all until the read returned, two
      // `authenticated` transitions during one read both saw "not migrated"
      // and ran the whole per-chat patch loop concurrently.
      const migrationKey = `${account}:gateway-organisation`;
      if (
        opts.migrate === true &&
        !migratedOrganisationRef.current.has(migrationKey)
      ) {
        migratedOrganisationRef.current.add(migrationKey);
        rows = await migrateOrganisationToGateway(rows, catalog);
        if (accountIdRef.current !== account) return;
        if (connectionGenerationRef.current !== generation) return;
        // Only re-read the catalog when the migration could have added to it.
        if (historyStore.loadCustomFolders(account).length > 0) {
          catalog = await listSessionGroups(ws).catch(() => catalog);
          if (accountIdRef.current !== account) return;
          if (connectionGenerationRef.current !== generation) return;
        }
      }

      // A gateway row with no name of ANY kind has never been sent to, and
      // rows like that exist: `sessions.patch` creates the session when it
      // does not exist, so seeding a new chat's model claims a row before the
      // first message. Presenting those is how a user ends up with a sidebar
      // of identical "New Chat" entries they never opened.
      //
      // Two exemptions, and both matter. The chat the user is LOOKING at is
      // exactly this shape until they type — hiding it would empty the row
      // under their cursor. And a chat with local messages is a chat,
      // whatever the gateway has yet to index for it.
      const openSessionId = activeSessionIdRef.current;
      // Chats this tab is holding a conversation for in memory: one it has
      // streamed in the background, or is buffering across a switch.
      //
      // The disk check below is no longer sufficient on its own. It used to
      // read a store written on every rendered message; the only writer now
      // is a completed transcript read, so a chat this device has watched
      // stream but never OPENED caches nothing — and an untitled gateway row
      // for it was hidden, which is the same narrowing the filter further
      // down was fixed to stop doing.
      const bufferedIds = backgroundSessionsRef.current;
      const visibleRows = rows.filter((row) => {
        if (!isUntitledRow(row)) return true;
        const id = bareSessionId(row.key);
        if (id == null) return false;
        if (id === openSessionId) return true;
        if ((bufferedIds.get(id)?.length ?? 0) > 0) return true;
        // Cached rows mean this device has read a real conversation for
        // the chat, whatever the gateway has yet to index a name for.
        //
        // `hasSessionTranscript` rather than loading it: this is an existence
        // test, and loading parsed up to a megabyte of JSON per chat on the
        // main thread to ask whether it was empty.
        return historyStore.hasSessionTranscript(account, id);
      });

      // Nothing below this line may write a REF a superseded read has no
      // business writing. `rosterGenerationRef` in particular: a stale
      // generation makes the next delete's compare-and-swap fail, which is
      // the guard that stops a delete destroying a newer conversation that
      // inherited the key. Checked here as well as before the snapshot,
      // because the writes come first.
      if (accountIdRef.current !== account) return;
      if (connectionGenerationRef.current !== generation) return;
      if (rosterReadSeqRef.current !== readSeq) return;

      // The generation behind each key, for the delete compare-and-swap. Read
      // from every terminal row, not just the visible ones: a row hidden as
      // untitled is still deletable from another surface.
      for (const row of rows) {
        if (!isTerminalChatRow(row)) continue;
        const id = bareSessionId(row.key);
        if (id != null && row.sessionId != null) {
          rosterGenerationRef.current.set(id, row.sessionId);
        }
      }

      const projected = projectGatewaySessions(visibleRows, {
        folder: defaultFolder,
        untitled: DEFAULT_SESSION_TITLE,
      });
      // The gateway's `derivedTitle` is the first message's text, so it only
      // appears once that message is INDEXED. In the gap — which the roster
      // refresh right after a first send lands in — the row is untitled and
      // the projection falls back to the placeholder, renaming a chat the
      // user just named by writing in it. The local title is the better
      // answer whenever the gateway has none.
      const localTitles = new Map(
        historyStore
          .loadSessions(account)
          .filter((s) => s.title.trim() !== DEFAULT_SESSION_TITLE)
          .map((s) => [s.sessionId, s.title] as const),
      );
      // What the user themselves set, on this device, and has not been
      // overtaken by a gateway that echoes it back yet.
      //
      // The fallback above only fires when the gateway has NO title, which
      // leaves the race a rename actually loses: rename, and before the
      // `sessions.patch` lands a catch-up read arrives carrying the
      // gateway's `derivedTitle` — a real title, so the fallback stands
      // aside, and the user's name is overwritten with the first line of
      // their own message. Nothing reads it back afterwards, so it is gone.
      // The push path closes this with `mayRetitle`; the read path did not.
      //
      // Same window loses a pin and a folder, and those have no fallback of
      // any kind, so they are carried from what is on screen for as long as
      // the rename lock holds.
      const locallyRenamed = userTitledSessionsRef.current;
      const onScreen = new Map(
        stateRef.current.sessions.map((s) => [s.sessionId, s] as const),
      );
      const rosterSessions = projected.map((s) => {
        const mine = locallyRenamed.has(s.sessionId)
          ? onScreen.get(s.sessionId)
          : undefined;
        const withOrganisation =
          mine == null
            ? s
            : {
                ...s,
                title: mine.title,
                pinned: mine.pinned,
                ...(mine.customFolderId == null
                  ? {}
                  : { customFolderId: mine.customFolderId }),
                folder: mine.folder,
              };
        if (withOrganisation.title !== DEFAULT_SESSION_TITLE) {
          return withOrganisation;
        }
        const local = localTitles.get(s.sessionId);
        return local == null
          ? withOrganisation
          : { ...withOrganisation, title: local };
      });
      // `catalog`, not `groups`: the migration above may have just CREATED
      // groups, and re-read the catalog for exactly that reason. Projecting
      // the original read showed — and cached — the pre-migration catalog, so
      // the folders the same pass had just pushed were missing from the
      // sidebar until the next reconnect.
      // Only a SUCCESSFUL catalog read speaks for the folder list. A null
      // catalog means the read failed, and the projection below leaves the
      // folders on screen exactly as they are.
      //
      // Pins are carried over, because the gateway's catalog has no notion of
      // one: pinning a folder is a local preference, `sessions.groups.put`
      // has nowhere to put it, and replacing the list wholesale therefore
      // dropped it — then the persistence effect wrote the unpinned list to
      // disk, so folder pinning survived nothing and was effectively
      // write-only. Matched on the id, which for a folder IS its name on both
      // sides.
      const localFolderPins = new Set(
        stateRef.current.customFolders
          .filter((f) => f.pinned === true)
          .map((f) => f.id),
      );
      const rosterFolders =
        catalog == null
          ? null
          : foldersFromGatewayGroups(catalog).map((f) =>
              localFolderPins.has(f.id) ? { ...f, pinned: true } : f,
            );
      // What the gateway is now known to hold, for the live category writes.
      if (catalog != null) {
        gatewayGroupNamesRef.current = new Set(catalog.map((g) => g.name));
      }
      const knownToGateway = new Set(rosterSessions.map((s) => s.sessionId));

      // Local rows the roster did not account for — kept or dropped on the
      // same rule the transcript merge uses: is this read EVIDENCE?
      //
      // Dropping them is load-bearing. It is how a chat deleted on another
      // device leaves this one, so it cannot simply stop. But it was applied
      // to a read that had no authority: the roster took one page, and
      // `limit` bounds the RAW list — cron, heartbeat and sub-agent rows
      // included — so a chat could be missing merely for being past the end
      // of it. The same pass then wrote it out of the launch cache, the next
      // launch's orphan recovery rebuilt it from its transcript as
      // "Recovered chat", and the next read dropped it again: a real
      // conversation flapping in and out of the sidebar under a name it
      // never had, and if it was the OPEN chat, `activeStillExists` below
      // switched the user silently into a different one.
      //
      // So a read only speaks for the chats it can see all of, and two
      // things are never spoken for:
      //
      //  - a chat holding a message that never reached the agent, which the
      //    gateway therefore has no row for and never did (`unsent`, the
      //    same mark the merge honours), and
      //  - anything at all, when the read did not reach the end of the list.
      //
      // There used to be a fourth: a chat with no local transcript. It was
      // meant for drafts, and `isDraft`/`isTemporary` above already cover
      // those — drafts are never persisted at all. What it actually matched
      // was every roster chat this device had listed and never opened, and
      // keeping those meant a cross-device delete NEVER landed for them:
      // absent from a complete live read and from a successful archived read,
      // such a chat survived every read forever, was re-cached each time, and
      // opened empty when clicked. Removed rather than narrowed, because the
      // two flags above are the whole of what it was for.
      //
      // Decided HERE rather than inside the `setState` updater, against the
      // last rendered list. Not a style choice: React defers an updater to
      // the render pass, so the three writes below — the launch cache, the
      // dropped chats' transcripts, the folder cache — read whatever the
      // updater had NOT yet written, and gating them on it meant they never
      // ran at all. One decision, applied to state and to disk.
      // Re-checked HERE, immediately before the snapshot, and this is the
      // check the race actually needs: everything above — the paging, the
      // migration, the catalog re-read — awaits, and a newer read can have
      // landed in any of those gaps. Reading `before` after that would be
      // judging a fresh session list against a stale answer.
      if (accountIdRef.current !== account) return;
      if (connectionGenerationRef.current !== generation) return;
      if (rosterReadSeqRef.current !== readSeq) return;

      // A message that never reached the agent lives in memory only, so
      // this asks memory: the chat on screen, or one this tab has open in the
      // background. Nowhere else can hold one.
      const hasUnsent = (id: string): boolean => {
        const live =
          id === activeSessionIdRef.current
            ? stateRef.current.messages
            : backgroundSessionsRef.current.get(id);
        return live != null && live.some((m) => m.unsent === true);
      };
      const before = stateRef.current.sessions;
      const unaccounted = before.filter(
        (s) =>
          !knownToGateway.has(s.sessionId) &&
          (s.isTemporary === true ||
            s.isDraft === true ||
            !rosterComplete ||
            // Touched after this read's answer was captured, so the answer
            // says nothing about it. THE most damaging omission in this
            // filter, and the reason `before` and `knownToGateway` disagree
            // about when "now" is: `knownToGateway` comes off the wire before
            // the awaits, `before` is sampled after them, and the connect
            // read is long — it pages, reads the archived list, and runs the
            // per-chat migration. A chat that became real in that window has
            // an acked bubble (so not `unsent`) and a written transcript (so
            // not empty) and is not a draft any more, so every other
            // exemption here passed it over: it was dropped, its transcript
            // deleted, its run orphaned, and the user switched out of the
            // chat they had just started. That window is the first message
            // after a page load.
            //
            // `updatedAt`, not `createdAt`, and that distinction is the whole
            // fix. `createdAt` is stamped by `createSession` and never again;
            // the first send drops `isDraft` and restamps `updatedAt`. So a
            // draft opened BEFORE the read and first sent DURING it has an
            // old `createdAt` and was still forgotten — the same drop, from a
            // chat that had been sitting in the composer. What the filter
            // needs to know is when the chat became REAL, and `updatedAt` is
            // the only stamp that says so.
            //
            // `createdAt` is kept alongside for the chat minted mid-read
            // whose send has not landed yet: there `updatedAt` is its
            // creation stamp anyway, so this is belt-and-braces rather than
            // a second signal.
            s.updatedAt >= readStartedAt ||
            s.createdAt >= readStartedAt ||
            hasUnsent(s.sessionId)),
      );
      const sessions = [...unaccounted, ...rosterSessions];
      // Nothing to show at all: keep whatever is on screen rather than
      // presenting an empty sidebar to a user who has chats — and write
      // nothing to disk either, which is the whole reason this bail and the
      // writes below have to be reading the same decision.
      if (sessions.length === 0) return;
      const kept = new Set(sessions.map((s) => s.sessionId));
      // Chats to FORGET — a stricter question than which rows to show, and
      // the two part company over archiving.
      //
      // The sidebar reads `archived: false`, so an archived chat is missing
      // from a complete read exactly the way a deleted one is. Leaving the
      // row is not the answer (archiving is meant to clear the sidebar, and
      // it does on every device that never cached the chat), but neither is
      // deleting the transcript: this branch's own cleanup script archives
      // chats on the stated promise that NOTHING IS DELETED, and unarchiving
      // one has to bring a real conversation back. So the row goes and the
      // transcript stays.
      //
      // Only a read that ESTABLISHED the chat is in neither list forgets it.
      // With the archived read unavailable the two cases are
      // indistinguishable, and then nothing is forgotten.
      //
      // Taken from the same snapshot as the rows, so a chat created between
      // the snapshot and the commit is not in it and cannot be forgotten by
      // it.
      const droppedIds =
        archivedKeys == null
          ? []
          : before
              .filter(
                (s) => !kept.has(s.sessionId) && !archivedKeys.has(s.sessionId),
              )
              .map((s) => s.sessionId);
      knownSessionIdsRef.current = kept;

      // Where the user would land if their open chat turned out to be gone,
      // resolved the same way `switchSession` resolves any chat: buffer, then
      // store.
      //
      // Prepared HERE, outside the updater, and this is not tidiness. React
      // may run an updater twice and discard the first result, so an updater
      // that reads and mutates refs sees its own first pass on the second —
      // the buffer it had just deleted comes back `undefined`, and the user
      // lands on the persisted copy of a chat that is still streaming.
      // `PreferencesProvider` says so in this codebase and
      // `silent-exchange-strictmode.test.tsx` exists because of it.
      //
      // It also gets a synchronous `localStorage` read and a full-transcript
      // parse out of a function React is entitled to call during render.
      const expectedLanding = sessions[0];
      const preparedBuffer =
        expectedLanding == null
          ? undefined
          : backgroundSessionsRef.current.get(expectedLanding.sessionId);
      const preparedMessages =
        expectedLanding == null
          ? []
          : (preparedBuffer ??
            paintFromCache(account, expectedLanding.sessionId));
      const preparedProcessing =
        expectedLanding == null
          ? null
          : (backgroundProcessingRef.current.get(expectedLanding.sessionId) ??
            null);
      /** Set by the updater so the work after it knows what happened. */
      let landedOn: string | null = null;
      let landedPrepared = false;

      setState((prev) => {
        // A chat minted between the snapshot and this commit — the new-chat
        // button, an instant before the read landed. It is not in `sessions`
        // and was never a candidate for dropping, so carry it over rather
        // than have the roster read make it vanish under the user's cursor.
        const late = prev.sessions.filter(
          (s) =>
            !kept.has(s.sessionId) &&
            !before.some((b) => b.sessionId === s.sessionId),
        );
        const rows = late.length === 0 ? sessions : [...late, ...sessions];
        const activeStillExists =
          prev.activeSessionId != null &&
          rows.some((s) => s.sessionId === prev.activeSessionId);
        const landing = rows[0];
        // Whether the chat prepared for above is the one actually being
        // landed on. It usually is — `late` is empty on every read but the
        // one that races the new-chat button.
        //
        // And `rows[0]` is the most recently active chat only when `late` and
        // `unaccounted` are both empty: those are PREPENDED, ahead of the
        // `updatedAt`-desc ordering the projection applies, so a draft or a
        // chat the read could not speak for sits at the head regardless of
        // when it was last touched. Landing on it is still reasonable — it is
        // a chat this device is holding for a reason — but "most recent" is
        // not why.
        const prepared =
          landing != null &&
          expectedLanding != null &&
          landing.sessionId === expectedLanding.sessionId;
        landedOn = activeStillExists ? null : (landing?.sessionId ?? null);
        landedPrepared = prepared;
        // Nothing cached and nothing buffered for the chat being opened is a
        // WAIT, not an empty conversation — the same rule `switchSession`
        // follows. The read for it is issued after this commit.
        const landingMessages =
          !activeStillExists && prepared ? preparedMessages : [];
        const landingProcessing =
          !activeStillExists && prepared ? preparedProcessing : null;

        // Per-chat state keyed by an id that is about to mean nothing. A
        // banner or a picker entry for a chat that is gone annotates whatever
        // chat lands next.
        const goneIds = new Set(
          before
            .filter((s) => !kept.has(s.sessionId))
            .map((s) => s.sessionId)
            .filter((id) => !rows.some((r) => r.sessionId === id)),
        );
        const sessionErrors = Object.fromEntries(
          Object.entries(prev.sessionErrors).filter(([id]) => !goneIds.has(id)),
        );
        const sessionModels = Object.fromEntries(
          Object.entries(prev.sessionModels).filter(([id]) => !goneIds.has(id)),
        );
        return {
          ...prev,
          sessions: rows,
          sessionErrors,
          sessionModels,
          ...(rosterFolders == null ? {} : { customFolders: rosterFolders }),
          ...(activeStillExists
            ? {}
            : {
                // The open chat was deleted elsewhere. Fall back to the most
                // recent rather than leaving a transcript with no row.
                activeSessionId: landing?.sessionId ?? null,
                // The BUFFER first, then the store — the same order
                // `switchSession` uses. Reading the store directly landed the
                // user on the stale persisted copy of a chat that was still
                // streaming in the background, with the live tail stranded in
                // `backgroundSessionsRef` and no indicator to explain it.
                messages: landingMessages,
                // The DROPPED chat's indicator must go — its run is orphaned
                // by `forgetSessionLocally`, so its terminator is discarded
                // and nothing else would ever clear this: the chat that
                // landed showed "Thinking" and a live Stop button for the
                // rest of the session. But the chat we land ON gets its own
                // indicator back if it has work in flight, which is what
                // `switchSession` does.
                processing: landingProcessing,
              }),
        };
      });

      // Everything the updater is not allowed to do, now that it has run
      // once and committed.
      if (landedOn != null) {
        knownSessionIdsRef.current = new Set(
          stateRef.current.sessions.map((s) => s.sessionId),
        );
        // The buffer is adopted only when the chat is actually opened, and
        // only then does it stop being needed — `switchSession` clears one it
        // has adopted for the same reason. Evicting on every read threw away
        // the live tail and the indicator of whichever chat happened to be at
        // the head, on every connect and every catch-up read.
        if (landedPrepared) {
          backgroundSessionsRef.current.delete(landedOn);
          backgroundProcessingRef.current.delete(landedOn);
        }
        // And read it from the agent, which is the half that was missing.
        // Landing here paints from the cache and nothing else, so landing on
        // a chat this device has never read showed the "New Chat" greeting
        // over a real conversation — the exact false claim `switchSession`
        // was fixed for, on the path a cross-device delete takes the user
        // down.
        const landedMessages = stateRef.current.messages;
        if (landedMessages.length === 0) {
          const readStarted = hydrateSessionFromGateway(landedOn);
          const isKnownChat = stateRef.current.sessions.some(
            (s) =>
              s.sessionId === landedOn &&
              s.isDraft !== true &&
              s.isTemporary !== true,
          );
          if (readStarted && isKnownChat) {
            setState((cur) =>
              cur.activeSessionId === landedOn
                ? { ...cur, loadingTranscriptFor: landedOn }
                : cur,
            );
          }
        }
      }

      // Forget the chats this read dropped.
      //
      // This used to be load-bearing for correctness: a blob left on disk
      // with no index row was rebuilt on the next launch as "Recovered
      // chat", the next read dropped it again, and a real conversation
      // flapped in and out of the sidebar under a name it never had. Orphan
      // recovery is gone, so a stale blob is now just cache nothing reads.
      // Still deleted — quota, and not leaving a deleted chat's text on disk
      // — but a miss here is hygiene, not the correctness bug it was while
      // recovery existed.
      //
      // The ONLY disk write left here. The sessions index and the folder
      // catalog used to be written from this function too, and both were
      // gated on the wrong thing — the sessions write on a holder the
      // updater fills in later (so it never ran), the folder write on the
      // read having succeeded rather than on the state having changed (so a
      // bail cached folders the sidebar was not showing). Neither needed to
      // exist: the persistence effects below write both from state, which is
      // the only copy that can agree with what the user sees. Nothing writes
      // the transcript blobs, though, which is why this one stays.
      for (const id of droppedIds) {
        forgetSessionLocally(id);
      }
    },
    [
      defaultFolder,
      historyStore,
      paintFromCache,
      migrateOrganisationToGateway,
      forgetSessionLocally,
      hydrateSessionFromGateway,
    ],
  );

  /**
   * Converge SETTINGS with the gateway's `/sync/v1` snapshot. Returns null
   * when the snapshot fetch failed or there's no usable sync client (caller
   * decides whether to retry); a non-null result means the merge ran.
   *
   * `preferencesOnly`, and that is the whole point: the sessions, folders and
   * transcripts this same snapshot carries are neither applied nor uploaded
   * any more. The gateway is the source of truth for all three
   * (`hydrateRosterFromGateway`, `hydrateSessionFromGateway`), so merging the
   * snapshot's copies in would leave the sidebar decided by whichever read
   * resolved last, and re-uploading them would keep feeding documents nothing
   * reads — the 8 MiB session document whose refusal is what stranded a chat
   * on one device in the first place. The `/sync/v1` session documents are
   * now dead weight; the whole service can go in a follow-up once
   * preferences have another home.
   */
  const applyRemoteHydration = useCallback(async () => {
    const account = accountIdRef.current;
    const sync = syncRef.current;
    if (account == null || sync == null) return null;
    const result = await hydrateFromRemote(
      historyStore,
      account,
      sync,
      prefsStore,
      prefsDocRef.current,
      { preferencesOnly: true },
    );
    if (result == null) return null;
    if (accountIdRef.current !== account) return result;
    // Settings changed on another device land here. Already persisted by the
    // sync layer; this moves them into live state.
    adoptPreferencesDoc(result.preferences);
    // No transcript pull here any more: the open chat is reconciled against
    // the gateway's own transcript on connect and on open, which is a
    // stronger guarantee than the snapshot ever gave (it only carried what
    // some browser tab had uploaded).
    return result;
  }, [historyStore, prefsStore, adoptPreferencesDoc]);

  /**
   * Accounts whose local organisation has been pushed to the gateway on this
   * device. Per account, not per connection: the migration is a one-way
   * catch-up, and re-running it on every reconnect would keep re-patching
   * rows a second device has since renamed.
   */
  const migratedOrganisationRef = useRef<Set<string>>(new Set());

  // Single-flight + throttle guards for `requestForeignSessionHydration`.
  const foreignHydrateInFlightRef = useRef(false);
  /**
   * Session keys a dropped frame named that this device has not resolved yet.
   * The throttle bounds how often the roster is READ; losing a key to it
   * would leave that chat invisible until the next reconnect.
   */
  const foreignHydratePendingRef = useRef<Set<string>>(new Set());
  /**
   * Consecutive failed catch-up reads, reset by the first success. Bounds the
   * requeue/re-arm pair — see `FOREIGN_HYDRATE_MAX_ATTEMPTS`.
   */
  const foreignHydrateFailuresRef = useRef(0);
  /**
   * The connection generation the failure count belongs to. A reconnect is a
   * new gateway as far as this is concerned, so the cap does not outlive it.
   */
  const foreignHydrateFailedGenerationRef = useRef(-1);
  const lastForeignHydrateAtRef = useRef(0);

  /**
   * A frame arrived for a session this device doesn't know yet (created on
   * another device mid-connection — see `isForeignSessionFrame`). Read the
   * ROSTER so the session materializes in Recents, then read its transcript,
   * without waiting for the next reconnect. The frame itself is dropped by
   * the caller.
   *
   * It used to pull the `/sync/v1` snapshot, which was where a session
   * created elsewhere came from. That path is now `preferencesOnly` — the
   * gateway owns the roster and the transcripts — so this had quietly become
   * a no-op that read the settings document and materialized nothing: a chat
   * started on the phone stayed invisible here until the next reconnect,
   * which is the wait this function exists to avoid.
   */
  const requestForeignSessionHydration = useCallback(
    (sessionKey?: string) => {
      const generation = connectionGenerationRef.current;
      // QUEUED, not dropped. The throttle and the single-flight are about how
      // often the ROSTER is read; the key is a separate thing that must not
      // be lost with the call. A frame for a second unknown session inside
      // the window used to return here, so that chat's transcript was never
      // read and it stayed invisible until the next reconnect — the wait this
      // function exists to avoid.
      const pendingKey = bareSessionId(sessionKey ?? '');
      if (pendingKey != null) {
        foreignHydratePendingRef.current.add(pendingKey);
      }
      if (foreignHydrateInFlightRef.current) return;
      // The socket is gone: nothing to read, and a re-arm would keep firing
      // against it. The next connect reads the whole roster anyway.
      if (wsRef.current == null) {
        foreignHydrateFailuresRef.current = 0;
        return;
      }
      // A CONNECT read owns the one-time organisation migration, and a
      // catch-up read supersedes it (`rosterReadSeqRef`) — so kicking one now
      // would cancel the migration and let the projection overwrite the local
      // titles and pins it was about to push. The keys are queued above and
      // the connect read's own completion drains them.
      if (rosterMigrateInFlightRef.current > 0) return;
      if (
        Date.now() - lastForeignHydrateAtRef.current <
        FOREIGN_HYDRATE_THROTTLE_MS
      ) {
        return;
      }
      // Bounded: the requeue-on-failure plus the re-arm below are otherwise
      // an infinite loop against a gateway whose `sessions.list` errors.
      // Cap reached — but only for THIS connection. Reset on a new
      // connection generation, because the cap is a statement about a gateway
      // that is failing right now, not about the tab. Without that, ~75s of
      // errors killed catch-up hydration for the life of the page even after
      // the gateway recovered: the counter's only other reset was a
      // successful catch-up read, which this return made unreachable.
      if (foreignHydrateFailedGenerationRef.current !== generation) {
        foreignHydrateFailedGenerationRef.current = generation;
        foreignHydrateFailuresRef.current = 0;
      }
      if (foreignHydrateFailuresRef.current >= FOREIGN_HYDRATE_MAX_ATTEMPTS) {
        foreignHydratePendingRef.current.clear();
        return;
      }
      foreignHydrateInFlightRef.current = true;
      // Drained by THIS run, and cleared before the read so a key that
      // arrives while it is in flight queues for the next one rather than
      // being dropped as already handled.
      const draining = [...foreignHydratePendingRef.current];
      foreignHydratePendingRef.current.clear();
      // No `subscribe` and no `migrate`: this is a mid-connection catch-up,
      // and both of those are once-per-connection / once-per-device.
      hydrateRosterFromGateway()
        .then(() => {
          settleRosterStatus('ready');
          foreignHydrateFailuresRef.current = 0;
          // The roster has just told us which of those keys are ours. For
          // each, its transcript is the other half of materializing it — the
          // dropped frames carried messages this device never saw.
          for (const id of draining) {
            if (knownSessionIdsRef.current.has(id)) {
              hydrateSessionFromGateway(id);
            }
          }
        })
        .catch((err: unknown) => {
          settleRosterStatus('unavailable');
          foreignHydrateFailuresRef.current += 1;
          // Put them back: the read that was going to resolve them failed.
          // Only while there are attempts left — otherwise the queue and the
          // re-arm feed each other forever.
          if (
            foreignHydrateFailuresRef.current < FOREIGN_HYDRATE_MAX_ATTEMPTS
          ) {
            for (const id of draining) {
              foreignHydratePendingRef.current.add(id);
            }
          }
          reportError(err);
        })
        .finally(() => {
          foreignHydrateInFlightRef.current = false;
          lastForeignHydrateAtRef.current = Date.now();
          // Anything that arrived during the read, or came back from a
          // failure, gets the next allowed run — backed off by the attempt
          // count, so a persistently failing gateway is asked less often
          // rather than every 5s forever.
          if (
            foreignHydratePendingRef.current.size > 0 &&
            foreignHydrateFailuresRef.current < FOREIGN_HYDRATE_MAX_ATTEMPTS
          ) {
            const delay =
              FOREIGN_HYDRATE_THROTTLE_MS *
              2 ** foreignHydrateFailuresRef.current;
            setTimeout(() => {
              requestForeignSessionHydrationRef.current?.();
            }, delay);
          }
        });
    },
    [
      hydrateRosterFromGateway,
      hydrateSessionFromGateway,
      reportError,
      settleRosterStatus,
    ],
  );
  /**
   * Self-reference for the re-arm above. A `useCallback` cannot name itself
   * in its own body without becoming its own dependency, and the re-arm is
   * exactly the case that needs it — the drain has to keep going until the
   * queue is empty.
   */
  const requestForeignSessionHydrationRef = useRef<
    ((sessionKey?: string) => void) | null
  >(null);
  requestForeignSessionHydrationRef.current = requestForeignSessionHydration;

  /**
   * Look up the session that owns `payload.runId`, lazily registering it
   * the first time we see a frame for a new run.
   */
  const resolveOwner = useCallback(
    (payload: { sessionKey?: string; runId?: string }): string | null => {
      const ourSessionKey = matchKnownSessionKey(
        payload.sessionKey,
        knownSessionIdsRef.current,
      );
      if (ourSessionKey != null && payload.runId != null) {
        runOwnershipRef.current.set(payload.runId, ourSessionKey);
      } else if (
        // Last-send fallback: ONLY for frames that carry no sessionKey at all
        // (early frames of a locally initiated run). A key that is present but
        // unknown means the run belongs to a session from ANOTHER device —
        // claiming it for the last-send session is exactly the cross-device
        // leak (mobile chat landing in an unrelated desktop thread).
        (payload.sessionKey == null || payload.sessionKey.length === 0) &&
        payload.runId != null &&
        !runOwnershipRef.current.has(payload.runId) &&
        lastSendSessionRef.current != null &&
        knownSessionIdsRef.current.has(lastSendSessionRef.current)
      ) {
        runOwnershipRef.current.set(payload.runId, lastSendSessionRef.current);
      }
      return (
        ourSessionKey ??
        (payload.runId != null
          ? (runOwnershipRef.current.get(payload.runId) ?? null)
          : null)
      );
    },
    [],
  );

  /**
   * The run a frame WITHOUT a run id belongs to, on a gateway that names none.
   *
   * Returns nothing where the gateway does name runs: there a bare frame is a
   * straggler, not the run's own, and treating it as the run's settled a live
   * run from another run's late terminator.
   *
   * Shared by the failure stamp and the outcome funnel so they cannot disagree
   * about which run a bare frame is. They did: only the funnel had this, so a
   * run-less gateway's failure never reached a record and the funnel then
   * reported that same run `ok` — a failure recorded as a success, which is
   * the one outcome worse than no number at all.
   */
  const runlessRunIdForFrame = useCallback(
    (payload: { sessionKey?: string; runId?: string }): string | undefined => {
      if (gatewayNamesRunIdsRef.current) return undefined;
      // With neither id nor a known session key there is nothing to attribute
      // a frame to but the chat in front of the user — the same fallback
      // `endedRunKey` uses for its run-less form.
      return outstandingRunForSession(
        resolveOwner(payload) ?? activeSessionIdRef.current,
      );
    },
    [resolveOwner, outstandingRunForSession],
  );

  /**
   * Attribute a run failure to the chat that owns it. Kept per session and
   * WITHOUT a timer: a run that fails while the user reads another chat — or
   * while they simply look away — must still be discoverable afterwards. It
   * retires when the user dismisses it or retries that chat.
   *
   * Foreign frames (another device's session we have not synced) are dropped,
   * matching `routeMessageFrame`: there is no local chat to attribute them to,
   * and that device surfaces its own failure.
   */
  const reportSessionError = useCallback(
    (
      payload: { sessionKey?: string; runId?: string },
      message: string,
      code?: string,
    ): void => {
      // Before the guards below: they decide who to SHOW the failure to, but
      // the run failed either way, and a failure recorded as a success is
      // worse than no stability number at all.
      const failedRunId = payload.runId ?? runlessRunIdForFrame(payload);
      if (failedRunId != null) {
        const record = runRecordsRef.current.get(failedRunId);
        const category = classify({ message, code });
        if (record != null) record.category = category;
        // No record yet: a streams-first gateway failing a run before its
        // ack. Kept for the ack to attach, so the run is not counted as `ok`.
        // Only reachable for an id the gateway named — the run-less form
        // resolves an existing record or nothing.
        else if (payload.runId != null) {
          noteEarlyEnd(payload.runId, { category });
        }
      }
      // The frame named a run, so this gateway names runs. Recorded here as
      // well as at the terminator because a failure can be the FIRST frame
      // that carries an id, and the flag gates the run-less path above.
      if (payload.runId != null) gatewayNamesRunIdsRef.current = true;

      // A deleted chat's failure is not the fresh chat's banner.
      if (isOrphanedRun(payload)) return;
      if (
        isForeignSessionFrame(
          payload,
          knownSessionIdsRef.current,
          runOwnershipRef.current,
        )
      ) {
        return;
      }
      const ownerId = resolveOwner(payload);
      const sessionId = ownerId ?? activeSessionIdRef.current;
      // After the guards, which drop failures the user is never shown: this
      // counts what the user hit, not what the wire carried.
      reportChatError({
        source: 'run',
        message,
        code,
        sessionId,
        runId: payload.runId,
      });
      if (sessionId == null) {
        // Nothing to attach it to (no session at all yet) — the transient
        // surface is the only one available.
        showTransientError(message);
        return;
      }
      setState((prev) =>
        prev.sessionErrors[sessionId] === message
          ? prev
          : {
              ...prev,
              sessionErrors: { ...prev.sessionErrors, [sessionId]: message },
            },
      );
    },
    [
      resolveOwner,
      showTransientError,
      isOrphanedRun,
      reportChatError,
      classify,
      noteEarlyEnd,
      runlessRunIdForFrame,
    ],
  );

  /**
   * Resolve which session a frame belongs to and apply a message reducer
   * either to live state (active session) or to a background buffer.
   */
  /**
   * The key a run is remembered under. An identified run is its own id. The
   * run-less stream has none, so it is keyed PER SESSION — a single global
   * flag could only ever be wrong in one direction or the other: shared, a
   * background session's terminator silenced the open chat; gated on the
   * active chat, a background session's own late frames opened a live block
   * in its buffer, which then never flushed. An unattributable frame belongs
   * to the chat on screen, which is where `resolveOwner` returning null lands.
   */
  const endedRunKey = useCallback(
    (runId: string | undefined, payload: RunFramePayload): string => {
      if (runId != null) return runId;
      const owner = resolveOwner(payload);
      return `${RUNLESS_RUN_KEY}:${owner ?? activeSessionIdRef.current ?? ''}`;
    },
    [resolveOwner],
  );

  /**
   * Record a run as finished and release everything keyed on it.
   *
   * Shared by the gateway's own end signals and by Stop, so both go through
   * the same bounded `rememberEndedRun`. `releasesOwnership` is the gateway's
   * distinction, kept here: ownership outlives every terminator but its own,
   * because a straggler still carries the runId and an unowned frame lands on
   * whichever chat is open.
   *
   * The pending-key removal is unconditional, though. That set means "runs
   * this chat has in flight", which Stop's sweep reads directly — a finished
   * run is not in flight whether or not its ownership entry is kept.
   */
  const retireRun = useCallback(
    (
      runId: string | undefined,
      payload: RunFramePayload = {},
      releasesOwnership = false,
    ) => {
      if (runId != null) {
        if (releasesOwnership) {
          runOwnershipRef.current.delete(runId);
          suppressedModelEchoRunIdsRef.current.delete(runId);
        }
        pendingRunKeysRef.current.delete(runId);
      }
      // The single funnel every terminator passes through, so no run is
      // missed or counted twice.
      //
      // A gateway that omits run ids is first-class here — `endedRunKey` has
      // a run-less form for exactly that — but the outcome path keyed only on
      // the id, so such a gateway produced ZERO outcomes while every failure
      // still shipped. Resolve the terminator to its session instead and
      // settle that session's outstanding run.
      //
      // `runlessRunIdForFrame` is empty on a gateway that names runs, because
      // a bare terminator means two different things: on a run-less gateway it
      // is the only terminator there is, while on an id-bearing one it is a
      // straggler — and settling the session's outstanding run for one marked
      // a run that was still streaming `ok`.
      reportRunOutcome(runId ?? runlessRunIdForFrame(payload));
      rememberEndedRun(endedRunKey(runId, payload));

      // Read again whatever this run got in the way of. See
      // `deferredReadsRef`: a page discarded because a run started is the
      // difference between a chat showing its history and showing the one
      // turn just sent.
      //
      // A tick later, deliberately. This terminator is what clears the
      // chat's `processing`, and a read issued before that state lands would
      // decline for precisely the reason it is being retried. A read that
      // still declines stays queued for the next run to end; a reconnect
      // re-reads everything anyway, so nothing waits forever.
      if (deferredReadsRef.current.size > 0) {
        const pending = [...deferredReadsRef.current];
        deferredReadsRef.current.clear();
        setTimeout(() => {
          for (const id of pending) {
            if (!hydrateSessionFromGateway(id)) {
              deferredReadsRef.current.add(id);
            }
          }
        }, 0);
      }
    },
    [
      rememberEndedRun,
      endedRunKey,
      reportRunOutcome,
      runlessRunIdForFrame,
      hydrateSessionFromGateway,
    ],
  );

  const routeMessageFrame = useCallback(
    (
      payload: { sessionKey?: string; runId?: string },
      reducer: (messages: readonly ChatMessage[]) => readonly ChatMessage[],
      activeExtra?: { processing?: ProcessingPatch },
    ): void => {
      // Orphan-ness first: a frame for a chat we just deleted must not send
      // us pulling a snapshot for it.
      if (isOrphanedRun(payload)) return;
      if (
        isForeignSessionFrame(
          payload,
          knownSessionIdsRef.current,
          runOwnershipRef.current,
        )
      ) {
        // Another device's session that hasn't reached this one yet: drop the
        // frame (its transcript arrives from the gateway read, not the live
        // stream) and read the roster in the background so the session shows
        // up without waiting for the next reconnect.
        requestForeignSessionHydration(payload.sessionKey);
        return;
      }
      const targetId = resolveOwner(payload);
      const activeId = activeSessionIdRef.current;

      if (targetId == null || targetId === activeId) {
        setState((prev) => {
          const next = reducer(prev.messages);
          if (
            next === prev.messages &&
            (activeExtra == null || !('processing' in activeExtra))
          ) {
            return prev;
          }
          if (activeExtra == null || !('processing' in activeExtra)) {
            return { ...prev, messages: next };
          }
          const processing = applyProcessingPatch(
            activeExtra.processing ?? null,
            prev.processing,
          );
          if (next === prev.messages && processing === prev.processing) {
            return prev;
          }
          return { ...prev, messages: next, processing };
        });
        return;
      }

      const account = accountIdRef.current;
      if (account == null) return;

      const existing =
        backgroundSessionsRef.current.get(targetId) ??
        paintFromCache(account, targetId);
      const next = reducer(existing);
      // Apply the processing signal even when the reducer is a message no-op —
      // an error frame can arrive before any assistant delta (nothing to
      // close), and the stashed "Thinking" indicator must still be dropped so
      // a switch-back doesn't show a stale verb spinner.
      if (activeExtra != null && 'processing' in activeExtra) {
        // Patched against the STASHED indicator, not the visible one: this
        // frame belongs to a session the user has switched away from, so that
        // stash is the run's current state. A patch that accumulates (counting
        // reasoning characters) or decides (ignoring an end-of-run frame for a
        // different run) has to see it.
        //
        // The LABEL does not follow, though: state frames are applied only for
        // the active session, so a backgrounded run's stashed label stays on
        // whatever it read when the user left ("Thinking") while its clock and
        // character count keep moving. Switching back mid-run therefore shows
        // a live row with a possibly stale verb — better than the frozen count
        // this patch replaced, and routing state frames too is a wider change
        // than the count needed.
        const processing = applyProcessingPatch(
          activeExtra.processing ?? null,
          backgroundProcessingRef.current.get(targetId) ?? null,
        );
        if (processing == null) {
          backgroundProcessingRef.current.delete(targetId);
        } else {
          backgroundProcessingRef.current.set(targetId, processing);
        }
      }
      if (next === existing) return;

      backgroundSessionsRef.current.set(targetId, next);
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === targetId ? { ...s, updatedAt: Date.now() } : s,
        ),
      }));

      // "No bubble is open" is the right test for flushing BUFFERED MESSAGES
      // — there is nothing mid-write to lose. It is the wrong test for the
      // indicator: a tool-result widget closes the bubble without ending the
      // run, so this used to delete the stashed row and its accumulated count
      // mid-run, and switching back showed a Send button over a turn still in
      // flight. The same "a widget is content, not the end of the run"
      // distinction the foreground branches already make.
      //
      // The stash's lifetime belongs to the processing patch above, which
      // clears it on a real terminator and nothing else.
      //
      // `isThinking` counts as live here as well (hence `isLiveMessage`).
      // Without it a background run's reasoning phase flushed the buffer on
      // every delta, and since persistence clears the live flags, the reload
      // found no open block and started a fresh one per delta — dozens of
      // one-line thinking blocks instead of one growing trace.
      // The buffer stays in memory until a read of that chat replaces it.
      // It used to be flushed to disk here, which is what made the local copy
      // a second record of the conversation — grown from frames this tab
      // happened to see, and then needing reconciliation against the agent's.
      void account;
      void next;
    },
    [
      resolveOwner,
      paintFromCache,
      requestForeignSessionHydration,
      isOrphanedRun,
    ],
  );

  /**
   * Write the session's model, and report what the gateway actually applied.
   *
   * `sessions.patch { key, model }` — NOT `/model <id>` sent as a chat
   * message, which is what this used to do and which was wrong three ways at
   * once: it appended the command and its answer to openclaw's transcript
   * (so a title was derived from it, and a chat whose only content was a
   * model switch ended up named `/model dt-a/bedrock/anthropic/opus/5` — 24
   * of one customer's 29 chats), it started a real agent run that then had to
   * be claimed by id and swept off the screen, and its only confirmation was
   * prose to be pattern-matched.
   *
   * The comment this replaces claimed the gateway "rejects `sessions.patch`
   * from webchat clients". It does not: `model` is a first-class patch field,
   * validated against the model catalog, and the terminal already patches
   * every organisation field over the same call.
   *
   * THE RESPONSE IS THE VERIFICATION. `resolved` carries the provider and
   * model the gateway settled on, so a refusal is a rejected request and a
   * silent substitution is a `resolved` that differs from what was asked.
   * That replaced a polling read-back — two `sessions.describe` attempts at
   * 1.5s and 4s — which could only ever tell us what this answer already
   * does, a few seconds later and with a window in which the picker showed a
   * model the gateway had already refused.
   *
   * Echo suppression stays wired up: a user can still type `/model` into the
   * composer, and that run's answer is still folded.
   */
  /**
   * Chats whose model has been written to the gateway on this connection.
   *
   * The model write is deferred to a chat's first send (see `sendMessage`),
   * so this is what distinguishes "never told the gateway" from "already
   * told it" without another round-trip.
   */
  const modelWrittenRef = useRef<Set<string>>(new Set());

  /**
   * Chats the user renamed in this tab.
   *
   * Guards the window between the rename and the `sessions.patch { label }`
   * that publishes it: openclaw's generated title arrives on a
   * `sessions.changed` push 2–4s after a chat's first send, which is exactly
   * when someone renaming a brand-new chat is typing, and a push carrying
   * only `displayName` would undo the rename. After the round trip the label
   * is on every push and outranks the generated title without this.
   *
   * Per tab, and deliberately not persisted: after a reload the label IS on
   * the gateway, so `sessionRowTitle` prefers it. Another device carries the
   * same exposure for the same few seconds and closes it the same way.
   */
  const userTitledSessionsRef = useRef<Set<string>>(new Set());

  /**
   * The generation the gateway last reported for each chat — its row's
   * `sessionId`, which is NOT the chat's key: it changes when a chat is reset
   * or replaced.
   *
   * Kept so `sessions.delete` can be a compare-and-swap. Without it a delete
   * racing a clear of the same chat on another device removes whatever
   * conversation now holds the key, which after a reset is a different one.
   */
  const rosterGenerationRef = useRef<Map<string, string>>(new Map());

  const writeSessionModel = useCallback(
    (
      ws: AgentWebSocket,
      modelId: string,
      sessionKey: string | undefined,
      write: {
        readonly kind: 'pick' | 'seed';
        readonly confirmInChat: boolean;
        /**
         * What this chat's model was before the caller optimistically moved
         * the picker, so a REFUSED write can put it back. `null` means the
         * gateway had no model for this chat, and clearing the slot is the
         * right bounce.
         */
        readonly previousModelId?: string | null;
      },
    ): void => {
      // No session row to patch. Callers hold the pick as `pendingModelId`
      // and apply it once `sendMessage` mints the real session.
      if (sessionKey == null) return;
      // The send path seeds any chat it has not seeded yet; recording every
      // write (pick or seed) is what stops it re-writing on the second send.
      modelWrittenRef.current.add(sessionKey);
      // Which pick this write belongs to.
      //
      // Its own counter, per session, bumped ONLY by `setSelectedModel`.
      // `sessionModelFetchSeqRef` is the obvious candidate and is wrong: it
      // is also bumped when the seeding effect re-runs, which happens on
      // every new chat — so a seed's own write was superseded before it
      // answered and stopped reporting its substitution.
      //
      // What this has to catch is pick A, then pick B before A answers: B
      // lands, then A's refusal writes A's previous model back over B and its
      // `modelWrittenRef.delete` discards B's record too.
      const writeSeq = modelPickSeqRef.current.get(sessionKey) ?? 0;
      const superseded = (): boolean =>
        (modelPickSeqRef.current.get(sessionKey) ?? 0) !== writeSeq;
      void patchSessionModel(ws, sessionKey, modelId)
        .then(({ modelId: applied }) => {
          // A gateway that accepted the write but answered without a
          // `resolved` block is taken at its word: the patch did not error,
          // so the model it holds is the one we asked for. Treating silence
          // as a mismatch would bounce the picker off a successful write.
          if (applied == null || modelIdsMatch(applied, modelId)) {
            if (!write.confirmInChat) return;
            // Superseded by a newer pick: two picks in flight would otherwise
            // post two "Model switched" lines in answer order, and the last
            // one can name the model the chat is no longer on.
            if (superseded()) return;
            // The line marks WHERE in a conversation the model changed, so it
            // is written only if the chat is still a conversation. Landing it
            // on an empty transcript would replace ChatView's landing layout
            // with a chat the user never started.
            setState((prev) =>
              prev.activeSessionId === sessionKey && prev.messages.length > 0
                ? {
                    ...prev,
                    messages: [
                      ...prev.messages,
                      {
                        id: generateMessageId('model-pick'),
                        role: 'assistant' as const,
                        content: `Model switched to ${defaultModelLabel(modelId)}.`,
                        timestamp: Date.now(),
                      },
                    ],
                  }
                : prev,
            );
            return;
          }
          // The gateway substituted another model — bounce the picker back to
          // what it kept, and say so. Unless a newer pick has since been
          // made, in which case this answer is about a model the user has
          // already moved off.
          if (superseded()) return;
          applySessionModel(sessionKey, applied);
          lastUsedModelIdRef.current = applied;
          if (activeSessionIdRef.current === sessionKey) {
            const wanted = defaultModelLabel(modelId);
            const actual = defaultModelLabel(
              reconcileModelIdWithCatalog(applied, modelsRef.current),
            );
            showTransientError(
              write.kind === 'pick'
                ? `Couldn't switch to ${wanted} — still on ${actual}.`
                : `Couldn't start this chat on ${wanted} — it's running ${actual}.`,
            );
          }
        })
        .catch((err: unknown) => {
          // The write did NOT land, so the chat has not been told its model
          // and the next send must try again. The key is added optimistically
          // above; leaving it set after a refusal made `sendMessage`'s
          // `!modelWrittenRef.has()` gate skip the write for every later send
          // in that chat, for the life of the connection — one transient
          // refusal and the chat silently ran the gateway's model instead.
          // Superseded by a newer pick: that pick has its own record and its
          // own model on screen, and both the delete and the bounce below
          // would be undoing it. The failure is still logged.
          if (!superseded()) {
            modelWrittenRef.current.delete(sessionKey);
            // And put the picker back. The caller moved it optimistically
            // before this write, so a refusal left it — and
            // `runRecord.modelId` for every later turn — naming a model the
            // chat is NOT running. The substitution branch above already
            // bounces; a refusal is the stronger case for it, and only this
            // one was missing.
            if ('previousModelId' in write) {
              applySessionModel(sessionKey, write.previousModelId ?? null);
              // The inherited model for the NEXT new chat, rolled back for
              // the same reason the substitution branch rolls it forward.
              // Left set, every fresh chat re-seeded with the refused id —
              // and re-seeding is a write, so each one failed the same way.
              lastUsedModelIdRef.current = write.previousModelId ?? null;
            }
          }
          // A refused write (unknown model, catalog still loading) or a socket
          // that went away. Reported, and said once where the user can see it.
          logModelDebug('model-write-failed', {
            sessionKey,
            modelId,
            error: String(err),
          });
          if (activeSessionIdRef.current === sessionKey) {
            showTransientError(
              write.kind === 'pick'
                ? `Couldn't switch to ${defaultModelLabel(modelId)}.`
                : // A seed is not a switch: the user picked nothing here, the
                  // chat was being started on an inherited or default model.
                  // "Couldn't switch to X" for a first send reads as a
                  // failure of something they just did.
                  `Couldn't start this chat on ${defaultModelLabel(modelId)}.`,
            );
          }
        });
    },
    [applySessionModel, showTransientError, logModelDebug],
  );

  const connect = useCallback(
    (url?: string, token?: string) => {
      const account = accountIdRef.current;
      if (account == null) return;

      // An explicit connect clears any prior manual-disconnect intent.
      manualDisconnectRef.current = false;
      // Latch synchronously so a concurrent caller (e.g. a second wake event)
      // can't enter a parallel connect while this attempt is still resolving.
      isConnectingRef.current = true;

      const cfg = configRef.current;

      // A caller-supplied pairing token (e.g. the setup form) is persisted to
      // the token store before the socket opens, so the handshake sends it as
      // `auth.token`. Drop any cached device token from a prior pairing so the
      // new token is used instead of a stale one (avoids "token mismatch").
      if (token != null && token.trim().length > 0) {
        urlStore.setAgentToken(account, token.trim());
        urlStore.clearDeviceToken(account);
      }

      // resolvedUrl precedence: explicit arg / config.gatewayUrl, then the
      // host resolver, then the localStorage-backed urlStore.
      void (async () => {
        const explicitUrl = url ?? cfg.gatewayUrl ?? null;
        const resolvedFromHost =
          explicitUrl ?? (await cfg.resolveUrl?.(account)) ?? null;
        const agentUrl =
          resolvedFromHost ?? urlStore.getAgentUrl(account) ?? null;
        if (agentUrl == null || agentUrl.length === 0) {
          isConnectingRef.current = false;
          return;
        }

        // Close existing connection
        if (wsRef.current != null) {
          wsRef.current.close();
        }

        setState((prev) => {
          const paired = deriveIsPaired({
            account,
            url: agentUrl,
            supportsPairingRole: cfg.supportsPairingRole,
            hasStoredToken: urlStore.getAgentToken(account) != null,
            fallbackPairingToken: cfg.fallbackPairingToken,
          });
          return {
            ...prev,
            agentUrl,
            status: 'connecting',
            isPaired: paired,
            // The chat list is about to be asked for, so say it is coming.
            //
            // This is the moment the sidebar first exists: until now the tab
            // showed the pairing form, and pairing flips `isPaired` here.
            // The launch-time seed cannot cover it — `hydrate` set the chat
            // list to `idle` precisely because an unpaired host asks nobody,
            // and it early-returns for an account it has already hydrated —
            // so the user's first ever sight of their sidebar, for the whole
            // connect-then-authenticate window, was "No chats yet — click +
            // New Chat" on an account with chats.
            //
            // Only when this connect will actually reach a socket, and never
            // over a list already answered for: a reconnect on a device that
            // has read the roster keeps showing it.
            rosterStatus:
              paired && prev.rosterStatus === 'idle'
                ? 'loading'
                : prev.rosterStatus,
          };
        });

        const ws = createOpenClawClient(agentUrl, {
          clientIdPrefix: cfg.clientIdPrefix,
          accountId: account,
          tokenProvider: urlStore,
          deviceSigner: deviceStore,
          healthStrategy: cfg.healthStrategy ?? 'heartbeat',
          supportsPairingRole: cfg.supportsPairingRole ?? true,
          catalogManifest: cfg.features?.generativeUi
            ? cfg.catalogManifest
            : undefined,
          fallbackPairingToken: cfg.fallbackPairingToken,
          resolveHandshakeToken: cfg.resolveHandshakeToken,
          onDebug: cfg.onConnectionDebug,
        });
        wsRef.current = ws;

        ws.onStatusChange((newStatus, errorMsg) => {
          // The attempt has produced a status; the client now drives `status`
          // (and `statusRef` will track it), so release the in-flight latch.
          isConnectingRef.current = false;
          setState((prev) => ({
            ...prev,
            status: newStatus,
            errorMessage:
              errorMsg ?? (newStatus === 'error' ? prev.errorMessage : null),
            // A dropped socket ends every run in flight: the frames that would
            // have stopped the clock are on the other side of it. Nothing else
            // clears the indicator here — before this PR the first answer
            // delta did it and the row was gone within a second or two of the
            // send, so a mid-answer drop was invisible. Now the row lives for
            // the whole turn, and without this a network blip or a laptop
            // sleep leaves it counting forever with the composer pinned to
            // Stop.
            ...(newStatus !== 'authenticated' && prev.processing != null
              ? { processing: null }
              : {}),
            // …and the half-streamed bubble has to close with it, or it stays
            // `isStreaming` forever: no timestamp (BubbleTime gates on it),
            // and — worst — picked up by the NEXT run, whose row has no runId
            // until its first state frame and so falls back to
            // newest-streaming. That opened the new run at "Responding" with
            // an estimate sized from the dead answer's characters: the
            // stale-length symptom `findStreamingMessage` exists to prevent.
            //
            // Unattributable, so nothing is re-dated — the drop says the run
            // stopped, not when it would have finished.
            ...(newStatus !== 'authenticated'
              ? {
                  messages: markTruncated(
                    prev.messages,
                    closeStreamingMessages(prev.messages),
                  ),
                }
              : {}),
          }));

          // A socket that died or errored before any roster read landed is the
          // other way the sidebar's wait ends. Every settle otherwise lives in
          // the read's own `then`/`catch`, which a connection that never
          // authenticates does not reach — so the shimmer had no terminal
          // state. 'connecting' is deliberately not settled: that one is still
          // on its way. A reconnect's read moves this back to 'loading'.
          if (newStatus === 'disconnected' || newStatus === 'error') {
            // Not for a disconnect the user asked for. On a host that pairs
            // by role the sidebar stays mounted through it, so the copy —
            // "Couldn't load your chats. Reconnecting…" — reported a failure
            // that had not happened and a recovery that was not coming.
            if (!manualDisconnectRef.current) {
              settleRosterStatus('unavailable');
            }
          }

          // Reset the session-context guard whenever we leave 'authenticated'
          // so the next successful auth re-sends the operator identity.
          if (newStatus !== 'authenticated') {
            // Before `connectionGeneration` moves and before `cancelledNow` is
            // cleared: the runs being settled belong to the DYING connection,
            // and a stopped one must still read `cancelled`.
            settleOnDisconnect(connectionGenerationRef.current);
            sessionContextSentForRef.current = null;
            // Whether the gateway adopts our send keys is a fact about THIS
            // connection. Left latched it outlived reconnects, account
            // switches and gateway-URL changes, so a tab that once spoke to an
            // adopting gateway went on refusing unfamiliar ids against a
            // minting one — leaving every row unnamed, and an unnamed row is
            // clearable by any live run.
            gatewayAdoptsRunKeysRef.current = false;
            gatewayNamesRunIdsRef.current = false;
            // The runs these describe are gone with the socket.
            connectionGenerationRef.current += 1;
            unackedSendKeysRef.current.clear();
            orphanedRunIdsRef.current.clear();
            cancelledNowRef.current.clear();
            // The runs those refs describe are gone with the socket. Keeping
            // them grew the ownership map for the tab's lifetime and left Stop
            // reaching for keys belonging to runs that no longer exist.
            pendingRunKeysRef.current.clear();
            runOwnershipRef.current.clear();
            // Which chats we have told the gateway their model. Cleared per
            // CONNECTION, not per tab: a gateway-URL change points the same
            // account at a different agent, where those rows do not exist, so
            // the first send in each chat must write again. The cost of being
            // wrong this way is one idempotent patch; the other way is a
            // first turn silently running the wrong model.
            modelWrittenRef.current.clear();
            // Same reasoning for runs the user has switched away from: their
            // frames are gone too, so a switch back must not restore a clock
            // counting a run that can no longer end.
            //
            // Their TRANSCRIPTS need the same treatment, and this is the one
            // place that can give it. A backgrounded buffer is flushed to the
            // store only when nothing in it is streaming, so a run streaming
            // at drop time would keep its open bubble in a buffer nothing will
            // ever close again — and the row that explained it has just been
            // dropped above. Switching back showed a half-answer still
            // "streaming" with no indicator, and the next send in that session
            // opened at "Responding" off the dead bubble.
            // No account is not the same as no entries. Nothing can be
            // created before an account exists, but a LOGOUT nulls it while
            // the buffers still hold live runs, and a drop after that used to
            // skip the whole loop — leaving bubbles marked streaming in a
            // buffer `switchSession` prefers over the store, which is the
            // exact leak this block exists to prevent. So the close always
            // happens; only the persist needs an account.
            const account = accountIdRef.current;
            for (const [id, messages] of backgroundSessionsRef.current) {
              const closed = markTruncated(
                messages,
                closeStreamingMessages(messages),
              );
              if (closed === messages) continue;
              if (account == null) {
                backgroundSessionsRef.current.set(id, closed);
                continue;
              }
              // Persisted and dropped, together. Persisting alone would leave
              // a buffer that `switchSession` prefers over the store; dropping
              // alone would lose the half-answer entirely.
              //
              // The truncation IS reconciled now, which is the point of this
              // whole change and the reason this comment no longer says
              // otherwise. `hydrateSessionFromGateway` reads the agent's own
              // transcript on the next connect or open, and
              // `mergeGatewayTranscript` recognises this half-answer as the
              // same bubble as the finished one — the local copy is a prefix
              // of it, and `truncated` is a flag rather than appended text,
              // so the content stays a clean prefix and the full answer
              // replaces it in place.
              //
              // It used to be permanent: the `/sync/v1` merge unioned by
              // message id with the LOCAL copy winning, so the gateway's
              // complete version of this same bubble could never replace the
              // half we saved, and it survived as long as the message id did.
              //
              // Kept in the buffer rather than written to disk: the user can
              // still see what arrived and it is closed rather than streaming
              // forever, and the next read of that chat replaces it with the
              // agent's complete version.
              backgroundSessionsRef.current.set(id, closed);
            }
            backgroundProcessingRef.current.clear();
          }

          if (newStatus === 'authenticated') {
            // One-time session-context message so the agent knows the
            // operator identity. Bypasses sendMessage so it doesn't appear
            // in visible chat history.
            const dedupKey = cfg.sessionContextDedupKey ?? account;
            const ctx = cfg.sessionContext;
            if (ctx != null && sessionContextSentForRef.current !== dedupKey) {
              try {
                ws.send(buildSessionContextMessage(ctx));
                sessionContextSentForRef.current = dedupKey;
              } catch (err) {
                reportError(err);
              }
            }

            // Fetch recent chat history so reconnects show prior messages.
            fetchChatHistory(ws, setState);

            // The sidebar, from the agent. One request does both jobs: it
            // returns the roster AND opts this connection into
            // `sessions.changed` pushes, which also carry the session row's
            // context-window numbers (see the usage effect). It therefore
            // replaces the bare `sessions.subscribe` that used to sit here
            // for the meter's pushes — per-connection, not per-session, so
            // one call covers switching chats too. A gateway that doesn't
            // know the method just errors the request: the meter falls back
            // to its `sessions.describe` seed and the cached sidebar stands.
            void (async () => {
              // Held for the WHOLE read, not just its migration step. A
              // catch-up read supersedes this one (`rosterReadSeqRef`), and
              // the token bail is the first check after every await — so a
              // catch-up kicked at any point before the migration cancels it,
              // and the projection then overwrites the local titles and pins
              // the migration was about to push. Set here rather than inside
              // because the read has many early returns and this needs one
              // release that cannot be missed.
              // A COUNTER, not a boolean. Two connect reads can overlap —
              // two `authenticated` transitions, or a reconnect while the
              // first is still paging — and with a boolean the first to
              // finish (typically the one that bailed as superseded) cleared
              // the hold while the other was still mid-await, reopening the
              // window a catch-up read could cancel the migration in.
              rosterMigrateInFlightRef.current += 1;
              try {
                // `migrate: true` on every connect: whether it RUNS is
                // decided inside, at the moment the read reaches the
                // migration, so this handler needs no latch of its own and
                // two `authenticated` transitions cannot both run it.
                await hydrateRosterFromGateway({
                  subscribe: true,
                  migrate: true,
                });
                settleRosterStatus('ready');
              } catch (err) {
                settleRosterStatus('unavailable');
                // A gateway that does not know these methods, or a socket
                // that went away mid-read. The cached sidebar stands; the
                // next reconnect tries again. Reported once rather than
                // surfaced: the user sees their chats either way.
                reportError(err);
              } finally {
                rosterMigrateInFlightRef.current = Math.max(
                  0,
                  rosterMigrateInFlightRef.current - 1,
                );
                // A connect read that got through is the clearest evidence
                // the gateway is answering again.
                foreignHydrateFailuresRef.current = 0;
                // Any catch-up key that arrived while the hold was on — and
                // ONLY then. Calling unconditionally kicks a full roster read
                // on every connect, which is both wasted and actively
                // harmful: it bumps the read token and supersedes the read
                // that just finished.
                // Scheduled for the remaining throttle window rather than
                // called straight through it: a reconnect within 5s of a
                // catch-up read hit the throttle, returned early, and left
                // the queued keys waiting for the next foreign frame.
                if (foreignHydratePendingRef.current.size > 0) {
                  const waited = Date.now() - lastForeignHydrateAtRef.current;
                  const remaining = Math.max(
                    0,
                    FOREIGN_HYDRATE_THROTTLE_MS - waited,
                  );
                  setTimeout(() => {
                    requestForeignSessionHydrationRef.current?.();
                  }, remaining);
                }
              }
            })();

            // Reconcile the open chat against the agent's own transcript.
            // THE reconnect case: the socket dropping mid-answer is what
            // strands a half-streamed bubble, and this is the moment we can
            // see what the run actually produced. Deferred one tick so the
            // hydration effect's own restore (which may switch the active
            // session) commits first.
            {
              const openSession = activeSessionIdRef.current;
              if (openSession != null) {
                setTimeout(() => {
                  if (activeSessionIdRef.current === openSession) {
                    hydrateSessionFromGateway(openSession);
                  }
                }, 0);
              }
            }

            // No model push on reconnect — the session's modelOverride is
            // persisted on the gateway's disk and survives connection drops.
            // The session-model effect re-READS it (sessions.describe) when
            // status reaches 'authenticated'.

            // Notify consumer so they can persist the successfully-paired
            // URL + cache it locally. Guard so reconnects with the same URL
            // don't hammer their persistence layer.
            if (
              agentUrl.length > 0 &&
              agentUrl !== lastPersistedUrlRef.current
            ) {
              lastPersistedUrlRef.current = agentUrl;
              try {
                cfg.persistAgentUrl?.(agentUrl);
                urlStore.setAgentUrl(account, agentUrl);
              } catch (err) {
                lastPersistedUrlRef.current = null;
                reportError(err);
              }
            }
          }
        });

        ws.onMessage((frame: GatewayFrame) => {
          handleFrame(frame, setState, {
            generativeUiEnabled: generativeUiEnabledRef.current,
            onAppRegister: undefined,
            onAppRemove: undefined,
            onTransientError: reportGatewayTransientError,
            // The bounded add: handleFrame's recognition path never evicts.
            suppressedModelEchoRunIds: {
              has: (runId) => suppressedModelEchoRunIdsRef.current.has(runId),
              add: rememberSuppressedModelEchoRun,
            },
            endedRunIds: endedRunIdsRef.current,
            pendingRunKeys: pendingRunKeysRef.current,
            gatewayAdoptsRunKeys: gatewayAdoptsRunKeysRef.current,
            historyFraming: {
              systemPreamble: configRef.current.systemPreamble,
              languageDirectives: allLanguageDirectives(),
            },
            localSendKeys: {
              get: (sendKey) => sentMessageIdsRef.current.get(sendKey),
            },
            routing: {
              routeMessageFrame,
              reportSessionError,
              isCancelled: (runId) =>
                runId != null &&
                (cancelledNowRef.current.has(runId) ||
                  cancelledRunIdsRef.current.has(runId)),
              resolveSessionId: (key) =>
                matchKnownSessionKey(key, knownSessionIdsRef.current),
              mayRetitle: (sessionId) =>
                !userTitledSessionsRef.current.has(sessionId),
              isFrameForActiveSession: (payload) => {
                // Its chat is gone, so it drives no chat's indicators — least
                // of all the one that replaced it. Checked first, as above.
                if (isOrphanedRun(payload)) return false;
                // Foreign frames (another device's not-yet-synced session)
                // must not drive the active chat's indicators.
                if (
                  isForeignSessionFrame(
                    payload,
                    knownSessionIdsRef.current,
                    runOwnershipRef.current,
                  )
                ) {
                  return false;
                }
                const ownerId = resolveOwner(payload);
                return (
                  ownerId == null || ownerId === activeSessionIdRef.current
                );
              },
              onRunEnd: ({ runId, payload, releasesOwnership }) => {
                // Latched HERE, not inside `retireRun`: `stopProcessing` also
                // retires runs, by the keys THIS CLIENT minted, so latching in
                // the funnel let a single Stop on a run-less gateway convince
                // the client that its gateway names runs — after which every
                // bare terminator counted nothing until the next reconnect.
                // Only an id that arrived on a frame is the gateway's word.
                if (runId != null) gatewayNamesRunIdsRef.current = true;
                retireRun(runId, payload, releasesOwnership);
              },
              isRunEnded: (runId, payload) =>
                endedRunIdsRef.current.has(endedRunKey(runId, payload)),
            },
          });
        });
      })().catch((err) => {
        isConnectingRef.current = false;
        reportError(err);
      });
    },
    [
      isOrphanedRun,
      reportError,
      routeMessageFrame,
      reportSessionError,
      resolveOwner,
      endedRunKey,
      urlStore,
      deviceStore,
      settleOnDisconnect,
      reportGatewayTransientError,
      retireRun,
      hydrateSessionFromGateway,
      hydrateRosterFromGateway,
      settleRosterStatus,
      rememberSuppressedModelEchoRun,
    ],
  );

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;

    const account = accountIdRef.current;
    const cfg = configRef.current;

    if (wsRef.current != null) {
      wsRef.current.close();
      wsRef.current = null;
    }
    // Token-only hosts (no self-service pairing): drop the stored token so the
    // disconnect is durable. A later remount re-reads storage, finds no token,
    // and shows the login screen instead of silently reconnecting. Pairing-role
    // hosts keep their URL-based pairing — there's nothing to drop.
    if (account != null && cfg.supportsPairingRole === false) {
      urlStore.clearAgentToken(account);
    }
    setState((prev) => ({
      ...prev,
      status: 'disconnected',
      processing: null,
      isPaired: deriveIsPaired({
        account,
        url: prev.agentUrl,
        supportsPairingRole: cfg.supportsPairingRole,
        hasStoredToken:
          account != null && urlStore.getAgentToken(account) != null,
        fallbackPairingToken: cfg.fallbackPairingToken,
      }),
    }));
  }, [urlStore]);

  const sendMessage = useCallback(
    (
      content: string,
      opts?: {
        readonly context?: string;
        readonly attachments?: readonly ChatAttachment[];
      },
    ) => {
      if (wsRef.current == null) return;

      // A new turn re-opens THIS chat's run-less stream: frames that carry no
      // runId belong to this run now, so the "that run is over" mark goes.
      endedRunIdsRef.current.delete(
        `${RUNLESS_RUN_KEY}:${activeSessionIdRef.current ?? ''}`,
      );

      const attachments =
        opts?.attachments?.filter((a) => a.content.length > 0) ?? [];
      const hasAttachments = attachments.length > 0;
      if (content.trim().length === 0 && !hasAttachments) return;

      // Guard the frame size BEFORE any state changes: an oversize frame
      // doesn't error, it closes the whole proxy session (1009), which
      // reads as a random disconnect. Surface a real error instead.
      const wirePayload = buildSendPayload(
        configRef.current.systemPreamble,
        content,
        opts?.context,
        // Read through a ref, not the render-time value: `sendMessage` is a
        // stable callback and would otherwise capture whatever language was
        // set when it was created.
        languageDirective(languageRef.current),
      );
      if (
        estimateChatSendBytes(wirePayload, attachments) > MAX_CHAT_SEND_BYTES
      ) {
        const tooLarge = 'Message too large to send — remove an attachment.';
        // Never reaches the gateway, so nothing else would report it.
        reportChatError({
          source: 'client',
          message: tooLarge,
          sessionId: activeSessionIdRef.current,
        });
        showTransientError(tooLarge);
        return;
      }

      // The transcript keeps attachment metadata + thumbnail only — never the
      // base64 payload (localStorage quota, 8 MiB sync-doc cap).
      const messageAttachments: readonly MessageAttachment[] = attachments.map(
        (a) => ({
          type: a.type,
          mimeType: a.mimeType,
          ...(a.fileName != null ? { fileName: a.fileName } : {}),
          ...(a.previewUrl != null ? { previewUrl: a.previewUrl } : {}),
        }),
      );

      // Resolve the target session BEFORE touching state. React runs setState
      // updaters eagerly only when the fiber's update queue is empty — when a
      // host calls sendMessage in the same batch as other updates (e.g. a
      // pending-message drain firing on the auth transition), an id assigned
      // inside the updater is still unset after the setState call returns.
      // The frame then went out with no sessionKey, the gateway filed the run
      // under its own main session, and the echoed reply — keyed
      // `agent:main:main`, matching no local session — was dropped as another
      // device's frame, leaving the spinner stuck. The refs are synced here
      // so a second same-batch send reuses the same new session.
      // Minting here means there was no session at all — so no `/model` write
      // can have reached this session yet (a pick made in this state has
      // nowhere to go; see `setSelectedModel`). The seed below carries it.
      const mintedSession = activeSessionIdRef.current == null;
      const targetId = activeSessionIdRef.current ?? generateChatSessionId();
      // Read BEFORE the setState below clears this chat's banner: "was this a
      // retry" is gone one line later.
      const isRetry = stateRef.current.sessionErrors[targetId] != null;
      const turnIndex = mintedSession
        ? 1
        : stateRef.current.messages.filter((m) => m.role === 'user').length + 1;
      // Anything the user did to a DRAFT before sending in it — renamed it,
      // pinned it, composed into a folder. None of it reached the gateway,
      // because `sessions.patch` creates the row and the point of a draft is
      // that no row exists yet. Read before the setState below drops
      // `isDraft`, and published just after the model seed.
      const draftRow = isDraftSession(targetId)
        ? stateRef.current.sessions.find((s) => s.sessionId === targetId)
        : undefined;
      activeSessionIdRef.current = targetId;
      knownSessionIdsRef.current.add(targetId);

      // `unsent` until the gateway answers this send, and cleared by the ack
      // below. It marks the bubble as one the agent's transcript is not
      // expected to carry, which is what stops `mergeGatewayTranscript`
      // deleting it: the merge treats a local bubble the gateway does not have
      // as deleted there, and the socket dropping between this append and the
      // ack is exactly the case where that inference is wrong — the reconnect
      // then triggers the read that ate the user's own typed message.
      //
      // Optimistic in the safe direction. A flag left set (a gateway that
      // never answers `chat.send`) only means the bubble is never replaced,
      // which is the behaviour that predates replacement.
      const userMsg: ChatMessage = {
        id: generateMessageId('user'),
        role: 'user',
        content,
        timestamp: Date.now(),
        ...(hasAttachments ? { attachments: messageAttachments } : {}),
        unsent: true,
      };

      // Image-only sends have no text to derive a title from.
      const titleSource =
        content.trim().length > 0
          ? content
          : (attachments[0]?.fileName ?? 'Image');

      setState((prev) => {
        const now = Date.now();
        let { sessions } = prev;

        if (!sessions.some((s) => s.sessionId === targetId)) {
          const newSession: ChatSession = {
            sessionId: targetId,
            folder: defaultFolder,
            title: DEFAULT_SESSION_TITLE,
            pinned: false,
            createdAt: now,
            updatedAt: now,
          };
          sessions = [newSession, ...sessions];
        }

        sessions = sessions.map((s) => {
          if (s.sessionId !== targetId) return s;
          // `isDraft` is dropped, not set false: this message is what makes the
          // chat real, so from here it belongs in Recents and in the persisted
          // index like any other.
          const { isDraft: _wasDraft, ...rest } = s;
          return {
            ...rest,
            title:
              s.title === DEFAULT_SESSION_TITLE
                ? deriveSessionTitle(titleSource)
                : s.title,
            updatedAt: now,
          };
        });

        // Sending again is the user acting on the failure — retire it rather
        // than leaving a stale banner above a live run.
        const { [targetId]: _cleared, ...remainingErrors } = prev.sessionErrors;
        // The pick (if any) now lives on a real session — retire the pending slot.
        return {
          ...prev,
          sessions,
          activeSessionId: targetId,
          processing: {
            label: 'Thinking',
            startedAt: Date.now(),
            thinkingChars: 0,
          },
          messages: [...prev.messages, userMsg],
          sessionErrors: remainingErrors,
          pendingModelId: null,
        };
      });

      // Tell the gateway which model to run, for any chat we have not told
      // yet — the one minted right here, and equally the one the user opened
      // with "+ New Chat" and is now sending in for the first time.
      //
      // This is the ONLY place the model is written for a fresh chat. The
      // session-model effect resolves and displays it on open but no longer
      // writes, because `sessions.patch` creates the session and that left a
      // gateway row behind every unused chat.
      //
      // Ordering: pushed FIRST, on the same socket, so the gateway applies it
      // before the message. Worth knowing that this is asserted rather than
      // measured here — #73 measured the analogous `sessions.create` racing
      // `chat.send` in the same millisecond and lost, so if a first turn is
      // ever seen running the wrong model, this is the suspect.
      if (mintedSession || !modelWrittenRef.current.has(targetId)) {
        const seedId =
          stateRef.current.sessionModels[targetId] ?? resolveSeedModelId();
        // Consumed: the pick belonged to this now-minted chat, and leaving it
        // set would make it outrank the default for the NEXT new chat too.
        pendingModelIdRef.current = null;
        // Same rule as `seedFreshSession`: seeding WRITES, so it must never
        // republish a dead id. Now that the configured default feeds this path,
        // the id can outlive the catalog that minted it (set on gateway A,
        // sending on gateway B) — pushing it raw would run the first turn on a
        // retired model, or fail with "No API key found for provider <x>",
        // while opening the same chat WITHOUT sending fell back correctly.
        // Dropping the push is that fallback: no `/model` means the gateway
        // runs its own default, which is what Settings promises for an
        // orphaned id. An empty catalog is "no opinion yet", not "unknown", so
        // the raw id stands there and the next read-back corrects it.
        const catalog = modelsRef.current;
        const liveSeedId =
          seedId == null || catalog.length === 0
            ? seedId
            : (resolveModelInCatalog(catalog, seedId)?.id ?? null);
        if (liveSeedId != null) {
          // No chat line for a seed: it would be a bubble about a model
          // rather than about the message. A refusal still reaches the user
          // as a toast — see `writeSessionModel`.
          const before = stateRef.current.sessionModels[targetId] ?? null;
          applySessionModel(targetId, liveSeedId);
          writeSessionModel(wsRef.current, liveSeedId, targetId, {
            kind: 'seed',
            confirmInChat: false,
            previousModelId: before,
          });
        }
      }

      // And the organisation the draft was carrying. One patch, and only for
      // the fields the user actually set: an omitted field means "leave it
      // alone", so sending a `null` category for a chat that was never filed
      // would be a write about nothing.
      // This message is what makes the chat real, so it stops being a draft
      // here whether or not it was carrying any organisation.
      if (draftSessionIdRef.current === targetId) {
        draftSessionIdRef.current = null;
      }
      if (draftRow != null) {
        const organisation: SessionOrganisationPatch = {
          ...(draftRow.title !== DEFAULT_SESSION_TITLE
            ? { label: draftRow.title }
            : {}),
          ...(draftRow.pinned === true ? { pinned: true } : {}),
          ...(draftRow.customFolderId != null
            ? safeCategory(folderNameFor(draftRow.customFolderId))
            : {}),
        };
        if (Object.keys(organisation).length > 0) {
          gatewayOrganisationWrite('save this chat’s details', (ws) =>
            patchSessionOrganisation(ws, targetId, organisation),
          );
        }
      }

      // The wire payload (built above, pre-guard) carries the host's system
      // preamble and optional hidden context; the chat bubble (userMsg above)
      // shows only what the user typed.
      lastSendSessionRef.current = targetId;
      // Any transcript read already in flight for this chat now describes a
      // conversation that predates this turn. See `sendSeqRef`.
      sendSeqRef.current.set(
        targetId,
        (sendSeqRef.current.get(targetId) ?? 0) + 1,
      );
      // Claim the run at send time, by the id the GATEWAY chose.
      //
      // `chat.send` answers with that id — openclaw echoes our idempotency key
      // back, a gateway that mints its own returns that instead — and the
      // client now reports it. Before, the response went unread and the
      // client had to work out which of a chat's runs was its own from frames
      // alone. That is not decidable: an unfamiliar run id is equally a cron
      // turn, another client, or another tab. Every mechanism built on that
      // guess got it wrong in a different way.
      //
      // With the real id in hand the key stops being a hopeful stand-in and
      // the two kinds of gateway stop needing separate reasoning: ownership,
      // cancellation, suppression and retirement all key off one id that
      // frames actually carry.
      const sentOnGeneration = connectionGenerationRef.current;
      /**
       * Shared BY REFERENCE across every key this run is claimed under: a
       * second record would restart the clock at ack time, reporting the
       * gateway's round-trip instead of the wait the user sat through.
       */
      const runRecord: RunRecord = {
        sessionId: targetId,
        startedAt: Date.now(),
        turnIndex,
        hadAttachments: hasAttachments,
        isRetry,
        connectionGeneration: sentOnGeneration,
        modelId: null,
      };
      const claimRun = (runId: string): boolean => {
        // The chat was deleted while this was in flight. `resolveOwner`'s
        // last-send fallback is guarded the same way; without it the run's
        // frames re-create the transcript we just removed, which is the
        // "Recovered chat" hole `deleteSession` exists to close.
        if (!knownSessionIdsRef.current.has(targetId)) return false;
        // The socket this was sent on is gone. Its runs went with it.
        //
        // Defensive by transport analysis, not by test: a response cannot
        // arrive over a dead connection, and `close()` rejects what is
        // parked — the fake was corrected to match, which removed the only
        // route to exercising this. Kept because `pendingRequests` clearing
        // only on `close()` is a property of the client, not of this file.
        if (connectionGenerationRef.current !== sentOnGeneration) return false;
        // The run finished before its own ack arrived — a fast turn on a
        // prompt gateway. Re-adding it would put a dead run back among the
        // live ones, and Stop would cancel it again on every press. It is
        // still counted: under our own key it already was, and under a
        // minting gateway's id its terminator is waiting to be claimed.
        if (endedRunIdsRef.current.has(runId)) {
          settleEarlyEnd(runId, runRecord);
          return false;
        }
        runOwnershipRef.current.set(runId, targetId);
        pendingRunKeysRef.current.add(runId);
        // Counted here because the guards above are exactly the "is this
        // ours?" test the denominator needs.
        runRecord.modelId =
          stateRef.current.sessionModels[targetId] ??
          selectedModelIdRef.current;
        beginRunRecord(runId, runRecord);
        return true;
      };
      let issuedKey: string | null = null;
      const runKey = wsRef.current.send(wirePayload, {
        sessionKey: targetId,
        ...(hasAttachments ? { attachments } : {}),
        onRunId: (runId) => {
          // The gateway named a run, which is what gates the run-less
          // fallback in `retireRun`. Here rather than in `claimRun`, which
          // the send path also calls with our OWN key before any ack — that
          // is the client hoping, not the gateway naming. Before the guards
          // below on purpose: an ack that is refused (deleted chat,
          // streams-first early end) still proves this gateway names runs.
          if (connectionGenerationRef.current === sentOnGeneration) {
            gatewayNamesRunIdsRef.current = true;
          }
          if (issuedKey != null) unackedSendKeysRef.current.delete(issuedKey);
          // The gateway has the message, so it is in its transcript and the
          // merge may treat it like any other bubble. Cleared here rather than
          // at any later terminator because THIS is the acknowledgement — a
          // run that then fails still happened, and its prompt is recorded.
          clearUnsentMark(targetId, userMsg.id);

          // Stopped inside the gap, before the gateway had named the run. The
          // cancel we sent went out under our key, which a minting gateway
          // never issued — so it landed nowhere. Now the run has a name the
          // stop can be carried over to it exactly: same send, no inference.
          const stoppedInTheGap =
            issuedKey != null && cancelledNowRef.current.has(issuedKey);
          if (issuedKey != null && issuedKey !== runId) {
            pendingRunKeysRef.current.delete(issuedKey);
            runOwnershipRef.current.delete(issuedKey);
            // `claimRun(runId)` re-files the SAME object under the gateway's
            // id — or settles it, if the run ended before this ack — so
            // dropping this leaves one run, still send-stamped.
            runRecordsRef.current.delete(issuedKey);
          }
          if (!knownSessionIdsRef.current.has(targetId)) {
            // The chat is gone. Remember the run so its frames are dropped
            // rather than landing in whichever chat is open now.
            //
            // This is the ack, so it covers everything that arrives after it —
            // which is everything, on a gateway that answers `chat.send`
            // before streaming, as `onRunId` documents it must. A gateway that
            // streams FIRST can still leak the frames it sent before
            // answering: they carry an id nothing here has heard of, and the
            // only alternative is to drop every unattributable frame while a
            // deletion is recent, which silences surviving chats, other
            // devices and cron turns alike. Not worth it for the narrower bug.
            orphanedRunIdsRef.current = rememberCancelled(
              orphanedRunIdsRef.current,
              [runId],
            );
          }
          if (!claimRun(runId)) return;
          // Ground truth for whether this gateway adopts our keys — it just
          // told us, for this exact send. Inferring it from "a frame carried
          // an id we hold" stopped working the moment we started holding the
          // gateway's own ids: every minting run then looked like adoption.
          //
          // After the guards on purpose: an ack from a connection that has
          // since been replaced must not latch anything about this one.
          if (runId === issuedKey) gatewayAdoptsRunKeysRef.current = true;
          if (stoppedInTheGap) {
            cancelledNowRef.current = rememberCancelled(
              cancelledNowRef.current,
              [runId],
            );
            retireRun(runId);
            wsRef.current?.request('chat.cancel', { runId }).catch(() => {
              // Gateway doesn't implement chat.cancel — fine.
            });
            setState((prev) => ({
              ...prev,
              cancelledRunIds: rememberCancelled(prev.cancelledRunIds, [runId]),
            }));
          }
        },
      });
      issuedKey = runKey;
      // The two halves of the message's identity, in one place: the key the
      // gateway will echo back on the row, and the bubble already on screen
      // for it. See `sentMessageIdsRef`.
      sentMessageIdsRef.current.set(runKey, userMsg.id);
      while (sentMessageIdsRef.current.size > MAX_REMEMBERED_ENDED_RUNS) {
        const oldest = sentMessageIdsRef.current.keys().next().value;
        if (oldest == null) break;
        sentMessageIdsRef.current.delete(oldest);
      }
      unackedSendKeysRef.current = rememberCancelled(
        unackedSendKeysRef.current,
        [runKey],
      );
      claimRun(runKey);
      while (pendingRunKeysRef.current.size > MAX_REMEMBERED_ENDED_RUNS) {
        const oldest = pendingRunKeysRef.current.values().next().value;
        if (oldest == null) break;
        pendingRunKeysRef.current.delete(oldest);
        // Evicted from BOTH. On a gateway that mints its own ids `retireRun`
        // never fires for a send key — the end frames carry the gateway's id —
        // so the ownership entry written just above would otherwise persist
        // one per send for the life of the tab, and keep offering Stop a key
        // the gateway never issued.
        runOwnershipRef.current.delete(oldest);
      }
    },
    [
      defaultFolder,
      reportChatError,
      beginRunRecord,
      settleEarlyEnd,
      showTransientError,
      safeCategory,
      applySessionModel,
      resolveSeedModelId,
      gatewayOrganisationWrite,
      folderNameFor,
      isDraftSession,
      retireRun,
      writeSessionModel,
      clearUnsentMark,
    ],
  );

  const clearMessages = useCallback(() => {
    // The active session keeps its slot; the messages go from memory AND from
    // disk.
    //
    // The disk half used to be someone else's job — a persist effect that
    // rewrote the chat's blob on every change — and that effect is gone: the
    // only writer of the paint cache now is a completed transcript read. So
    // for a while a clear emptied the screen and left the conversation on
    // disk, and anything that repainted from the cache before a read landed
    // brought it back: switching away and returning, reloading the tab, or
    // the agent refusing the reset. The user cleared a chat, saw it empty,
    // and found it again a moment later.
    //
    // `clearedAt` is still stamped, but it is no longer what carries the wipe
    // to other devices. It was read by the `/sync/v1` session path as a
    // transcript-level tombstone, and that path is preferences-only now — so
    // for a while nothing propagated a clear at all, and the other device
    // kept showing (and re-persisting) the cleared conversation forever. What
    // propagates it is `sessions.reset` below plus the transcript read: an
    // emptied transcript, reported COMPLETE, is applied by
    // `mergeGatewayTranscript`.
    //
    // The stamp itself is now local-only bookkeeping. It was load-bearing
    // while the `/sync/v1` archive was read on chat open — that read would
    // have handed the old messages straight back — and that reader is gone.
    // Kept because `mergeMessages` still honours it and a stored `clearedAt`
    // from an older build must keep meaning what it meant.
    const clearedAt = Date.now();
    setState((prev) => ({
      ...prev,
      messages: [],
      processing: null,
      // The row sits under the transcript, so it goes with it — otherwise
      // "1 running subtask" is left annotating a blank conversation.
      runningSubtasks: clearSubtasksFor(
        prev.runningSubtasks,
        prev.activeSessionId,
      ),
      sessions:
        prev.activeSessionId == null
          ? prev.sessions
          : prev.sessions.map((s) =>
              s.sessionId === prev.activeSessionId ? { ...s, clearedAt } : s,
            ),
    }));
    // And clear it on the AGENT, which the local wipe never did: openclaw
    // kept the conversation in the model's context, so a "cleared" chat's
    // next turn still answered as if the hidden messages were there.
    // `sessions.reset` starts a fresh transcript under the same key — the old
    // one is preserved server-side but no longer live.
    const cleared = activeSessionIdRef.current;
    // The copy on disk, which nothing else deletes any more.
    const account = accountIdRef.current;
    if (cleared != null && account != null) {
      historyStore.deleteSessionMessages(account, cleared);
    }
    // The turn that is still running, if there is one.
    //
    // Emptying the transcript stopped the indicator and took the Stop button
    // with it, and cancelled nothing — so the run kept streaming its answer
    // into the chat the user had just cleared, with no way to stop it and
    // nothing to explain where the text was coming from. Resetting the
    // AGENT's transcript underneath a live run makes that worse, not better.
    // So a clear ends the turn, the way Stop does.
    if (cleared != null) {
      const live: string[] = [];
      for (const [runId, owner] of runOwnershipRef.current) {
        if (owner === cleared) live.push(runId);
      }
      if (live.length > 0) {
        cancelledNowRef.current = rememberCancelled(
          cancelledNowRef.current,
          live,
        );
        setState((prev) => ({
          ...prev,
          cancelledRunIds: rememberCancelled(prev.cancelledRunIds, live),
        }));
        for (const runId of live) {
          retireRun(runId);
          wsRef.current?.request('chat.cancel', { runId }).catch(() => {
            // A gateway without `chat.cancel`. The suppression above still
            // keeps its frames out of the cleared chat.
          });
        }
      }
    }
    // Paging belongs to the transcript that was just thrown away. Left
    // pointing at it, the cleared chat still offered "load earlier" — and
    // fetching it prepended the cleared turns back in, since the guard that
    // refuses a page from another transcript can only fire when the gateway
    // names one.
    if (cleared != null) {
      olderPagesRef.current.delete(cleared);
      requestedOffsetsRef.current.delete(cleared);
      setState((prev) =>
        prev.activeSessionId === cleared
          ? { ...prev, canLoadOlder: false, loadingOlder: false }
          : prev,
      );
    }
    // Not for a DRAFT, for the same reason the four organisation writes skip
    // one: this is a `sessions.*` write against a key the gateway has never
    // heard of, and there is no conversation to reset behind it. A draft's
    // transcript is empty, so the local wipe above is the whole operation.
    if (cleared != null && !isDraftSession(cleared)) {
      gatewayOrganisationWrite('clear this chat', (ws) =>
        resetSession(ws, cleared),
      );
      // A reset gives the key a NEW generation, so the one we recorded is now
      // wrong — and a wrong `expectedSessionId` would make the next delete of
      // this chat fail the compare-and-swap it exists to serve. Forgotten
      // rather than guessed: with no generation the delete is unconditional,
      // which is the correct behaviour for a chat we have not re-read.
      rosterGenerationRef.current.delete(cleared);
    }
  }, [gatewayOrganisationWrite, isDraftSession, retireRun, historyStore]);

  const createSession = useCallback(
    ({
      folder,
      isTemporary,
    }: {
      readonly folder: SessionFolder;
      readonly isTemporary?: boolean;
    }): string => {
      const id = generateChatSessionId();
      const now = Date.now();
      const newSession: ChatSession = {
        sessionId: id,
        folder,
        title: DEFAULT_SESSION_TITLE,
        pinned: false,
        createdAt: now,
        updatedAt: now,
        ...(isTemporary === true ? { isTemporary: true } : { isDraft: true }),
      };
      // Sync the session refs before setState commits so a same-batch
      // sendMessage resolves this session (see sendMessage's comment); the
      // render re-sync keeps them consistent afterwards.
      activeSessionIdRef.current = id;
      knownSessionIdsRef.current.add(id);
      // Synced here for the same reason, and read by `isDraftSession`: the
      // organisation actions are called in this same batch by the sidebar's
      // per-folder compose button.
      draftSessionIdRef.current = isTemporary === true ? null : id;
      setState((prev) => {
        // Stash outgoing messages whenever the chat has work in flight — a
        // visibly streaming bubble OR a "Thinking" indicator (send already
        // happened, first delta not back yet). Without the second case, an
        // early switch loses the just-sent user message because the delta
        // arrives before the persist effect flushes it.
        const hasInFlight = needsBackgroundCopy(prev.messages, prev.processing);
        if (prev.activeSessionId != null && hasInFlight) {
          backgroundSessionsRef.current.set(prev.activeSessionId, [
            ...prev.messages,
          ]);
          if (prev.processing != null) {
            backgroundProcessingRef.current.set(
              prev.activeSessionId,
              prev.processing,
            );
          }
        }
        return {
          ...prev,
          sessions: [
            newSession,
            // Drafts go the way temporary chats do: the one being replaced was
            // never sent in, so keeping it would leave a chat behind for every
            // "+ New Chat" the user clicked and walked away from.
            ...prev.sessions.filter(
              (s) => s.isTemporary !== true && s.isDraft !== true,
            ),
          ],
          activeSessionId: id,
          messages: [],
          processing: null,
        };
      });

      // No model push here: the session-model effect (keyed on
      // activeSessionId) sees the fresh session has no gateway row and seeds
      // it with the last-used model — falling back to the gateway default
      // when there is no previous session to inherit from.

      return id;
    },
    [],
  );

  const switchSession = useCallback(
    (sessionId: string) => {
      const account = accountIdRef.current;
      if (account == null) return;
      const cached = backgroundSessionsRef.current.get(sessionId);
      const cachedProcessing =
        backgroundProcessingRef.current.get(sessionId) ?? null;
      const messages =
        cached != null ? [...cached] : paintFromCache(account, sessionId);
      if (cached != null) {
        backgroundSessionsRef.current.delete(sessionId);
      }
      backgroundProcessingRef.current.delete(sessionId);
      // Pull the transcript from the gateway if we opened a session we have no
      // local copy of (skip when restoring a still-streaming background chat).
      // Nothing to show and a read on its way: say so, rather than letting
      // the view read an empty transcript as a chat nobody has written in.
      // Only for a chat that exists on the agent — a draft is genuinely empty
      // and its greeting is the right screen.
      const isKnownChat = stateRef.current.sessions.some(
        (s) =>
          s.sessionId === sessionId &&
          s.isDraft !== true &&
          s.isTemporary !== true,
      );
      // ALWAYS read, cached or not.
      //
      // This used to be skipped whenever anything was cached, which included
      // the live-tail buffer a background run writes. That buffer has no
      // eviction, so a chat this device had never read but which received one
      // background delta was pinned to that single orphan bubble for the rest
      // of the tab's life: no read was ever issued, and every switch back
      // showed the same one-message transcript. A buffer is something to
      // PAINT with, never a substitute for the agent's own answer.
      //
      // Reading unconditionally is safe because the read itself declines while
      // a run is outstanding for the chat — the live path owns the transcript
      // then — so the case the old gate was protecting is still protected, by
      // the code that knows about it.
      //
      // The only read here. It used to be preceded by a `/sync/v1` archive
      // pull, sequenced ahead of this one because both merge and write the
      // transcript and running them in parallel let the later write drop the
      // earlier one's additions. That pull is gone: the gateway is the single
      // source of truth for transcripts, and the chats it could not fully
      // serve — the ones predating per-chat session keys, whose early turns
      // went to the shared main session that was later reset — are an
      // accepted loss rather than a reason to keep a second reader alive.
      const readStarted = hydrateSessionFromGateway(sessionId);
      // "Loading" has to mean a read is coming. It is a wait only when
      // there is nothing to show AND something is on its way; with the socket
      // down `readStarted` is false and an empty chat is just empty.
      const awaitingRead =
        readStarted && cached == null && messages.length === 0 && isKnownChat;
      // Sync the active-session ref before setState commits so a same-batch
      // sendMessage targets the switched-to session (see sendMessage's
      // comment). Only for locally known sessions — the updater below ignores
      // unknown ids, so the ref must too.
      if (knownSessionIdsRef.current.has(sessionId)) {
        activeSessionIdRef.current = sessionId;
      }
      setState((prev) => {
        if (!prev.sessions.some((s) => s.sessionId === sessionId)) return prev;
        const loadingTranscriptFor = awaitingRead ? sessionId : null;
        // Stash outgoing messages whenever the chat has work in flight — a
        // visibly streaming bubble OR a "Thinking" indicator (send already
        // happened, first delta not back yet). Without the second case, an
        // early switch loses the just-sent user message because the delta
        // arrives before the persist effect flushes it.
        const hasInFlight = needsBackgroundCopy(prev.messages, prev.processing);
        if (
          prev.activeSessionId != null &&
          prev.activeSessionId !== sessionId &&
          hasInFlight
        ) {
          backgroundSessionsRef.current.set(prev.activeSessionId, [
            ...prev.messages,
          ]);
          if (prev.processing != null) {
            backgroundProcessingRef.current.set(
              prev.activeSessionId,
              prev.processing,
            );
          }
        }
        return {
          ...prev,
          sessions: prev.sessions.filter(
            (s) =>
              (s.isTemporary !== true && s.isDraft !== true) ||
              s.sessionId === sessionId,
          ),
          activeSessionId: sessionId,
          messages,
          loadingTranscriptFor,
          // A failure belongs to the chat it happened in. Carried across a
          // switch it would put "couldn't load this conversation" over a
          // different one, whose read is fine.
          failedTranscriptFor: null,
          // Restore the indicator if the session has work pending — switching
          // back during "Thinking" should still show the verb spinner.
          processing: cachedProcessing,
          // No model work on switch: `selectedModelId` is derived from
          // `sessionModels[activeSessionId]`, so flipping the active session
          // IS the display update — instantly, from whatever this session's
          // last known model was, with no flicker and no second setState. The
          // session-model effect then refreshes it from the gateway.
        };
      });
    },
    [paintFromCache, hydrateSessionFromGateway],
  );

  const renameSession = useCallback(
    (sessionId: string, title: string) => {
      const trimmed = title.trim();
      if (trimmed.length === 0) return;
      const label = trimmed.slice(0, SESSION_TITLE_MAX_LEN);
      // The chat is named by hand from here: openclaw's generated title must
      // not land on top of it. Once the patch below is applied the gateway
      // echoes the label on every push and it outranks the generated title on
      // its own — this covers the round trip, which on a brand-new chat
      // overlaps the 2–4s the utility model takes.
      userTitledSessionsRef.current.add(sessionId);
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === sessionId ? { ...s, title: label } : s,
        ),
      }));
      // The gateway's own name for the chat. Set it and its utility-model
      // `derivedTitle` stops being what the sidebar shows — on every device,
      // with no document to replicate.
      //
      // The guard is NOT released when the patch resolves. A push composed
      // before the label was applied can still arrive after it, carrying
      // `displayName` alone — which is the race, and releasing on the
      // response loses it. It is not released later either: a chat the user
      // has named should never take a generated title, whenever it arrives.
      // What the guard must not block is a LABEL from another device, and
      // `handleFrame` lets those through on their own (see `mayRetitle`).
      //
      // Not for a draft: the patch would claim the row (`isDraftSession`).
      // The name is applied above and published by the first send.
      if (isDraftSession(sessionId)) return;
      gatewayOrganisationWrite('rename this chat', (ws) =>
        patchSessionOrganisation(ws, sessionId, { label }),
      );
    },
    [gatewayOrganisationWrite, isDraftSession],
  );

  const pinSession = useCallback(
    (sessionId: string, pinned: boolean) => {
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === sessionId ? { ...s, pinned } : s,
        ),
      }));
      // Pinning is load-bearing beyond the sidebar: a pinned session is
      // exempt from every automatic maintenance path openclaw runs (age,
      // row cap, disk budget), so this is also how a user keeps a chat.
      // Nothing to exempt on a draft, though — see `isDraftSession`.
      if (isDraftSession(sessionId)) return;
      gatewayOrganisationWrite('pin this chat', (ws) =>
        patchSessionOrganisation(ws, sessionId, { pinned }),
      );
    },
    [gatewayOrganisationWrite, isDraftSession],
  );

  const stopProcessing = useCallback(() => {
    const currentMessages = messagesRef.current;
    // A run that is still only THINKING is just as in-flight as one streaming
    // text — and it is the common case for Stop now that reasoning is on by
    // default, since the block can be open for many seconds before the first
    // answer token. Missing it here left the run uncancelled, its runId
    // un-blacklisted (so the answer streamed in after Stop), and its block
    // shimmering forever.
    const hasStreaming = currentMessages.some(isLiveMessage);
    if (processingRef.current == null && !hasStreaming) return;

    const activeSessionId = activeSessionIdRef.current;

    /**
     * Every run this chat has in flight, as far as the client can tell.
     *
     * Assembled from what is observable — an open bubble in this transcript,
     * the row — because those are the only ids that reach the run actually
     * generating on a gateway that MINTS its own run ids rather than adopting
     * our send keys. Gating this behind "we have no send keys" made Stop a
     * no-op there: the keys are never retired on such a gateway, so the gate
     * never opened and neither the cancel nor the client-side suppression
     * reached the live run.
     *
     * This chat's own send keys go in alongside, for the window before any
     * frame has named a run. See `keysToSweep` below for which of them, and
     * why that depends on whether the gateway has been seen adopting keys.
     */
    const observedRunIds = new Set<string>();
    for (const m of currentMessages) {
      // `isLiveMessage`, so a run still in its reasoning phase counts: a
      // thinking block is open with no streaming bubble behind it yet.
      if (isLiveMessage(m) && m.runId != null) observedRunIds.add(m.runId);
    }
    const rowRunId = processingRef.current?.runId;
    if (rowRunId != null) observedRunIds.add(rowRunId);
    const stoppedRunIds = new Set<string>(observedRunIds);

    const ourKeysHere = [...pendingRunKeysRef.current].filter(
      (key) => runOwnershipRef.current.get(key) === activeSessionId,
    );
    // Every run this chat has in flight. No bound and no branch: the ids held
    // here are the ones the gateway named, and `retireRun` removes each when
    // its run ends — on either kind of gateway — so what is left IS the live
    // set. The bound this replaces was justified by keys never being retired
    // on a minting gateway, which made cancelling stale ones inert; that
    // stopped being true when the ack became the source of the id, and the
    // bound then started dropping real runs a user was waiting on. Nothing
    // gates Enter on the previous turn having finished, so more than a handful
    // at once is reachable.
    for (const key of ourKeysHere) stoppedRunIds.add(key);

    // Written before the setState below, and read by `isCancelled`: a frame
    // handled in the same batch as this Stop would otherwise see the old set.
    cancelledNowRef.current = rememberCancelled(
      cancelledNowRef.current,
      stoppedRunIds,
    );

    const ws = wsRef.current;
    if (ws != null) {
      const cancel = (runId?: string) =>
        ws
          .request('chat.cancel', runId != null ? { runId } : undefined)
          .catch(() => {
            // Gateway doesn't implement chat.cancel — fine.
          });
      for (const id of stoppedRunIds) cancel(id);
      // The keyless form is the last resort, and only for the window where
      // the gateway has not yet said what it calls our run. Once the ack has
      // named it there is a real id to cancel, and a param-less cancel — read
      // as "cancel the current run" on some gateways — is pure bystander risk.
      //
      // The boundary this cannot cross: a gateway that never answers
      // `chat.send` leaves the client with nothing but its own key, which a
      // gateway minting its own ids never issued. The cancel lands nowhere and
      // the frames still paint — there is no client-side fix for that, which
      // is why `onRunId` is documented as a protocol requirement. Pinned by
      // `cannot suppress a stopped run on a gateway that never answers the
      // send`, so the limitation is recorded rather than assumed away.
      //
      const haveOwnActionableId =
        observedRunIds.size > 0 ||
        ourKeysHere.some((key) => !unackedSendKeysRef.current.has(key)) ||
        // Adoption already proven for this connection: a key names its run
        // here even while its own ack is outstanding.
        (gatewayAdoptsRunKeysRef.current && ourKeysHere.length > 0);
      if (!haveOwnActionableId) cancel();
    }

    setState((prev) => {
      const hasStreamingNow = prev.messages.some(isLiveMessage);
      if (prev.processing == null && !hasStreamingNow) return prev;

      const stoppedMsg: ChatMessage = {
        id: generateMessageId('stopped'),
        role: 'assistant' as const,
        content: '_Stopped._',
        timestamp: Date.now(),
      };

      return {
        ...prev,
        processing: null,
        // Stop ends this chat's turn, so its subtask count goes with it:
        // a count left behind an explicit cancel reads as the cancel failing.
        // Other chats' subtasks are untouched.
        runningSubtasks: clearSubtasksFor(
          prev.runningSubtasks,
          prev.activeSessionId,
        ),
        messages: [...closeStreamingMessages(prev.messages), stoppedMsg],
        cancelledRunIds: rememberCancelled(prev.cancelledRunIds, stoppedRunIds),
      };
    });

    // Retired here, from ids computed BEFORE the updater ran.
    //
    // The previous version collected them inside the updater and read them
    // after `setState` returned — which works only while React runs updaters
    // eagerly, and it does not while frames are queuing updates, i.e. during
    // exactly the live run a Stop interrupts. The loop then iterated an empty
    // array and retired nothing: the same flush-versus-frame-time trap
    // `endRunPatch` documents, arrived at from the other side.
    //
    // It also snapshotted the whole cumulative `cancelledRunIds`, so every
    // Stop re-retired every run ever cancelled and pushed the recent ids the
    // late-frame guard needs out of the bounded set.
    for (const id of stoppedRunIds) retireRun(id);
  }, [retireRun]);

  const deleteSession = useCallback(
    (sessionId: string) => {
      // Sampled first: `forgetSessionLocally` clears this chat's generation
      // along with the rest of its bookkeeping, and the compare-and-swap
      // below needs it.
      const expectedGeneration = rosterGenerationRef.current.get(sessionId);
      forgetSessionLocally(sessionId);

      const account = accountIdRef.current;
      if (account != null) {
        // Record a tombstone so the deletion survives a sync merge (otherwise
        // the session resurrects from another device's index) and push it.
        historyStore.addTombstone(account, 'session', sessionId);
        syncRef.current?.queueDeleteSession(sessionId);
        // The sessions-index queue happens via the persistence effect below
        // when `state.sessions` updates (the tombstone is now in the store).
      }

      // And delete it on the AGENT, transcript included. Until now the chat
      // only left the terminal's own index: openclaw kept the conversation,
      // so a deleted chat was still in the agent's session store, still
      // counting against its row cap, still findable from any other surface.
      // The gateway writes and verifies a compressed archive before removing
      // rows, so this is recoverable by an operator but gone from the product.
      //
      // Compare-and-swap on the generation the roster reported for this chat
      // (`expectedSessionId`), so a delete that races a clear or reset of the
      // same chat on another device FAILS instead of destroying the newer
      // conversation that inherited the key. `sessions.reset` — added in this
      // same change, on the same key — is exactly what makes that race
      // reachable. Omitted when we never saw a row for the chat, where there
      // is no generation to assert and an unconditional delete is still the
      // right call.
      gatewayOrganisationWrite('delete this chat', (ws) =>
        deleteGatewaySession(ws, sessionId, expectedGeneration),
      );

      /** Set by the updater below; see the note after it. */
      let landedAfterDelete: string | null = null;
      let landedHadBuffer = false;

      setState((prev) => {
        const remaining = prev.sessions.filter(
          (s) => s.sessionId !== sessionId,
        );
        // The chat is gone; its recorded failure goes with it.
        const { [sessionId]: _dropped, ...sessionErrors } = prev.sessionErrors;
        const { [sessionId]: _droppedModel, ...sessionModels } =
          prev.sessionModels;
        if (prev.activeSessionId !== sessionId) {
          return { ...prev, sessions: remaining, sessionErrors, sessionModels };
        }
        if (remaining.length === 0) {
          const now = Date.now();
          // A draft, like any chat the user has not typed in: deleting the last
          // chat should leave an empty composer, not a fresh "New Chat" row.
          const fresh: ChatSession = {
            sessionId: generateChatSessionId(),
            folder: defaultFolder,
            title: DEFAULT_SESSION_TITLE,
            pinned: false,
            createdAt: now,
            updatedAt: now,
            isDraft: true,
          };
          return {
            ...prev,
            sessions: [fresh],
            activeSessionId: fresh.sessionId,
            messages: [],
            processing: null,
            sessionErrors,
            sessionModels,
          };
        }
        const next = [...remaining].sort(
          (a, b) => b.updatedAt - a.updatedAt,
        )[0]!;
        // Buffer first, then the store — the same order `switchSession` and
        // the roster read both use, and the third copy of this decision.
        // Reading only the store landed the user on the stale persisted copy
        // of a chat that was still streaming, with its live tail stranded in
        // memory and no indicator to explain the answer that kept growing.
        const buffered = backgroundSessionsRef.current.get(next.sessionId);
        const nextMessages =
          buffered ??
          (account != null ? paintFromCache(account, next.sessionId) : []);
        landedAfterDelete = next.sessionId;
        landedHadBuffer = buffered != null;
        return {
          ...prev,
          sessions: remaining,
          activeSessionId: next.sessionId,
          messages: nextMessages,
          // And its indicator back if it has work in flight, rather than a
          // hard-coded null that left a streaming chat looking finished.
          processing:
            backgroundProcessingRef.current.get(next.sessionId) ?? null,
          sessionErrors,
          sessionModels,
        };
      });

      // Outside the updater, for the reason the roster read's landing
      // documents: React may run one twice and discard the first result.
      if (landedAfterDelete != null) {
        if (landedHadBuffer) {
          backgroundSessionsRef.current.delete(landedAfterDelete);
        }
        backgroundProcessingRef.current.delete(landedAfterDelete);
        // Read it from the agent too. Painting from the cache alone showed
        // the "New Chat" greeting over a real conversation on any chat this
        // device had never opened.
        if (stateRef.current.messages.length === 0) {
          const readStarted = hydrateSessionFromGateway(landedAfterDelete);
          const known = stateRef.current.sessions.some(
            (s) =>
              s.sessionId === landedAfterDelete &&
              s.isDraft !== true &&
              s.isTemporary !== true,
          );
          if (readStarted && known) {
            setState((cur) =>
              cur.activeSessionId === landedAfterDelete
                ? { ...cur, loadingTranscriptFor: landedAfterDelete }
                : cur,
            );
          }
        }
      }
    },
    [
      defaultFolder,
      historyStore,
      paintFromCache,
      gatewayOrganisationWrite,
      forgetSessionLocally,
      hydrateSessionFromGateway,
    ],
  );

  const setSessionFolder = useCallback(
    (sessionId: string, customFolderId: string | undefined) => {
      // Stamp `folderUpdatedAt` (NOT `updatedAt` — a move isn't chat
      // activity and must not resurface the chat in Recents) so the sync
      // merge propagates the move instead of losing it to any record
      // that's fresher by `updatedAt` on another device.
      const folderUpdatedAt = Date.now();
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === sessionId
            ? customFolderId == null
              ? (() => {
                  const { customFolderId: _drop, ...rest } = s;
                  return { ...rest, folderUpdatedAt } as ChatSession;
                })()
              : { ...s, customFolderId, folderUpdatedAt }
            : s,
        ),
      }));
      // The gateway files a chat by group NAME (`category`), not by id, and
      // `null` is what unfiles it — an omitted field means "leave it alone".
      //
      // The draft guard matters most here: the sidebar's per-folder compose
      // button calls `createSession` and then this, so without it every
      // "+ New Chat" inside a folder claimed a gateway row — and the row came
      // back carrying a `category`, which `isUntitledRow` does not hide.
      if (isDraftSession(sessionId)) return;
      const category = safeCategory(folderNameFor(customFolderId));
      // Nothing safe to say — see `safeCategory`. The move is applied locally
      // and converges on the next connect.
      if (!('category' in category)) return;
      gatewayOrganisationWrite('move this chat', (ws) =>
        patchSessionOrganisation(ws, sessionId, category),
      );
    },
    [gatewayOrganisationWrite, folderNameFor, isDraftSession, safeCategory],
  );

  const createCustomFolder = useCallback(
    (name: string): string => {
      const trimmed = name.trim();
      if (trimmed.length === 0) return '';
      const folder: CustomFolder = {
        // The NAME as the id, which is the id the gateway's own catalog
        // gives it. A random one churned: the first roster read re-ids every
        // folder to its name, and rows the read re-projects were rewritten to
        // match while rows carried over locally — drafts, and chats a partial
        // read could not speak for — kept the random id and pointed at a
        // folder that no longer existed. So "+ New Chat" inside a folder the
        // user had just made jumped out of it the moment the next read
        // landed. Minting the id the projection will mint removes the churn
        // rather than trying to follow it.
        id: trimmed,
        name: trimmed,
        createdAt: Date.now(),
      };
      setState((prev) => ({
        ...prev,
        customFolders: prev.customFolders.some((f) => f.id === folder.id)
          ? prev.customFolders
          : [...prev.customFolders, folder],
      }));
      // `sessions.groups.put` REPLACES the ordered catalog, so creating one
      // group means sending every name — and every name means every name the
      // GATEWAY has, not only the ones this device knows about.
      //
      // Built from local state alone, this deleted groups: after a failed
      // catalog read (which deliberately leaves folders untouched), or when
      // another device had added one, or when two folders were created before
      // a render, the names sent were a subset and the put replaced the
      // catalog with it. Unioned with what the gateway last reported, so a
      // create can add but never silently remove.
      const names = [
        ...new Set([
          ...(gatewayGroupNamesRef.current ?? []),
          ...stateRef.current.customFolders.map((f) => f.name),
          trimmed,
        ]),
      ];
      gatewayOrganisationWrite('create this folder', async (ws) => {
        await putSessionGroups(ws, names);
        // Now known to be on the gateway, which is what lets a chat be filed
        // under it (`safeCategory`). Recorded on SUCCESS only: this call is
        // the one that can be refused, and a folder that is on screen but
        // not on the gateway must not be used as a `category`.
        const known = gatewayGroupNamesRef.current ?? new Set<string>();
        for (const n of names) known.add(n);
        gatewayGroupNamesRef.current = known;
      });
      return folder.id;
    },
    [gatewayOrganisationWrite],
  );

  const renameCustomFolder = useCallback(
    (folderId: string, name: string) => {
      const trimmed = name.trim();
      if (trimmed.length === 0) return;
      const previous = stateRef.current.customFolders.find(
        (f) => f.id === folderId,
      )?.name;
      setState((prev) => ({
        ...prev,
        customFolders: prev.customFolders.map((f) =>
          f.id === folderId ? { ...f, name: trimmed } : f,
        ),
      }));
      // Groups are keyed by name, so a rename needs the OLD one. The gateway
      // repoints every member session itself, including archived ones — work
      // the terminal used to do a chat at a time.
      if (previous != null && previous !== trimmed) {
        gatewayOrganisationWrite('rename this folder', async (ws) => {
          await renameSessionGroup(ws, previous, trimmed);
          const known = gatewayGroupNamesRef.current;
          if (known != null) {
            known.delete(previous);
            known.add(trimmed);
          }
        });
      }
    },
    [gatewayOrganisationWrite],
  );

  const pinCustomFolder = useCallback((folderId: string, pinned: boolean) => {
    setState((prev) => ({
      ...prev,
      customFolders: prev.customFolders.map((f) => {
        if (f.id !== folderId) return f;
        if (pinned) return { ...f, pinned: true };
        const { pinned: _drop, ...rest } = f;
        return rest as CustomFolder;
      }),
    }));
  }, []);

  const deleteCustomFolder = useCallback(
    (folderId: string) => {
      const account = accountIdRef.current;
      const name = stateRef.current.customFolders.find(
        (f) => f.id === folderId,
      )?.name;
      if (account != null) {
        // Record a folder tombstone so the deletion survives a sync merge
        // — without one, `mergeIndex` resurrects the folder (empty) from
        // any other device's index. Uploaded via the persistence effect's
        // `queueIndex` (tombstones ride in the index doc).
        historyStore.addTombstone(account, 'folder', folderId);
      }
      // The unfiling of member sessions is a folder-assignment change like
      // any other — stamp it so it wins sync merges (see setSessionFolder).
      const folderUpdatedAt = Date.now();
      setState((prev) => ({
        ...prev,
        customFolders: prev.customFolders.filter((f) => f.id !== folderId),
        sessions: prev.sessions.map((s) => {
          if (s.customFolderId !== folderId) return s;
          const { customFolderId: _drop, ...rest } = s;
          return { ...rest, folderUpdatedAt } as ChatSession;
        }),
      }));
      // Its own call rather than a `groups.put` omission: the put REFUSES to
      // drop a group that still has members, and the delete is what unfiles
      // them server-side. Member chats survive, as they do locally.
      if (name != null) {
        gatewayOrganisationWrite('delete this folder', (ws) =>
          deleteSessionGroup(ws, name),
        );
      }
    },
    [historyStore, gatewayOrganisationWrite],
  );

  const respondToApproval = useCallback(
    (approvalId: string, approved: boolean) => {
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.approval?.approvalId === approvalId
            ? {
                ...msg,
                approval: {
                  ...msg.approval,
                  state: approved ? ('approved' as const) : ('denied' as const),
                },
              }
            : msg,
        ),
      }));
      wsRef.current
        ?.request('approval.respond', { approvalId, approved })
        .catch(reportError);
    },
    [reportError],
  );

  const request: AgentActions['request'] = useCallback(
    async <T,>(
      method: string,
      params?: Record<string, unknown>,
    ): Promise<T> => {
      if (wsRef.current == null) {
        throw new Error('Not connected');
      }
      return wsRef.current.request<T>(method, params);
    },
    [],
  );

  const requestPairing = useCallback(async () => {
    if (wsRef.current == null) {
      throw new Error('Not connected');
    }
    return wsRef.current.requestPairing();
  }, []);

  const sendUiEvent = useCallback(
    (params: {
      readonly surfaceId: string;
      readonly componentId?: string;
      readonly event: string;
      readonly data?: Record<string, unknown>;
    }) => {
      wsRef.current?.sendUiEvent(params);
    },
    [],
  );

  const dismissFullTerminalUi = useCallback(() => {
    setState((prev) => ({ ...prev, fullTerminalUi: null }));
  }, []);

  const dismissSessionError = useCallback((sessionId?: string) => {
    setState((prev) => {
      const target = sessionId ?? prev.activeSessionId;
      if (target == null || prev.sessionErrors[target] == null) return prev;
      const { [target]: _dropped, ...rest } = prev.sessionErrors;
      return { ...prev, sessionErrors: rest };
    });
  }, []);

  const setSelectedModel = useCallback(
    (modelId: string) => {
      const sessionId = activeSessionIdRef.current;
      const isAuthenticated = statusRef.current === 'authenticated';
      const ws = wsRef.current;

      // Re-picking what is already selected would re-send, re-verify and
      // append a SECOND identical confirmation line into persisted history —
      // and the picker offers the checked entry as a live target.
      //
      // Only a no-op when the GATEWAY is known to be on that same id too. A
      // session whose override is a stale/legacy id merely DISPLAYS as its
      // catalog equivalent (reconcileModelIdWithCatalog normalizes it), so
      // re-picking the shown model is the user's one way to realign the
      // gateway — swallowing it would strand them with sends that keep
      // failing against a model the picker claims is active.
      const gatewayKnownId =
        sessionId != null
          ? stateRef.current.sessionModels[sessionId]
          : undefined;
      if (
        selectedModelIdRef.current === modelId &&
        gatewayKnownId === modelId
      ) {
        return;
      }

      // Optimistic: the picker flips immediately (via the derivation) and the
      // gateway is verified below, bouncing back if the switch didn't land.
      // A pick also invalidates any read-back already in flight — the user has
      // just told us the answer, so a describe that started before the click
      // must not land on top of it. That single bump replaces the per-session
      // ownership ref and the unplaced-pick flag this used to need.
      sessionModelFetchSeqRef.current += 1;
      if (sessionId != null) {
        // This chat's pick counter, which is what invalidates an older
        // write's bounce (see `writeSessionModel`).
        modelPickSeqRef.current.set(
          sessionId,
          (modelPickSeqRef.current.get(sessionId) ?? 0) + 1,
        );
        applySessionModel(sessionId, modelId);
      } else {
        // No session yet, so no slot: park it where the derivation ranks it
        // above any resolved default, and `sendMessage` places it for real.
        setState((prev) => ({ ...prev, pendingModelId: modelId }));
        pendingModelIdRef.current = modelId;
      }

      // Record the intent before any bail-out below: an explicit pick is what
      // a fresh session inherits, and a pick made while offline (or with no
      // session yet) is still the model the user asked for.
      lastUsedModelIdRef.current = modelId;

      if (!isAuthenticated || ws == null) return;

      // With no session yet there is nothing to write to. `sessions.patch`
      // needs a key, and the key `sendMessage` will mint for the first
      // message does not exist yet — so there is no row to move and nothing
      // to verify. (Before this change the write was a `/model` chat message,
      // which was worse than a no-op: with no key it landed on the gateway's
      // MAIN session, moving a model the user would never run and producing a
      // "Model switched to X." line for a session the chat never used.) The
      // pick is held as `pendingModelId` (+ last-used) and applied by
      // `sendMessage` when it mints the real session.
      if (sessionId == null) return;

      // A DRAFT is that same case wearing a session id. `createSession` sets
      // `activeSessionIdRef` synchronously, so a pick made on a fresh
      // "+ New Chat" arrives here with a non-null id and used to fall straight
      // through to the write — and `sessions.patch` CREATES the row, which is
      // the exact leak the draft lifecycle exists to prevent. Worse than what
      // it replaced: the next roster read projects that row back carrying no
      // `isDraft`, so a chat the user opened and abandoned without typing
      // becomes a permanent "New Chat" in the sidebar.
      //
      // Display only, then. `applySessionModel` above has already moved the
      // picker, and `sendMessage` publishes `sessionModels[targetId]` on the
      // first send — so the model the user chose is what the first turn runs,
      // with nothing claimed on the gateway until there is a turn to run.
      // `isDraftSession`, not a state read, for the same same-batch reason
      // the organisation writes use it: a caller that creates the chat and
      // picks in one handler has not rendered in between, so the row is not
      // in `stateRef` yet.
      if (isDraftSession(sessionId)) return;

      // Don't start a chat that has no messages: the confirmation line below
      // is skipped while the transcript is empty. Sampled here AND re-checked
      // where it commits — a send in between must not collect a line about a
      // switch made before it, and a chat cleared in between must not be
      // reopened by one.
      const chatWasUnsent = stateRef.current.messages.length === 0;

      // `sessions.patch { model }` is the write path — it touches no
      // transcript, so nothing is muted and nothing has to be swept. The id
      // already carries the routing-provider prefix (`dt-a/`, `dt-o/`) so it
      // goes as-is. The patch's own response confirms or bounces the pick
      // (see `writeSessionModel`); the confirmation LINE is what
      // `confirmInChat` gates, and only a chat that is already a
      // conversation gets one.
      writeSessionModel(ws, modelId, sessionId, {
        kind: 'pick',
        confirmInChat: !chatWasUnsent,
        // Sampled before `applySessionModel` above moved the picker, so a
        // refusal can put it back.
        previousModelId: gatewayKnownId ?? null,
      });
    },
    [applySessionModel, writeSessionModel, isDraftSession],
  );

  // `selectedModelId` is derived, so it is stitched in here rather than stored.
  const exposedState = useMemo<AgentState>(
    () => ({ ...state, selectedModelId }),
    [state, selectedModelId],
  );

  // Sidecar auth (model catalog, session sync, personalization). The host's
  // forward override wins — e.g. the dime cloud routes sidecar calls through
  // its web-api with a DIME JWT, so the browser never holds the gateway
  // credential. Without an override, token-mode gateway auth with the stored
  // agent token (standalone pairing). Null ⇒ no credential right now (session
  // sync treats that as transient and retries; personalization surfaces a
  // retryable error).
  const sidecarAuthHeaders = useCallback(
    (account: string): Readonly<Record<string, string>> | null => {
      const sidecar = configRef.current.sidecar;
      if (sidecar != null) {
        const headers = sidecar.getAuthHeaders();
        if (headers == null) return null;
        return { ...headers, 'X-Sync-User': SYNC_USER };
      }
      const token = urlStore.getAgentToken(account);
      if (token == null || token === '') return null;
      return { Authorization: `Bearer ${token}`, 'X-Sync-User': SYNC_USER };
    },
    [urlStore],
  );

  // The sidecar HTTP primitive handed to the panes (`AgentActions.sidecarRequest`).
  // Same route and credential as the catalog fetch and the sync client: the
  // host's forward when configured, else the origin derived from the gateway
  // the session is currently targeting. Both are read at call time, so one
  // instance serves across account and gateway switches.
  const sidecarRequest = useMemo(
    () =>
      createSidecarRequest({
        getBase: () =>
          configRef.current.sidecar?.baseUrl ??
          deriveHttpBase(agentUrlRef.current),
        getAuthHeaders: () => {
          const account = accountIdRef.current;
          return account == null ? null : sidecarAuthHeaders(account);
        },
      }),
    [sidecarAuthHeaders],
  );

  const actions = useMemo<AgentActions>(
    () => ({
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      loadOlderMessages,
      retryTranscriptRead,
      respondToApproval,
      request,
      sidecarRequest,
      requestPairing,
      sendUiEvent,
      dismissFullTerminalUi,
      dismissSessionError,
      createSession,
      switchSession,
      renameSession,
      pinSession,
      deleteSession,
      setSessionFolder,
      createCustomFolder,
      renameCustomFolder,
      pinCustomFolder,
      deleteCustomFolder,
      stopProcessing,
      setSelectedModel,
    }),
    [
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      loadOlderMessages,
      retryTranscriptRead,
      respondToApproval,
      request,
      sidecarRequest,
      requestPairing,
      sendUiEvent,
      dismissFullTerminalUi,
      dismissSessionError,
      createSession,
      switchSession,
      renameSession,
      pinSession,
      deleteSession,
      setSessionFolder,
      createCustomFolder,
      renameCustomFolder,
      pinCustomFolder,
      deleteCustomFolder,
      stopProcessing,
      setSelectedModel,
    ],
  );

  // React to account switches: tear down the current session and
  // auto-reconnect if the new account has a stored or resolvable URL.
  const hydratedAccountRef = useRef<string | null>(null);

  // Native session-sync client for the hydrated account (null when the gateway
  // URL is unusable). Disposed + recreated on account/gateway change.
  const syncRef = useRef<TerminalSync | null>(null);
  const hydrate = useCallback(() => {
    const account = accountIdRef.current;
    // Attachment bytes belong to one account. The preview cache is keyed by
    // route, which says nothing about whose file it is, so without this one
    // user's pictures stayed in memory after a switch and a workspace path
    // could resolve to the other account's image.
    if (hydratedAccountRef.current !== account) clearAttachmentPreviews();

    // Signed-out: reset everything.
    if (account == null) {
      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      hydratedAccountRef.current = null;
      setState({ ...INITIAL_AGENT_STATE });
      return;
    }

    // Already hydrated this account — config updates that arrive later must
    // not tear down the live session.
    if (hydratedAccountRef.current === account) {
      return;
    }

    const cfg = configRef.current;

    void (async () => {
      // localStorage is authoritative for the active browser; fall back to
      // explicit gatewayUrl / the host resolver.
      const storedUrl = urlStore.getAgentUrl(account);
      const resolved =
        cfg.gatewayUrl ?? (await cfg.resolveUrl?.(account)) ?? null;
      const effectiveUrl = storedUrl ?? resolved;

      // Re-check the account hasn't changed underneath the await.
      if (accountIdRef.current !== account) return;

      if (effectiveUrl == null || effectiveUrl.length === 0) {
        if (wsRef.current != null) {
          wsRef.current.close();
          wsRef.current = null;
        }
        hydratedAccountRef.current = null;
        setState({ ...INITIAL_AGENT_STATE });
        return;
      }

      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      hydratedAccountRef.current = account;
      // Per-account/per-gateway model state must not leak across a switch. The
      // keyed model state itself is reset by the `INITIAL_AGENT_STATE` spread
      // below; these are the out-of-band pieces.
      lastUsedModelIdRef.current = null;
      pendingModelIdRef.current = null;
      gatewayDefaultsFetchedConnectionRef.current = false;
      // Session ids are per account, so a retitle guard held across a switch
      // is a guard on some other account's chat.
      userTitledSessionsRef.current.clear();
      rosterGenerationRef.current.clear();
      // Group names are per account too, and this one AUTHORISES writes: left
      // set, account A's folder names let a `category` patch through on
      // account B until B's own catalog read lands.
      gatewayGroupNamesRef.current = null;
      modelPickSeqRef.current.clear();
      requestedOffsetsRef.current.clear();
      foreignHydratePendingRef.current.clear();
      foreignHydrateFailuresRef.current = 0;
      // Invalidate anything the OLD account still has in flight: a `/model`
      // verification (which would otherwise describe the old session id on
      // the new account's socket) and any read-back, which would land on the
      // new account's freshly reset state.
      sessionModelFetchSeqRef.current += 1;

      // Hydrate sessions + the active session's messages from localStorage —
      // as a CACHE for the first paint, nothing more. The roster read that
      // follows authentication is what establishes the chat list.
      //
      // This used to run `recoverOrphanedSessions` too: a scan for message
      // blobs with no index row, rebuilt as sessions named "Recovered chat".
      // That belonged to the world where localStorage WAS the chat list, and
      // it was load-bearing there — the persisted-session validator was
      // rejecting the whole index on load, and this is what hid it for
      // months. It has no job now: if the cached index is corrupt, stale or
      // missing, the gateway says so a second later, and a row invented from
      // a local blob is a row the gateway does not have. That invention is
      // what made a chat flap in and out of the sidebar under a name it
      // never had.
      const hydratedSessions =
        historyStore.migrateLegacyHistoryIfNeeded(account);
      // The retired mixed-transcript blobs. Nothing reads them, and left in
      // place they crowd the paint cache out of the origin's storage quota.
      historyStore.purgeLegacyTranscripts(account);
      const uiState = historyStore.loadHistoryUiState(account);
      const activeSessionId = resolveActiveSession(
        hydratedSessions,
        uiState.activeSessionId,
      );
      const persistedMessages =
        activeSessionId != null ? paintFromCache(account, activeSessionId) : [];
      const persistedFolders = historyStore.loadCustomFolders(account);
      const { folders: hydratedFolders, recovered: foldersRecovered } =
        historyStore.recoverOrphanedFolders(hydratedSessions, persistedFolders);
      if (foldersRecovered) {
        historyStore.saveCustomFolders(account, hydratedFolders);
      }
      // The picker starts EMPTY. The gateway's `/api/models` is the only
      // source of models — there is no built-in fallback catalog — and the
      // SELECTION is not persisted UI-side at all: each session's model lives
      // on the gateway (its `modelOverride`) and is read back silently via
      // `sessions.describe` once the connection authenticates. `modelsStatus`
      // holds the composer's picker slot until the live catalog resolves.
      const paired = deriveIsPaired({
        account,
        url: effectiveUrl,
        supportsPairingRole: cfg.supportsPairingRole,
        hasStoredToken: urlStore.getAgentToken(account) != null,
        fallbackPairingToken: cfg.fallbackPairingToken,
      });
      setState({
        ...INITIAL_AGENT_STATE,
        agentUrl: effectiveUrl,
        isPaired: paired,
        sessions: hydratedSessions,
        // Same for the chat that reopens: nothing cached for it means the
        // read is what will fill it, and until then this is a wait rather
        // than an empty conversation.
        //
        // Gated on `paired` for the same reason `rosterStatus` is, a few
        // lines below: an unpaired host opens no socket, so no read will ever
        // settle this and the chat would say "Loading conversation…" for as
        // long as the tab is open. A wait has to have something to wait for.
        loadingTranscriptFor:
          paired && activeSessionId != null && persistedMessages.length === 0
            ? activeSessionId
            : null,
        // `hydratedSessions` is the CACHE, so the sidebar is not yet showing
        // an answer — even when it is empty. Set here rather than when the
        // read starts, because the window that showed "no chats yet" to a
        // user who has chats opens at mount and closes a second after
        // authentication, and the read is only the back half of it.
        //
        // Only when a socket is actually going to be opened (see the `paired`
        // gate below). An unpaired host opens none, so no read will ever
        // settle this — and `loading` with nothing coming is a shimmer that
        // never ends. `idle` is the honest answer there: nothing has been
        // asked because there is nobody to ask.
        rosterStatus: paired ? 'loading' : 'idle',
        customFolders: hydratedFolders,
        activeSessionId,
        messages: persistedMessages,
      });

      if (storedUrl == null) {
        // Cache the URL locally so subsequent reads go through the store.
        urlStore.setAgentUrl(account, effectiveUrl);
      }
      // Only open the socket when paired. Pairing-role hosts count a bare URL
      // as paired (the gateway opens a pairing role for `node.pair.request`);
      // token-only hosts need a token first — otherwise we'd fire a handshake
      // that just fails auth, so the login screen shows instead.
      if (paired) {
        connect(effectiveUrl);
      }

      // Live model catalog is fetched off the `authenticated` status (see the
      // model-fetch effect below), NOT here — the native `/api/models` is
      // token-gated like `/sync/v1`, and a fresh pairing only seeds the gateway
      // token after the handshake resolves.

      // Native session-sync CLIENT only. The restore (snapshot pull) is NOT
      // run here — it's driven by the connection reaching `authenticated` (see
      // the restore effect below), because on a fresh pairing the gateway
      // token isn't seeded until the handshake resolves, so a restore fired
      // now would no-op on missing auth and never retry. Writes queue against
      // this client as soon as it exists.
      syncRef.current?.dispose();
      syncRef.current = createSessionSync({
        store: historyStore,
        prefsStore,
        // A cross-device write that loses a 412 comes back merged HERE, not
        // through hydration — adopt it or the running app keeps rendering (and
        // seeding from) the value that just lost, until the next reconnect.
        //
        // Guarded on the account the client was built for, mirroring the
        // hydration path. `dispose()` only sets a flag and clears timers; it
        // does not abort an in-flight drain, and `resolveAndRetry` calls back
        // unconditionally — so switching accounts mid-resolve landed account
        // A's settings in account B's live state, and B's next edit then wrote
        // them to B's own storage and gateway doc.
        onPreferencesMerged: (doc) => {
          if (accountIdRef.current !== account) return;
          adoptPreferencesDoc(doc);
        },
        // Same account guard as above, for the same reason. Adopted only when
        // the part the UI shows actually moved: this fires on every queued
        // write, and the pending count ticks on each streaming delta.
        onStatusChange: (status) => {
          if (accountIdRef.current !== account) return;
          setState((prev) => {
            const cur = prev.syncStatus;
            if (
              cur != null &&
              cur.lastError === status.lastError &&
              cur.droppedSessionIds.length ===
                status.droppedSessionIds.length &&
              cur.droppedSessionIds.every(
                (id, i) => id === status.droppedSessionIds[i],
              )
            ) {
              return prev;
            }
            return {
              ...prev,
              syncStatus: {
                lastError: status.lastError,
                droppedSessionIds: status.droppedSessionIds,
              },
            };
          });
        },
        accountId: account,
        agentUrl: effectiveUrl,
        baseUrl: configRef.current.sidecar?.baseUrl,
        getAuthHeaders: () => sidecarAuthHeaders(account),
      });
    })().catch(reportError);
  }, [
    connect,
    urlStore,
    historyStore,
    paintFromCache,
    prefsStore,
    adoptPreferencesDoc,
    reportError,
    sidecarAuthHeaders,
  ]);

  // Mirror preference edits to the gateway so they reach the user's other
  // devices. The sync client debounces and retries, and the doc carries
  // per-field stamps, so a burst of edits collapses into one PUT and a
  // concurrent edit elsewhere merges rather than clobbers.
  //
  // Upload local edits, and nothing else.
  //
  // `setPreference` is the only local mutator in the app, so the provider tells
  // us directly which doc changes were typed by the user. What this replaced
  // was the same question answered backwards: watch every doc change, diff it
  // against a "last synced" baseline, and infer. That needed the baseline
  // seeded on load (or the first read off disk PUT the whole document and
  // burned a rev every launch), reset on account switch (or the incoming
  // account's doc read as an edit of the outgoing one), and re-recorded on
  // every gateway arrival (or a hydration bounced straight back at the
  // gateway that had just sent it). Three guards, each protecting against a
  // case that simply cannot arise here, because an arrival never calls this.
  //
  // An edit made before the sync client exists is dropped, as it was before:
  // `hydrateFromRemote` uploads whenever the merge moved something, which
  // covers exactly that — see `settings-upload-discipline.test.tsx`.
  //
  // A device-local field is not an edit this document has anything to say
  // about, so it is skipped here rather than stripped downstream: stripping
  // alone would still PUT a byte-identical doc and burn a rev per toggle.
  useEffect(
    () =>
      onLocalEdit((doc, key) => {
        if (isDeviceLocalPreference(key)) return;
        syncRef.current?.queueUiState({
          preferences: toSyncedPreferencesDoc(doc),
        });
      }),
    [onLocalEdit],
  );

  // Hydrate on mount + whenever the account or gateway config changes.
  useEffect(() => {
    hydrate();
  }, [config.accountId, config.gatewayUrl, hydrate]);

  // Re-hydrate when the host signals an account change.
  useEffect(() => {
    const onAccountChange = config.onAccountChange;
    if (onAccountChange == null) return undefined;
    return onAccountChange(() => {
      hydrate();
    });
  }, [config.onAccountChange, hydrate]);

  // Restore from the gateway once per authenticated connection. This is the
  // core of the rework: restore is driven by the `authenticated` status — NOT
  // fired at mount — so the gateway token is guaranteed seeded (a fresh
  // pairing only seeds it after the handshake resolves). The ref resets
  // whenever we leave `authenticated`, so a reconnect re-restores; a `null`
  // result (transient failure or a gateway without `/sync/v1`) retries a
  // bounded number of times before settling to local-only.
  const restoredConnectionRef = useRef(false);
  useEffect(() => {
    if (state.status !== 'authenticated') {
      restoredConnectionRef.current = false;
      return undefined;
    }
    const account = accountId;
    if (account == null) return undefined;
    if (hydratedAccountRef.current !== account) return undefined;
    if (restoredConnectionRef.current) return undefined;
    const sync = syncRef.current;
    if (sync == null) return undefined;
    restoredConnectionRef.current = true;

    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      // The merge + state application live in `applyRemoteHydration` (shared
      // with the foreign-frame trigger); this effect only owns the bounded
      // retry policy for the connect-time restore.
      applyRemoteHydration()
        .then((result) => {
          if (cancelled || result != null) return;
          attempt += 1;
          if (attempt < RESTORE_MAX_ATTEMPTS) {
            timer = setTimeout(run, RESTORE_RETRY_BASE_MS * attempt);
          }
        })
        .catch(reportError);
    };
    run();
    return () => {
      cancelled = true;
      if (timer != null) clearTimeout(timer);
    };
  }, [state.status, accountId, reportError, applyRemoteHydration]);

  // Live model catalog: fetch once per authenticated connection, mirroring the
  // restore effect — `/api/models` is token-gated like `/sync/v1`, so the
  // gateway token must be seeded (only true after the handshake). Authenticates
  // the same way as the sync client. This is the ONLY source of models, so a
  // failure is retried on a bounded schedule (see the MODELS_* constants) — a
  // cold sidecar 502s and a token can seed a beat after `authenticated`, and a
  // single empty result used to leave the user unable to switch models for the
  // whole connection. `modelsStatus` tracks the outcome so the composer can
  // show a placeholder rather than silently omitting the picker.
  const modelsFetchedConnectionRef = useRef(false);
  useEffect(() => {
    if (state.status !== 'authenticated') {
      modelsFetchedConnectionRef.current = false;
      return undefined;
    }
    const account = accountId;
    if (account == null || hydratedAccountRef.current !== account)
      return undefined;
    if (modelsFetchedConnectionRef.current) return undefined;
    const url = state.agentUrl;
    if (url == null) return undefined;
    // No resolvable HTTP base ⇒ the fetch can never fire (it would return `[]`
    // without a request). Report it as unavailable rather than burning the
    // retry budget on timers that cannot succeed.
    const baseUrl = configRef.current.sidecar?.baseUrl;
    if (baseUrl == null && deriveHttpBase(url) == null) {
      setState((prev) => ({ ...prev, modelsStatus: 'unavailable' }));
      return undefined;
    }
    modelsFetchedConnectionRef.current = true;
    setState((prev) => ({ ...prev, modelsStatus: 'loading' }));

    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleRetry = () => {
      attempt += 1;
      if (attempt >= MODELS_MAX_ATTEMPTS) {
        // Budget exhausted. Say so instead of leaving the placeholder spinning
        // forever — the picker stays absent, but the UI stops implying it is
        // about to arrive.
        setState((prev) => ({ ...prev, modelsStatus: 'unavailable' }));
        return;
      }
      const delay = Math.min(
        MODELS_RETRY_BASE_MS * attempt,
        MODELS_RETRY_MAX_MS,
      );
      timer = setTimeout(run, delay);
    };

    // An arrow, not a hoisted declaration: the effect's guards above narrow
    // `account`/`url` away from null, and TS only keeps that narrowing for
    // closures created after the guards.
    const run = (): void => {
      fetchGatewayModels(url, {
        baseUrl,
        getAuthHeaders: () => sidecarAuthHeaders(account),
      })
        .then((live: readonly ModelInfo[]) => {
          if (cancelled || accountIdRef.current !== account) return;
          if (live.length === 0) {
            scheduleRetry();
            return;
          }
          // The native /api/models reads openclaw.json, whose labels are the
          // region-stripped id (e.g. "anthropic.claude-opus-4-8") — not the
          // friendly "Opus 4.8". Prettify via the native id→label table,
          // keeping the endpoint's label for ids it doesn't know
          // (defaultModelLabel echoes the id back when unknown).
          const models = live.map((m) => {
            const pretty = defaultModelLabel(m.id);
            return pretty !== m.id ? { ...m, label: pretty } : m;
          });
          // Adopt the catalog for DISPLAY only — nothing is pushed to the
          // gateway here, and nothing needs to be reconciled into a selection:
          // `selectedModelId` is derived from `models`, so simply storing the
          // catalog re-resolves the picker. This block used to hand-roll the
          // reconcile-or-fall-back-to-a-default chain that the derivation now
          // states once.
          setState((prev) => ({ ...prev, models, modelsStatus: 'ready' }));
        })
        .catch(reportError);
    };

    run();
    return () => {
      cancelled = true;
      if (timer != null) clearTimeout(timer);
    };
  }, [
    state.status,
    state.agentUrl,
    accountId,
    reportError,
    sidecarAuthHeaders,
  ]);

  // Per-session model: the gateway is the single source of truth (each
  // session's `modelOverride` lives on its disk), so on hydrate, session
  // switch, and every (re)connect the ACTIVE session's model is read back
  // silently via the `sessions.describe` RPC — never through chat, so
  // nothing shows in the transcript. A session with no gateway row yet (a
  // fresh chat before its first send) is SEEDED, in the order
  // `resolveSeedModelId` documents: a model already decided for that session,
  // then a pick made before it existed, then the user's configured default
  // (Settings → Model), then the last-used model — the in-memory last-known
  // pick, or, after a reload, the most recently updated previous session's
  // model read from the gateway. Only a user with no default and no previous
  // sessions falls back to the gateway default (`sessions.list` → `defaults`,
  // fetched once per connection).
  useEffect(() => {
    // Bumped BEFORE the early returns, unlike the model token below.
    //
    // An effect run that bails still means "whatever is in flight is now out
    // of date" — and the runs that bail are exactly the interesting ones: the
    // connection dropped, or the account is mid-hydration. Bumping only after
    // the guards left the reconnect half of this token's promise false, and
    // the gap is reachable: the socket's error listener sets `status` to
    // `error` WITHOUT closing the socket, and pending requests are only
    // rejected on close — so a describe in flight when a transient error
    // lands is answered by the failed connection, and repaints the reading
    // the clear layout effect had just wiped.
    //
    // The model token deliberately stays below the guards: it is consumed by
    // `applyModelId`, which cannot run from a bailed effect anyway, and
    // moving it would change which pick wins a race this PR does not touch.
    const usageToken = ++sessionUsageSeedSeqRef.current;
    const isUsageStale = (): boolean =>
      sessionUsageSeedSeqRef.current !== usageToken;

    if (state.status !== 'authenticated') {
      gatewayDefaultsFetchedConnectionRef.current = false;
      return;
    }
    const account = accountId;
    if (account == null || hydratedAccountRef.current !== account) return;
    const ws = wsRef.current;
    if (ws == null) return;
    const sessionId = state.activeSessionId;
    // Single staleness test for everything this effect resolves: a newer
    // effect run OR a user pick bumps the token, and both mean "your answer is
    // out of date". This replaced three separate guards (a per-session
    // ownership ref, an unplaced-pick flag, and a pick-sequence snapshot),
    // each of which had to be checked at the right moment by hand.
    const token = ++sessionModelFetchSeqRef.current;
    const isStale = (): boolean => sessionModelFetchSeqRef.current !== token;

    const applyModelId = (modelId: string | null): void => {
      if (modelId == null || isStale()) return;
      if (sessionId == null) {
        // No session to attribute it to: this is the agent's SHARED main
        // session, so it is display-only — it ranks below a pending pick in
        // the derivation and never becomes what a chat runs.
        setState((prev) =>
          prev.mainSessionModelId === modelId
            ? prev
            : { ...prev, mainSessionModelId: modelId },
        );
        return;
      }
      applySessionModel(sessionId, modelId);
      lastUsedModelIdRef.current = modelId;
    };

    const fetchGatewayDefault = (): void => {
      const cached = stateRef.current.gatewayDefaultModelId;
      if (cached != null) {
        // Already known: the derivation is already using it.
        return;
      }
      if (gatewayDefaultsFetchedConnectionRef.current) return;
      gatewayDefaultsFetchedConnectionRef.current = true;
      ws.request<SessionsListDefaultsResult>('sessions.list', { limit: 1 })
        .then((payload) => {
          const composed = composeSessionModelId(payload?.defaults);
          if (composed != null && !isStale()) {
            setState((prev) => ({ ...prev, gatewayDefaultModelId: composed }));
          }
          // A valid answer carrying no `defaults` means this gateway HAS no
          // default — the flag stays set so we don't re-ask every switch.
        })
        .catch((err: unknown) => {
          // Transient failure, not "no defaults": re-arm so a later session
          // switch or reconnect can try again. Leaving it latched disabled the
          // lookup for the rest of the connection after a single blip.
          gatewayDefaultsFetchedConnectionRef.current = false;
          logModelDebug('model-defaults-failed', { error: String(err) });
        });
    };

    // Resolve the model a gateway-unknown session should run, and DISPLAY it.
    //
    // No write here, deliberately: `sessions.patch` creates the session row,
    // so writing on open claimed a gateway session for a chat that may never
    // be typed in. `sendMessage` publishes it with the first message instead
    // — see `seedTargetModel` — and a refusal is reported from that write's
    // own answer.
    const seedFreshSession = (seedId: string): void => {
      // A pick landed while this seed was resolving: the pick wins outright.
      // Gating only the push (and still applying the display) let a late seed
      // flip the picker away from a pick the user had already seen confirmed.
      if (isStale()) return;
      // Seeding WRITES, so it must never republish a dead id. Both sources are
      // raw gateway values — the in-memory last-used, or a previous session's
      // row — so one session carrying a retired routing prefix (the case the
      // deleted localStorage migration used to repair) would otherwise be
      // copied onto every new chat, where each send then fails with
      // "No API key found for provider <retired>".
      //
      // Resolving through the catalog repairs that in the normal case, since
      // the match is normalized: a `dtmodel/…` row seeds as the live `dt-a/…`
      // id. A seed the catalog no longer carries at all is not propagated —
      // the gateway default is the safe inheritance. An empty catalog means we
      // have no opinion yet, so the raw id stands and the next read corrects.
      const catalog = modelsRef.current;
      let liveSeedId = seedId;
      if (catalog.length > 0) {
        const resolved = resolveModelInCatalog(catalog, seedId);
        if (resolved == null) {
          logModelDebug('model-seed-dropped-unknown', { sessionId, seedId });
          fetchGatewayDefault();
          return;
        }
        liveSeedId = resolved.id;
      }

      // DISPLAY ONLY. The write is deferred to the first send.
      //
      // `sessions.patch` creates the session when it does not exist, so
      // writing the inherited model when a chat is merely OPENED claimed a
      // gateway row for a conversation that may never happen: "+ New Chat"
      // with a non-default model left a row behind every time, and the agent
      // accumulated sessions nobody had spoken to. The write is only load-
      // bearing at the moment a turn is about to run, and `sendMessage` does
      // it there — see `seedTargetModel`.
      //
      // Nothing is lost by waiting: the picker shows the resolved model from
      // here, and the user has to type before a turn can run on the wrong
      // one.
      applyModelId(liveSeedId);
    };

    // Fresh session (no gateway row): seed it.
    //  1. `resolveSeedModelId()` — pre-session pick, then the user's configured
    //     default, then this page load's last pick.
    //  2. The most recently updated OTHER session's model, from the gateway.
    //  3. No previous sessions ⇒ the gateway default.
    const resolveFreshSessionModel = (): void => {
      // A model was already decided for THIS session — `sendMessage` seeded the
      // chat it just minted, or a pick landed on it. That is strictly more
      // specific than any global default, so it must not be re-resolved: this
      // effect re-runs on the activeSessionId change that minting causes, and
      // by then the pre-session pick has been consumed, so re-resolving would
      // push the configured default straight over the user's pick.
      const decided =
        sessionId != null
          ? stateRef.current.sessionModels[sessionId]
          : undefined;
      if (decided != null) {
        // Display only. A decided model was either just WRITTEN by the mint
        // path (which seeds the session it creates) or read back from the
        // gateway — so re-seeding it here would patch a value the session
        // already has, twice per new chat.
        applyModelId(decided);
        return;
      }
      const seed = resolveSeedModelId();
      if (seed != null) {
        // Consumed here, exactly as `sendMessage` does for the chat it mints.
        // Without this twin, "+ New Chat" seeded correctly but never cleared
        // the ref, so the pick outranked the configured default for every
        // later new chat — the leak the mint path's comment claims to prevent,
        // one branch short. The state mirror is cleared alongside it so the
        // two can't drift apart again.
        pendingModelIdRef.current = null;
        if (stateRef.current.pendingModelId != null) {
          setState((prev) =>
            prev.pendingModelId == null
              ? prev
              : { ...prev, pendingModelId: null },
          );
        }
        seedFreshSession(seed);
        return;
      }
      const mostRecentOther = sessionsRef.current
        .filter((s) => s.sessionId !== sessionId)
        .reduce<ChatSession | null>(
          (best, s) =>
            best == null || s.updatedAt > best.updatedAt ? s : best,
          null,
        );
      if (mostRecentOther == null) {
        fetchGatewayDefault();
        return;
      }
      ws.request<SessionsDescribeResult>('sessions.describe', {
        key: mostRecentOther.sessionId,
      })
        .then((payload) => {
          const inherited = composeSessionModelId(payload?.session);
          if (inherited != null) {
            seedFreshSession(inherited);
          } else {
            // The previous session never reached the gateway either (e.g. a
            // seeded-as-default chat that was never used) — default.
            fetchGatewayDefault();
          }
        })
        .catch((err: unknown) => {
          logModelDebug('model-inherit-failed', {
            fromSessionId: mostRecentOther.sessionId,
            error: String(err),
          });
          fetchGatewayDefault();
        });
    };

    const describeKey = sessionId ?? ws.getMainSessionKey();
    if (describeKey == null) {
      fetchGatewayDefault();
      return;
    }
    ws.request<SessionsDescribeResult>('sessions.describe', {
      key: describeKey,
    })
      .then((payload) => {
        // The context meter rides THIS response rather than issuing its own.
        // It used to run a byte-identical describe for the same key at the
        // same moment, which wasted a round trip on every switch and reconnect.
        //
        // `isUsageStale` orders this answer against newer runs of this effect
        // — a session switch, a reconnect. It does NOT order it against
        // `sessions.changed`: a describe that resolves after a push can still
        // overwrite a fresher fill with pre-run figures (reconnect during a
        // run, the run finishing mid-round-trip).
        //
        // How long that costs is worth being exact about, because "one push"
        // undersells it: `sessions.changed` only fires around RUNS, so a
        // describe that lands after a run's last push holds the stale figure
        // until the user's next message — a whole turn, not a moment.
        // Accepted rather than fixed: the honest fix is a shared monotonic
        // stamp across both writers, which is a bigger change than this
        // reading is worth. Noted here so the next reader doesn't take the
        // guard for more than it is.
        //
        // Seeded only for a real chat — the agent's shared main session is not
        // what the footer is about.
        if (sessionId != null && !isUsageStale()) {
          const usage = parseSessionContextUsage(payload?.session);
          if (usage != null) {
            setState((prev) => {
              const contextUsage = reconcileSessionContextUsage(
                prev.contextUsage,
                usage,
              );
              return contextUsage === prev.contextUsage
                ? prev
                : { ...prev, contextUsage };
            });
          }
        }
        const modelId = composeSessionModelId(payload?.session);
        if (modelId != null) {
          applyModelId(modelId);
        } else if (sessionId != null) {
          resolveFreshSessionModel();
        } else {
          // No UI session yet at all — just resolve something to display.
          fetchGatewayDefault();
        }
      })
      .catch((err: unknown) => {
        // Transient failure — leave the current display; the next
        // switch/reconnect retries. Logged because this is the read whose
        // failure is indistinguishable from "the model really is this".
        logModelDebug('model-read-failed', {
          sessionId,
          describeKey,
          error: String(err),
        });
      });
  }, [
    state.status,
    state.activeSessionId,
    accountId,
    logModelDebug,
    applySessionModel,
    resolveSeedModelId,
    writeSessionModel,
  ]);

  // Clear the context-usage reading when the chat, connection, or account
  // changes, so one chat's fill never shows under another chat's name. The
  // reseed rides the session-model effect's `sessions.describe` above; live
  // updates arrive as `sessions.changed` pushes.
  //
  // Sub-agent subtasks are NOT cleared here: they are keyed per chat, so a
  // switch is a lookup rather than a reset. Their connection-scoped clear is
  // the effect below.
  //
  // LAYOUT, not passive, because `sendMessage`, `createSession` and
  // `switchSession` move `activeSessionIdRef` imperatively before their
  // `setState` commits: from that moment the router accepts pushes for the new
  // chat, and `contextUsage` carries no session id for this effect to tell
  // them apart by. A layout effect is flushed in the same task as the ref
  // move, so nothing can be delivered in between; a passive one is not. The
  // hazard is a yield rather than an order — a caller that moves the ref from
  // a promise continuation, socket callback or transition reopens it, so tag
  // the reading with its session id if one appears. (`useLayoutEffect` →
  // `useEffect` is a mutation the suite cannot catch: `act` flushes both in
  // one turn.)
  useLayoutEffect(() => {
    setState((prev) =>
      prev.contextUsage == null ? prev : { ...prev, contextUsage: null },
    );
    // `accountId` buys one tidy frame, not isolation. This runs before
    // `hydrate`'s reset, which sits behind `await cfg.resolveUrl`, but the old
    // account's socket stays subscribed until `hydrate` closes it — so a push
    // for the old chat during that window refills the reading (measured).
    // Real isolation needs the socket torn down at the `accountId` change, a
    // connection-lifecycle change that predates this feature.
  }, [state.status, state.activeSessionId, accountId]);

  // Drop every chat's sub-agent subtasks when the connection or the account
  // changes. Not on a session switch — the map is per chat — but a socket
  // that went away leaves nothing able to say which children still run, and
  // `tasks.list` is not available to this client.
  useLayoutEffect(() => {
    setState((prev) =>
      prev.runningSubtasks.size === 0
        ? prev
        : { ...prev, runningSubtasks: NO_RUNNING_SUBTASKS },
    );
  }, [state.status, accountId]);

  // NOTHING persists the rendered transcript.
  //
  // There used to be an effect here writing `state.messages` to disk on every
  // change, which is how the browser came to hold a second record of every
  // conversation: grown bubble by bubble from whatever frames this tab
  // happened to see, and therefore able to disagree with the agent's own
  // record — which is what all the reconciliation was for.
  //
  // The only thing written for a transcript now is the paint cache, and only
  // `hydrateSessionFromGateway` writes it: the agent's answer, verbatim,
  // replacing what was there. Everything a tab renders beyond that — the
  // answer streaming right now, a message that failed to send, a background
  // chat's live tail — lives in memory for as long as the tab does, which is
  // exactly as long as it means anything.

  // Persist the sessions index whenever it changes.
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    // Cache only — the index document is not uploaded any more. The chat
    // list and folder catalog live on the gateway (`sessions.patch`,
    // `sessions.groups.*`), which every device reads, so there is nothing
    // left for a replicated document to carry.
    historyStore.saveSessions(
      accountId,
      // Drafts are excluded for the same reason temporary chats are: an unsent
      // chat that survived a reload would come back as a "New Chat" row with
      // nothing in it, which is the shape this flag exists to keep out.
      state.sessions.filter(
        (s) => s.isTemporary !== true && s.isDraft !== true,
      ),
    );
  }, [accountId, historyStore, state.sessions]);

  // Persist the user's custom folders whenever they change. Cache only, as
  // above: the group catalog is the gateway's.
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    historyStore.saveCustomFolders(accountId, state.customFolders);
  }, [accountId, historyStore, state.customFolders]);

  // Persist the active session id so a reload re-opens the same chat.
  const lastSavedActiveRef = useRef<string | null>(null);
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    if (lastSavedActiveRef.current === state.activeSessionId) return;
    lastSavedActiveRef.current = state.activeSessionId;
    const ui = historyStore.loadHistoryUiState(accountId);
    historyStore.saveHistoryUiState(accountId, {
      ...ui,
      activeSessionId: state.activeSessionId,
    });
  }, [accountId, historyStore, state.activeSessionId]);

  // Auto-reconnect when the user returns to an idle tab. Backgrounded tabs get
  // their timers throttled and the socket silently dropped; `reconnecting-
  // websocket` then burns through its retry budget while hidden and lands in a
  // terminal 'error'/'disconnected' state with nothing left running to recover
  // it (the heartbeat watchdog is a `setInterval` that's torn down on close).
  // So when the tab becomes visible again or the network returns, kick a fresh
  // connect if we're dead. Guards keep this safe: we only fire for an account
  // that already had a resolved gateway (`hydratedAccountRef`), only from a
  // dead status, never when the user hit Disconnect on purpose
  // (`manualDisconnectRef`), never while a connect is already in flight
  // (`isConnectingRef`), and never more than once per `RECONNECT_COOLDOWN_MS`
  // (cross-event backoff so a flapping network / rapid alt-tabbing can't spawn a
  // fresh client per event). Events are debounced because focus +
  // visibilitychange + online tend to fire together on a single wake.
  useEffect(() => {
    const RECONNECT_COOLDOWN_MS = 5000;
    let debounce: ReturnType<typeof setTimeout> | null = null;

    const attemptReconnect = () => {
      if (debounce != null) clearTimeout(debounce);
      debounce = setTimeout(() => {
        debounce = null;
        if (document.visibilityState !== 'visible') return;
        const account = hydratedAccountRef.current;
        if (account == null) return;
        // Don't undo a deliberate Disconnect — only recover passive drops.
        if (manualDisconnectRef.current) return;
        // A connect is already resolving — don't start a second one.
        if (isConnectingRef.current) return;
        const status = statusRef.current;
        if (status !== 'disconnected' && status !== 'error') return;
        // Cross-event backoff: skip if we already fired recently.
        const now = Date.now();
        if (now - lastReconnectAtRef.current < RECONNECT_COOLDOWN_MS) return;
        lastReconnectAtRef.current = now;
        // Reconnect to the gateway the session is currently targeting. Prefer
        // the in-flight `agentUrl` (what the user is looking at — e.g. a URL
        // they just switched to that failed to authenticate, which `urlStore`
        // hasn't persisted yet) over the stored URL. Both normally equal the
        // URL hydrate resolved; passing it explicitly wins over connect's
        // `cfg.gatewayUrl`-first default so the retry can't drift.
        connect(
          agentUrlRef.current ?? urlStore.getAgentUrl(account) ?? undefined,
        );
      }, 300);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') attemptReconnect();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', attemptReconnect);
    window.addEventListener('focus', attemptReconnect);
    return () => {
      if (debounce != null) clearTimeout(debounce);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', attemptReconnect);
      window.removeEventListener('focus', attemptReconnect);
    };
  }, [connect, urlStore]);

  // Cleanup on unmount. Crucially, reset the hydration guard so a *re-mount*
  // (a genuine one, or React StrictMode's dev setup→cleanup→setup double-
  // invoke) re-runs `hydrate()` and reconnects. Without this reset, the
  // cleanup closes the socket but the re-mounted `hydrate()` short-circuits on
  // `hydratedAccountRef`, leaving the UI stuck on 'connecting' with a dead
  // socket (reproduces only on page refresh, where the component mounts fresh).
  useEffect(() => {
    return () => {
      wsRef.current?.close();
      wsRef.current = null;
      syncRef.current?.dispose();
      syncRef.current = null;
      hydratedAccountRef.current = null;
    };
  }, []);

  return (
    <AgentStateProvider value={exposedState}>
      <AgentActionsProvider value={actions}>{children}</AgentActionsProvider>
    </AgentStateProvider>
  );
}

/** Shape of a single message in the `chat.history` RPC response. */
interface HistoryMessage {
  readonly role?: string;
  readonly content?:
    | string
    | ReadonlyArray<{ type?: string; text?: string; mimeType?: string }>;
  readonly timestamp?: number;
}

/**
 * Maximum number of messages OpenClaw's `chat.history` will return in one
 * call. The RPC has no cursor pagination — this is the deepest tail we can
 * pull.
 */
const CHAT_HISTORY_GATEWAY_LIMIT = 1000;

/**
 * Per-message character ceiling for a transcript read. The schema allows up
 * to 500k; this is well above any real bubble while still bounding a
 * pathological one. See the call site for why a default cap is not safe once
 * the read replaces the local copy.
 */
const CHAT_HISTORY_MAX_CHARS = 200_000;

/**
 * How many rows to ask for per roster page. Deep enough that a normal user is
 * answered in one request (the busiest observed store had 44 chats), while
 * still being a page rather than a promise: `limit` bounds the RAW list, which
 * includes cron, heartbeat and sub-agent rows that are not chats at all.
 */
const SESSION_ROSTER_LIMIT = 200;

/**
 * How far the roster will page before giving up, and the ceiling on rows it
 * will accumulate.
 *
 * The roster read runs on every connect, so it cannot be unbounded. These are
 * also what make a gateway that ignores `offset` terminate: such a page
 * repeats rows already seen, contributes nothing after the dedupe, and stops
 * the loop on its own — the caps are the backstop, not the mechanism.
 *
 * 10 × 200 is far past openclaw's own `session.maintenance.maxEntries` (2000
 * on this fleet), so hitting either means something is wrong rather than
 * something is large.
 */
const SESSION_ROSTER_MAX_PAGES = 10;
const SESSION_ROSTER_MAX_ROWS = 2_000;

/**
 * Fetch recent chat history from the gateway after authentication. In
 * single-session mode (no active UI session) this seeds the empty chat
 * with the gateway tail. In session mode we skip merging into a
 * user-managed session.
 */
function fetchChatHistory(
  ws: AgentWebSocket,
  setState: Dispatch<SetStateAction<AgentState>>,
): void {
  ws.request<{ messages?: readonly HistoryMessage[] }>('chat.history', {
    // Newer gateways require `sessionKey`. History seeding only applies in
    // single-session mode, so the gateway's main session is the right one.
    sessionKey: ws.getMainSessionKey() ?? undefined,
    limit: CHAT_HISTORY_GATEWAY_LIMIT,
  })
    .then((res) => {
      const seeded = normalizeHistoryMessages(res.messages);
      if (seeded.length === 0) return;

      setState((prev) => {
        if (prev.activeSessionId != null) return prev;
        if (prev.messages.length === 0) {
          return { ...prev, messages: seeded };
        }
        const seen = new Set(prev.messages.map(messageDedupKey));
        const additions = seeded.filter((m) => !seen.has(messageDedupKey(m)));
        if (additions.length === 0) return prev;
        const merged = [...prev.messages, ...additions].sort(
          (a, b) => a.timestamp - b.timestamp,
        );
        return { ...prev, messages: merged };
      });
    })
    .catch(() => {
      // Gateway doesn't support chat.history — silently skip.
    });
}

function messageDedupKey(m: ChatMessage): string {
  return `${m.timestamp}|${m.role}|${m.content}`;
}

function normalizeHistoryMessages(
  raw: readonly HistoryMessage[] | undefined,
): ChatMessage[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((m: HistoryMessage, i: number) => {
    let text = '';
    let attachments: MessageAttachment[] | undefined;
    if (typeof m.content === 'string') {
      text = m.content;
    } else if (Array.isArray(m.content)) {
      const blocks = m.content as ReadonlyArray<{
        type?: string;
        text?: string;
        mimeType?: string;
      }>;
      text = blocks
        .filter((c) => c.type === 'text' && c.text != null)
        .map((c) => c.text!)
        .join('');
      // Image blocks survive as metadata chips — the history payload has no
      // thumbnail, but an image-only message must not render as empty.
      const images = blocks.filter((c) => c.type === 'image');
      if (images.length > 0) {
        attachments = images.map((c) => ({
          type: 'image' as const,
          mimeType: c.mimeType ?? 'image/*',
        }));
      }
    }
    const msg: ChatMessage = {
      id: `history-${m.timestamp ?? 'x'}-${i}`,
      role: m.role === 'user' ? 'user' : 'assistant',
      content: text,
      timestamp: m.timestamp ?? Date.now(),
      ...(attachments != null ? { attachments } : {}),
    };
    return msg;
  });
}
